export type DatabaseTable = {
  name: string;
  domain: string;
  columnCount: number;
  columns: string[];
};

export type DatabaseDomain = {
  name: string;
  tableCount: number;
  columnCount: number;
  largeTableCount: number;
  samples: string[];
};

export const bzDatabaseAtlasSummary = {
  "source": "Courriel FW: BD ERP — pièce jointe bzinc.md",
  "generatedAt": "2026-08-31",
  "tableCount": 730,
  "relationCount": 268,
  "columnCount": 5928,
  "domainCount": 29,
  "largestTables": [
    {
      "name": "ingram_detail",
      "domain": "Ingram",
      "columnCount": 121
    },
    {
      "name": "clients",
      "domain": "Clients & CRM",
      "columnCount": 98
    },
    {
      "name": "users",
      "domain": "Identités & accès",
      "columnCount": 78
    },
    {
      "name": "tic_tickets",
      "domain": "Tickets TI",
      "columnCount": 68
    },
    {
      "name": "clients_incidents",
      "domain": "Clients & CRM",
      "columnCount": 58
    },
    {
      "name": "accounting_invoices",
      "domain": "Comptabilité / ventes",
      "columnCount": 54
    },
    {
      "name": "contracts_invoices",
      "domain": "Contrats",
      "columnCount": 41
    },
    {
      "name": "inventory",
      "domain": "Inventaire",
      "columnCount": 40
    },
    {
      "name": "clients_loi25",
      "domain": "Clients & CRM",
      "columnCount": 39
    },
    {
      "name": "accounting_pos",
      "domain": "Comptabilité / ventes",
      "columnCount": 38
    },
    {
      "name": "bzt_accounting_bids",
      "domain": "Télécom BZT",
      "columnCount": 38
    },
    {
      "name": "bzt_contracts_invoices",
      "domain": "Télécom BZT",
      "columnCount": 38
    }
  ],
  "architectureSignals": [
    "730 tables dans un seul modèle Mermaid : signe fort d’un ERP historique très large et fortement couplé.",
    "Les domaines Clients, BZT, Comptabilité, Tickets TI, Sécurité, Documentation, Dépenses et Licences dominent la surface de données.",
    "Plusieurs tables contiennent beaucoup de colonnes, ce qui suggère des agrégats métiers trop larges ou des responsabilités mélangées.",
    "Pour des bots en production, la documentation actuelle doit évoluer vers une cartographie par domaines, propriétaires, niveaux de sensibilité et contrats d’accès."
  ],
  "recommendedName": "Atlas des données BZ",
  "recommendedTagline": "Cartographie claire des databases, tables, domaines métiers et priorités sécurité-by-design."
} as const;

export const bzDatabaseDomains: DatabaseDomain[] = [
  {
    "name": "Clients & CRM",
    "tableCount": 132,
    "columnCount": 934,
    "largeTableCount": 3,
    "samples": [
      "clients",
      "clients_access_managements",
      "clients_access_managements_assets_links",
      "clients_assets_identify",
      "clients_asterisk",
      "clients_backup_plans"
    ]
  },
  {
    "name": "Legacy / noyau divers",
    "tableCount": 104,
    "columnCount": 755,
    "largeTableCount": 1,
    "samples": [
      "acl_permissions",
      "acl_roles",
      "acombabridge_address",
      "bzcomplaint",
      "bzurgence",
      "calendar"
    ]
  },
  {
    "name": "Télécom BZT",
    "tableCount": 78,
    "columnCount": 847,
    "largeTableCount": 7,
    "samples": [
      "bzt_911_config_blocklist",
      "bzt_911_customers",
      "bzt_911_signatures",
      "bzt_accounting_bids",
      "bzt_accounting_bids_lines",
      "bzt_accounting_bids_notes"
    ]
  },
  {
    "name": "Comptabilité / ventes",
    "tableCount": 56,
    "columnCount": 670,
    "largeTableCount": 8,
    "samples": [
      "accounting_bids",
      "accounting_bids_lines",
      "accounting_bids_notes",
      "accounting_bids_signatures",
      "accounting_books",
      "accounting_books_categories"
    ]
  },
  {
    "name": "Tickets TI",
    "tableCount": 37,
    "columnCount": 291,
    "largeTableCount": 2,
    "samples": [
      "tic_appointment_cancel_tokens",
      "tic_assign",
      "tic_assign_to",
      "tic_bids",
      "tic_categories",
      "tic_changelog"
    ]
  },
  {
    "name": "Sécurité & conformité",
    "tableCount": 35,
    "columnCount": 201,
    "largeTableCount": 1,
    "samples": [
      "sec_a_sql_scripts",
      "sec_catalog_loi25_people",
      "sec_catalog_service_incidents",
      "sec_catalog_service_sensitive_data",
      "sec_clients_catalog_group_services_scores",
      "sec_clients_catalog_services_nist_identify"
    ]
  },
  {
    "name": "Documentation",
    "tableCount": 29,
    "columnCount": 182,
    "largeTableCount": 0,
    "samples": [
      "documentation_catalog_dependencies",
      "documentation_catalog_groups",
      "documentation_clientpermissions",
      "documentation_clients_rights",
      "documentation_document_views",
      "documentation_documents"
    ]
  },
  {
    "name": "Dépenses",
    "tableCount": 27,
    "columnCount": 186,
    "largeTableCount": 0,
    "samples": [
      "expense_account_credit_card",
      "expense_account_credit_card_users",
      "expense_accounts",
      "expense_accounts_2",
      "expense_accounts_2_banking_informations",
      "expense_accounts_2_items"
    ]
  },
  {
    "name": "Licences & contrats",
    "tableCount": 27,
    "columnCount": 189,
    "largeTableCount": 1,
    "samples": [
      "lic_archive_contracts",
      "lic_archive_contracts_details",
      "lic_archive_contracts_files",
      "lic_archive_contracts_tos",
      "lic_archive_licenses",
      "lic_archive_licenses_users_types"
    ]
  },
  {
    "name": "RH",
    "tableCount": 26,
    "columnCount": 147,
    "largeTableCount": 0,
    "samples": [
      "rh_billing_info",
      "rh_conditions",
      "rh_conges",
      "rh_departements",
      "rh_discussions",
      "rh_documents"
    ]
  },
  {
    "name": "Identités & accès",
    "tableCount": 23,
    "columnCount": 164,
    "largeTableCount": 1,
    "samples": [
      "users",
      "users_backups_favoris",
      "users_clients_favoris",
      "users_clients_view_counts",
      "users_clients_views",
      "users_credentials_authorization_code"
    ]
  },
  {
    "name": "Contrats",
    "tableCount": 19,
    "columnCount": 234,
    "largeTableCount": 2,
    "samples": [
      "contracts",
      "contracts_alerts",
      "contracts_contacts",
      "contracts_files",
      "contracts_imports_ingram_invoices",
      "contracts_imports_sherweb_invoices"
    ]
  },
  {
    "name": "Ventes",
    "tableCount": 15,
    "columnCount": 95,
    "largeTableCount": 0,
    "samples": [
      "ven_pd_details",
      "ven_pd_ms_groupes",
      "ven_pd_ms_produits",
      "ven_pd_notes",
      "ven_pd_section_params",
      "ven_pd_sections"
    ]
  },
  {
    "name": "Administration tâches",
    "tableCount": 13,
    "columnCount": 107,
    "largeTableCount": 0,
    "samples": [
      "admintaches_contact",
      "admintaches_contextai",
      "admintaches_departement",
      "admintaches_lk_departements_users",
      "admintaches_lk_task_user",
      "admintaches_notes"
    ]
  },
  {
    "name": "Archives / backups",
    "tableCount": 11,
    "columnCount": 65,
    "largeTableCount": 0,
    "samples": [
      "bak_clients_identifiants",
      "bak_grille",
      "bak_jobs",
      "bak_logiciels",
      "bak_params",
      "bak_recurrences"
    ]
  },
  {
    "name": "Sauvegardes",
    "tableCount": 10,
    "columnCount": 77,
    "largeTableCount": 0,
    "samples": [
      "veeam_api_data",
      "veeam_api_data_details",
      "veeam_bz_notes",
      "veeam_bz_quantities",
      "veeam_client_links",
      "veeam_client_notes"
    ]
  },
  {
    "name": "Surveillance",
    "tableCount": 10,
    "columnCount": 57,
    "largeTableCount": 0,
    "samples": [
      "surv_adjectives",
      "surv_emails",
      "surv_hashes",
      "surv_questions_adjectives",
      "surv_results",
      "surv_scales"
    ]
  },
  {
    "name": "ERP noyau",
    "tableCount": 9,
    "columnCount": 87,
    "largeTableCount": 0,
    "samples": [
      "erp_alerts",
      "erp_chats",
      "erp_chats_messages",
      "erp_com_comptes",
      "erp_com_comptes_depenses",
      "erp_com_comptes_files"
    ]
  },
  {
    "name": "Exports / vues",
    "tableCount": 9,
    "columnCount": 98,
    "largeTableCount": 0,
    "samples": [
      "get_clients_succursale_with_basics",
      "get_clients_with_basics",
      "get_contracts_lines_with_subs_qty",
      "get_contracts_primary_lines_with_qty_365",
      "get_contracts_slave_lines_with_qty_365",
      "get_count_assigned_sub_per_subscription"
    ]
  },
  {
    "name": "TactiCE",
    "tableCount": 9,
    "columnCount": 61,
    "largeTableCount": 0,
    "samples": [
      "tac_clients",
      "tac_historique",
      "tac_recurrences",
      "tac_recurrences_assign",
      "tac_recurrences_tickets",
      "tac_taches"
    ]
  },
  {
    "name": "Production / projets",
    "tableCount": 8,
    "columnCount": 41,
    "largeTableCount": 0,
    "samples": [
      "pro_projects",
      "pro_projects_contacts",
      "pro_projects_files",
      "pro_projects_managers",
      "pro_projects_phases",
      "pro_projects_phases_tasks"
    ]
  },
  {
    "name": "Projets",
    "tableCount": 8,
    "columnCount": 44,
    "largeTableCount": 0,
    "samples": [
      "projects",
      "projects_assigned",
      "projects_contacts",
      "projects_files",
      "projects_subproject",
      "projects_subproject_assigned"
    ]
  },
  {
    "name": "Clients — legacy",
    "tableCount": 6,
    "columnCount": 40,
    "largeTableCount": 0,
    "samples": [
      "cli_contrats",
      "cli_contrats_details",
      "cli_contrats_email_queue",
      "cli_contrats_files",
      "cli_contrats_history",
      "cli_contrats_invoice_lines"
    ]
  },
  {
    "name": "Communications",
    "tableCount": 6,
    "columnCount": 38,
    "largeTableCount": 0,
    "samples": [
      "com_comptes_depenses",
      "com_files",
      "com_params",
      "com_periodes",
      "com_periodes_techs",
      "com_types_depenses"
    ]
  },
  {
    "name": "Vocalytic",
    "tableCount": 6,
    "columnCount": 80,
    "largeTableCount": 0,
    "samples": [
      "vocalytic_call_analyses",
      "vocalytic_deleted_call_analyses_logs",
      "vocalytic_employee_monthly_recommendations",
      "vocalytic_prompt_configs",
      "vocalytic_prompt_versions",
      "vocalytic_tracked_users"
    ]
  },
  {
    "name": "Courriels",
    "tableCount": 5,
    "columnCount": 35,
    "largeTableCount": 0,
    "samples": [
      "sendmail_attachments",
      "sendmail_emails",
      "sendmail_jobs",
      "sendmail_jobs_products",
      "sendmail_templates"
    ]
  },
  {
    "name": "Guides",
    "tableCount": 4,
    "columnCount": 13,
    "largeTableCount": 0,
    "samples": [
      "guides_clients",
      "guides_guides",
      "guides_vardata",
      "guides_variables"
    ]
  },
  {
    "name": "Ingram",
    "tableCount": 4,
    "columnCount": 138,
    "largeTableCount": 1,
    "samples": [
      "ingram_detail",
      "ingram_import_files",
      "ingram_product_group_assignments",
      "ingram_product_groups"
    ]
  },
  {
    "name": "Inventaire",
    "tableCount": 4,
    "columnCount": 52,
    "largeTableCount": 1,
    "samples": [
      "inventory",
      "inventory_software",
      "inventory_templates",
      "inventory_types"
    ]
  }
];

export const bzDatabaseTables: DatabaseTable[] = [
  {
    "name": "accounting_bids",
    "columns": [
      "id_client",
      "id_order",
      "type",
      "creation_date",
      "status",
      "bid_number",
      "description",
      "reference",
      "id_user",
      "id_ticket",
      "id_supplier",
      "alternative_address",
      "deliver_to",
      "client_name",
      "client_address",
      "client_city",
      "client_state",
      "client_postal_code",
      "client_phone",
      "id_succursale",
      "succursale_name",
      "succursale_address",
      "succursale_city",
      "succursale_state",
      "succursale_postal_code",
      "succursale_phone",
      "subtotal",
      "tps",
      "tvq",
      "total",
      "url_hash",
      "is_archived",
      "id_seller",
      "non_taxable",
      "post_signature_changed_at",
      "post_signature_changed_by",
      "id_bid"
    ],
    "domain": "Comptabilité / ventes",
    "columnCount": 37
  },
  {
    "name": "accounting_bids_lines",
    "columns": [
      "id_bid",
      "id_product",
      "id_category",
      "category_name",
      "qty",
      "price",
      "part_number",
      "description",
      "product_serial_number",
      "supplier_inventory_qty",
      "is_eco_fees",
      "eco_fees_id_bid_line",
      "id_eco_fees",
      "index",
      "qty_ordered",
      "qty_invoiced",
      "id_bid_line"
    ],
    "domain": "Comptabilité / ventes",
    "columnCount": 17
  },
  {
    "name": "accounting_bids_notes",
    "columns": [
      "note",
      "creationdate",
      "id_user",
      "id_bid",
      "id_bid_note"
    ],
    "domain": "Comptabilité / ventes",
    "columnCount": 5
  },
  {
    "name": "accounting_bids_signatures",
    "columns": [
      "id_sign",
      "id_bid",
      "signed_at",
      "signer_email",
      "signature_data",
      "po_number",
      "signer_name"
    ],
    "domain": "Comptabilité / ventes",
    "columnCount": 7
  },
  {
    "name": "accounting_books",
    "columns": [
      "name",
      "inactive",
      "id_book"
    ],
    "domain": "Comptabilité / ventes",
    "columnCount": 3
  },
  {
    "name": "accounting_books_categories",
    "columns": [
      "name",
      "id_category"
    ],
    "domain": "Comptabilité / ventes",
    "columnCount": 2
  },
  {
    "name": "accounting_categories",
    "columns": [
      "name",
      "active",
      "id_product_default",
      "id_book",
      "id_account_tactice",
      "is_expense_account",
      "is_km",
      "id_expense_account_taxe_category",
      "definition",
      "id_category"
    ],
    "domain": "Comptabilité / ventes",
    "columnCount": 10
  },
  {
    "name": "accounting_ccaisse",
    "columns": [
      "creation_date",
      "logic_code",
      "data",
      "id_ccaisse"
    ],
    "domain": "Comptabilité / ventes",
    "columnCount": 4
  },
  {
    "name": "accounting_ecofees",
    "columns": [
      "description",
      "fees",
      "id_book",
      "id_ecofees"
    ],
    "domain": "Comptabilité / ventes",
    "columnCount": 4
  },
  {
    "name": "accounting_follow_ups",
    "columns": [
      "id_user",
      "id_client",
      "message",
      "created_date",
      "id_follow_up"
    ],
    "domain": "Comptabilité / ventes",
    "columnCount": 5
  },
  {
    "name": "accounting_invoices",
    "columns": [
      "id_client",
      "type",
      "creation_date",
      "status",
      "invoice_number",
      "description",
      "reference",
      "id_user",
      "id_ticket",
      "id_supplier",
      "alternative_address",
      "deliver_to",
      "id_order",
      "id_bid",
      "invoice_id_invoice",
      "credited_id_invoice",
      "tx_invoice_number",
      "tx_id_invoice",
      "client_name",
      "client_address",
      "client_city",
      "client_state",
      "client_postal_code",
      "client_phone",
      "id_succursale",
      "succursale_name",
      "succursale_address",
      "succursale_city",
      "succursale_state",
      "succursale_postal_code",
      "succursale_phone",
      "subtotal",
      "tps",
      "tvq",
      "total",
      "non_taxable",
      "paid",
      "balance",
      "url_hash",
      "id_contract",
      "contract_archived_on",
      "id_stripe_invoice",
      "contract_json_licences_data",
      "status_stripe",
      "payment_method",
      "id_succursale_payment",
      "stripe_credit",
      "id_user_prime_vente",
      "id_user_prime_vente_at_invoice",
      "subtotal_2",
      "tps_2",
      "tvq_2",
      "total_2",
      "id_invoice"
    ],
    "domain": "Comptabilité / ventes",
    "columnCount": 54
  },
  {
    "name": "accounting_invoices_lines",
    "columns": [
      "id_invoice",
      "id_product",
      "id_category",
      "category_name",
      "qty",
      "price",
      "tps",
      "tvq",
      "total",
      "part_number",
      "description",
      "qty_from_inventory",
      "product_serial_number",
      "supplier_inventory_qty",
      "is_eco_fees",
      "eco_fees_id_invoice_line",
      "id_eco_fees",
      "index",
      "is_bundle",
      "is_bundle_O365",
      "bundle_id_invoice_line",
      "id_bundle_from",
      "bundle_discount",
      "bundle_discount_type",
      "id_stripe_item",
      "id_product_slave",
      "qty_slave",
      "product_slave_price",
      "part_number_slave",
      "is_billable_month",
      "qty2",
      "id_invoice_line"
    ],
    "domain": "Comptabilité / ventes",
    "columnCount": 32
  },
  {
    "name": "accounting_invoices_lines_tags",
    "columns": [
      "id_invoice_line",
      "id_tag",
      "id_invoice_line_tag"
    ],
    "domain": "Comptabilité / ventes",
    "columnCount": 3
  },
  {
    "name": "accounting_invoices_notes",
    "columns": [
      "note",
      "creationdate",
      "id_user",
      "id_invoice",
      "id_invoice_note"
    ],
    "domain": "Comptabilité / ventes",
    "columnCount": 5
  },
  {
    "name": "accounting_invoices_tickets",
    "columns": [
      "id_invoice",
      "id_ticket",
      "id_invoice_ticket"
    ],
    "domain": "Comptabilité / ventes",
    "columnCount": 3
  },
  {
    "name": "accounting_items",
    "columns": [
      "id_product",
      "part_number",
      "description",
      "serial_number",
      "created_date",
      "id_item"
    ],
    "domain": "Comptabilité / ventes",
    "columnCount": 6
  },
  {
    "name": "accounting_items_inventories",
    "columns": [
      "id_item",
      "id_loan",
      "id_from_inventory",
      "id_to_inventory",
      "type",
      "created_date",
      "id_item_inventory"
    ],
    "domain": "Comptabilité / ventes",
    "columnCount": 7
  },
  {
    "name": "accounting_items_invoicing_lifecycles",
    "columns": [
      "id_item",
      "to_bid_date",
      "id_bid_line",
      "to_order_date",
      "id_order_line",
      "to_po_date",
      "id_po_line",
      "to_invoice_date",
      "id_invoice_line",
      "to_credit_date",
      "id_credit_line",
      "created_date",
      "id_item_invoicing_lifecycle"
    ],
    "domain": "Comptabilité / ventes",
    "columnCount": 13
  },
  {
    "name": "accounting_loans",
    "columns": [
      "description",
      "id_client",
      "id_user",
      "loan_date",
      "return_date",
      "renew_reason",
      "status",
      "id_loan"
    ],
    "domain": "Comptabilité / ventes",
    "columnCount": 8
  },
  {
    "name": "accounting_loans_items",
    "columns": [
      "id_loan",
      "id_item",
      "id_loan_item"
    ],
    "domain": "Comptabilité / ventes",
    "columnCount": 3
  },
  {
    "name": "accounting_locations",
    "columns": [
      "name",
      "description",
      "accounting_locationscol",
      "id_location"
    ],
    "domain": "Comptabilité / ventes",
    "columnCount": 4
  },
  {
    "name": "accounting_orders",
    "columns": [
      "id_client",
      "type",
      "creation_date",
      "status",
      "order_number",
      "description",
      "reference",
      "id_user",
      "id_bid",
      "id_invoice",
      "id_ticket",
      "id_supplier",
      "alternative_address",
      "deliver_to",
      "client_name",
      "client_address",
      "client_city",
      "client_state",
      "client_postal_code",
      "client_phone",
      "id_succursale",
      "succursale_name",
      "succursale_address",
      "succursale_city",
      "succursale_state",
      "succursale_postal_code",
      "succursale_phone",
      "subtotal",
      "tps",
      "tvq",
      "total",
      "url_hash",
      "eta_date",
      "id_order"
    ],
    "domain": "Comptabilité / ventes",
    "columnCount": 34
  },
  {
    "name": "accounting_orders_lines",
    "columns": [
      "id_order",
      "id_product",
      "qty",
      "price",
      "received",
      "part_number",
      "description",
      "product_serial_number",
      "supplier_inventory_qty",
      "is_eco_fees",
      "eco_fees_id_order_line",
      "id_eco_fees",
      "index",
      "id_order_line"
    ],
    "domain": "Comptabilité / ventes",
    "columnCount": 14
  },
  {
    "name": "accounting_orders_notes",
    "columns": [
      "note",
      "creationdate",
      "id_user",
      "id_order",
      "id_order_note"
    ],
    "domain": "Comptabilité / ventes",
    "columnCount": 5
  },
  {
    "name": "accounting_params",
    "columns": [
      "closing_period",
      "society_name",
      "society_name_abbreviations",
      "institution_number",
      "organisation_number",
      "bank_number",
      "folio_number",
      "traitement_center_number",
      "creation_number",
      "ccaisse_email",
      "confirmation_email",
      "tickets_km_price",
      "account_ages_email_message",
      "id_param"
    ],
    "domain": "Comptabilité / ventes",
    "columnCount": 14
  },
  {
    "name": "accounting_payments",
    "columns": [
      "id_client",
      "creation_date",
      "notes",
      "payment_method",
      "total",
      "excess_before",
      "excess_created",
      "excess_used",
      "payment_number",
      "status",
      "email_send",
      "paiement_date_future",
      "id_payment"
    ],
    "domain": "Comptabilité / ventes",
    "columnCount": 13
  },
  {
    "name": "accounting_payments_details",
    "columns": [
      "id_payment",
      "id_invoice",
      "payment",
      "discount",
      "id_payment_detail"
    ],
    "domain": "Comptabilité / ventes",
    "columnCount": 5
  },
  {
    "name": "accounting_payments_suppliers",
    "columns": [
      "id_supplier",
      "id_credit_card",
      "creation_date",
      "notes",
      "payment_method",
      "total",
      "excess_before",
      "excess_created",
      "excess_used",
      "payment_number",
      "status",
      "email_send",
      "id_payment_supplier"
    ],
    "domain": "Comptabilité / ventes",
    "columnCount": 13
  },
  {
    "name": "accounting_payments_suppliers_details",
    "columns": [
      "id_payment_supplier",
      "id_supplier_invoice",
      "id_credit_card_rapport",
      "payment",
      "discount",
      "id_payment_supplier_detail"
    ],
    "domain": "Comptabilité / ventes",
    "columnCount": 6
  },
  {
    "name": "accounting_pos",
    "columns": [
      "id_client",
      "type",
      "creation_date",
      "status",
      "po_number",
      "description",
      "reference",
      "id_user",
      "id_ticket",
      "id_supplier",
      "alternative_address",
      "deliver_to",
      "id_order",
      "id_bid",
      "invoice_id_invoice",
      "tx_po_number",
      "tx_id_po",
      "client_name",
      "client_address",
      "client_city",
      "client_state",
      "client_postal_code",
      "client_phone",
      "id_succursale",
      "succursale_name",
      "succursale_address",
      "succursale_city",
      "succursale_state",
      "succursale_postal_code",
      "succursale_phone",
      "subtotal",
      "tps",
      "tvq",
      "total",
      "paid",
      "balance",
      "urlhash",
      "id_po"
    ],
    "domain": "Comptabilité / ventes",
    "columnCount": 38
  },
  {
    "name": "accounting_pos_lines",
    "columns": [
      "id_po",
      "id_product",
      "qty",
      "price",
      "received",
      "part_number",
      "description",
      "qty_from_inventory",
      "product_serial_number",
      "supplier_inventory_qty",
      "is_eco_fees",
      "eco_fees_id_po_line",
      "id_eco_fees",
      "index",
      "id_po_line"
    ],
    "domain": "Comptabilité / ventes",
    "columnCount": 15
  },
  {
    "name": "accounting_pos_notes",
    "columns": [
      "note",
      "creationdate",
      "id_user",
      "id_po",
      "id_po_note"
    ],
    "domain": "Comptabilité / ventes",
    "columnCount": 5
  },
  {
    "name": "accounting_product_groups",
    "columns": [
      "group_definition",
      "id_group"
    ],
    "domain": "Comptabilité / ventes",
    "columnCount": 2
  },
  {
    "name": "accounting_product_groups_details",
    "columns": [
      "name",
      "id_book",
      "id_tree",
      "inactive",
      "id_product_group_detail"
    ],
    "domain": "Comptabilité / ventes",
    "columnCount": 5
  },
  {
    "name": "accounting_products",
    "columns": [
      "description",
      "part_number",
      "sku",
      "price",
      "contract_price",
      "id_ecofees",
      "id_category",
      "serial_number_required",
      "quantity_required",
      "quantity_pending",
      "quantity_received",
      "is_inventoried",
      "is_bundle",
      "is_bundle_O365",
      "active",
      "id_product_O365",
      "ingram_subscription_frequency",
      "ingram_billing_frequency",
      "graph_product_guid",
      "id_product_sherweb",
      "definition",
      "is_km",
      "is_expense_account",
      "id_expense_account_taxe_category",
      "is_expense_config_visible",
      "is_expense_credit_card_visible",
      "ids_products_sherwebs",
      "id_product"
    ],
    "domain": "Comptabilité / ventes",
    "columnCount": 28
  },
  {
    "name": "accounting_products_adjustement_logs",
    "columns": [
      "id_product",
      "field",
      "old_value",
      "new_value",
      "id_user",
      "date",
      "reason",
      "is_manual_log",
      "id_adjustement_logs"
    ],
    "domain": "Comptabilité / ventes",
    "columnCount": 9
  },
  {
    "name": "accounting_products_bundles",
    "columns": [
      "id_product",
      "id_product_slave",
      "id_bundle",
      "multiple_qty",
      "is_billable_month",
      "id_product_bundle"
    ],
    "domain": "Comptabilité / ventes",
    "columnCount": 6
  },
  {
    "name": "accounting_products_bundles_logs",
    "columns": [
      "id_product",
      "creation_date",
      "old_value",
      "new_value",
      "id_user",
      "id_bundle_log"
    ],
    "domain": "Comptabilité / ventes",
    "columnCount": 6
  },
  {
    "name": "accounting_products_lifecycles",
    "columns": [
      "id_product",
      "invoice_date",
      "invoice_id_user",
      "id_invoice_line",
      "invoice_credit_date",
      "invoice_credit_id_user",
      "id_invoice_line_credit",
      "supplier_purchase_date",
      "supplier_purchase_id_user",
      "id_supplier_purchase_line",
      "supplier_invoice_date",
      "supplier_invoice_id_user",
      "id_supplier_invoice_line",
      "supplier_invoice_credit_date",
      "supplier_invoice_credit_id_user",
      "id_supplier_invoice_line_credit",
      "internal_use_date",
      "internal_use_reason",
      "internal_use_id_user",
      "internal_use_credit_date",
      "internal_use_credit_reason",
      "internal_use_credit_id_user",
      "manual_in_reason",
      "manual_in_date",
      "manual_in_id_user",
      "manual_out_date",
      "manual_out_reason",
      "manual_out_id_user",
      "cost_price",
      "inactive",
      "id_serial_number",
      "creation_date",
      "id_lifecycle"
    ],
    "domain": "Comptabilité / ventes",
    "columnCount": 33
  },
  {
    "name": "accounting_products_reception_logs",
    "columns": [
      "id_product",
      "id_adjustement_log",
      "id_invoice_line",
      "id_supplier_invoice_line",
      "id_supplier_purchase_line",
      "id_user",
      "cost_price",
      "date",
      "inactive",
      "id_serial_number",
      "id_invoice_line_credit",
      "is_internal_use",
      "id_adjustement_log_internal_use",
      "id_adjustement_log_internal_use_credit",
      "id_supplier_invoice_line_credit",
      "id_reception_log"
    ],
    "domain": "Comptabilité / ventes",
    "columnCount": 16
  },
  {
    "name": "accounting_products_serial_number_logs",
    "columns": [
      "serial_number_old",
      "serial_number",
      "id_user",
      "creation_date",
      "reason",
      "id_product_serial_number",
      "id_product",
      "id_product_serial_number_log"
    ],
    "domain": "Comptabilité / ventes",
    "columnCount": 8
  },
  {
    "name": "accounting_products_serial_numbers",
    "columns": [
      "serial_number",
      "id_invoice_line",
      "id_purchase_line",
      "id_product",
      "id_reception_log",
      "id_product_serial_number"
    ],
    "domain": "Comptabilité / ventes",
    "columnCount": 6
  },
  {
    "name": "accounting_products_tags",
    "columns": [
      "id_product",
      "id_tag",
      "id_product_tag"
    ],
    "domain": "Comptabilité / ventes",
    "columnCount": 3
  },
  {
    "name": "accounting_prog_logs",
    "columns": [
      "function_name",
      "variable_name",
      "old_value",
      "new_value",
      "creation_date",
      "comment",
      "id_user",
      "id_log"
    ],
    "domain": "Comptabilité / ventes",
    "columnCount": 8
  },
  {
    "name": "accounting_purchases",
    "columns": [
      "id_supplier",
      "id_credit_card",
      "id_user",
      "creation_date",
      "status",
      "purchase_number",
      "description",
      "reference",
      "subtotal",
      "tps",
      "tvq",
      "total",
      "dropship_id_client",
      "dropship_client_name",
      "dropship_id_succursale",
      "dropship_succursale_name",
      "dropship_address",
      "dropship_city",
      "dropship_state",
      "dropship_postal_code",
      "dropship_phone",
      "id_purchase"
    ],
    "domain": "Comptabilité / ventes",
    "columnCount": 22
  },
  {
    "name": "accounting_purchases_lines",
    "columns": [
      "id_purchase",
      "id_product",
      "qty",
      "price",
      "part_number",
      "description",
      "index",
      "id_category",
      "qty_received",
      "id_purchase_line"
    ],
    "domain": "Comptabilité / ventes",
    "columnCount": 10
  },
  {
    "name": "accounting_recevables_notes",
    "columns": [
      "id_client",
      "type",
      "level",
      "subject",
      "tos",
      "id_creator",
      "body",
      "is_sent",
      "sent_date",
      "archived",
      "created_date",
      "updated_date",
      "id_recevable_note"
    ],
    "domain": "Comptabilité / ventes",
    "columnCount": 13
  },
  {
    "name": "accounting_recevables_notes_invoices",
    "columns": [
      "id_invoice",
      "id_recevable_note",
      "created_date",
      "updated_date",
      "id_recevable_note_invoice"
    ],
    "domain": "Comptabilité / ventes",
    "columnCount": 5
  },
  {
    "name": "accounting_rentals_lifecycles",
    "columns": [
      "id_product",
      "id_contract_detail",
      "rental_date_start",
      "rental_date_end",
      "id_user_rental",
      "id_serial_number",
      "creation_date",
      "id_re_rental_lifecycle",
      "id_rental_lifecycle"
    ],
    "domain": "Comptabilité / ventes",
    "columnCount": 9
  },
  {
    "name": "accounting_suppliers",
    "columns": [
      "name",
      "minimum_purchase",
      "minimum_purchase_fees",
      "xml_service",
      "description",
      "address",
      "city",
      "country",
      "postalcode",
      "phone",
      "fax",
      "website",
      "email",
      "contact_name",
      "contact_email",
      "contact_phone",
      "id_book",
      "can_be_deleted",
      "province",
      "payment_excess",
      "payment_therm",
      "client_number",
      "institution",
      "succursale",
      "account",
      "full_name",
      "is_active",
      "id_supplier"
    ],
    "domain": "Comptabilité / ventes",
    "columnCount": 28
  },
  {
    "name": "accounting_suppliers_fcaisse",
    "columns": [
      "creation_date",
      "logic_code",
      "data",
      "id_fcaisse"
    ],
    "domain": "Comptabilité / ventes",
    "columnCount": 4
  },
  {
    "name": "accounting_suppliers_invoices",
    "columns": [
      "id_supplier",
      "id_credit_card",
      "source_type",
      "id_user",
      "creation_date",
      "status",
      "invoice_number",
      "description",
      "reference",
      "subtotal",
      "tps",
      "tvq",
      "total",
      "paid",
      "balance",
      "id_supplier_invoice"
    ],
    "domain": "Comptabilité / ventes",
    "columnCount": 16
  },
  {
    "name": "accounting_suppliers_invoices_lines",
    "columns": [
      "id_supplier_invoice",
      "id_purchase_line",
      "id_product",
      "id_category",
      "qty",
      "price",
      "part_number",
      "description",
      "index",
      "id_supplier_invoice_line"
    ],
    "domain": "Comptabilité / ventes",
    "columnCount": 10
  },
  {
    "name": "accounting_suppliers_params",
    "columns": [
      "society_name",
      "society_name_abbreviations",
      "organisation_number",
      "institution_number",
      "bank_number",
      "folio_number",
      "traitement_center_number",
      "creation_number",
      "fcaisse_email",
      "confirmation_email",
      "id_supplier_param"
    ],
    "domain": "Comptabilité / ventes",
    "columnCount": 11
  },
  {
    "name": "accounting_suppliers_warehouse",
    "columns": [
      "id_supplier",
      "name",
      "address",
      "city",
      "state",
      "county",
      "postal_code",
      "fax_number",
      "phone_number",
      "contact_name",
      "contact_email",
      "contact_phone",
      "favorites",
      "status",
      "id_warehouse"
    ],
    "domain": "Comptabilité / ventes",
    "columnCount": 15
  },
  {
    "name": "accounting_tags",
    "columns": [
      "name",
      "active",
      "id_tag"
    ],
    "domain": "Comptabilité / ventes",
    "columnCount": 3
  },
  {
    "name": "acl_permissions",
    "columns": [
      "id_user",
      "id_role",
      "id_permission"
    ],
    "domain": "Legacy / noyau divers",
    "columnCount": 3
  },
  {
    "name": "acl_roles",
    "columns": [
      "name",
      "id_role"
    ],
    "domain": "Legacy / noyau divers",
    "columnCount": 2
  },
  {
    "name": "acombabridge_address",
    "columns": [
      "address_indexpos",
      "AdStatus",
      "AdUnique",
      "AdUsed",
      "AdUniqueUser",
      "AdTimeModified",
      "AdNumber",
      "AdName",
      "AdCareOf",
      "AdAddress",
      "AdCity",
      "AdPostalCode",
      "AdISOCountryCode",
      "AdLanguage",
      "AdPhoneDescription",
      "AdPhoneNumber",
      "AdPhoneExtention",
      "AdEMailDescription",
      "AdEMail",
      "AdWebAddress",
      "AdBatchEMailActive",
      "CardPos",
      "id_address"
    ],
    "domain": "Legacy / noyau divers",
    "columnCount": 23
  },
  {
    "name": "admintaches_contact",
    "columns": [
      "name",
      "email",
      "phone",
      "avatar",
      "promptAI",
      "createdAt",
      "updatedAt",
      "clientId",
      "departementId",
      "id"
    ],
    "domain": "Administration tâches",
    "columnCount": 10
  },
  {
    "name": "admintaches_contextai",
    "columns": [
      "idclient",
      "idcontrat",
      "idcontact",
      "created_at",
      "updated_at",
      "content",
      "idtype",
      "iddepartement",
      "typecontext",
      "ACTIF",
      "id"
    ],
    "domain": "Administration tâches",
    "columnCount": 11
  },
  {
    "name": "admintaches_departement",
    "columns": [
      "name",
      "description",
      "promptAI",
      "createdAt",
      "updatedAt",
      "id"
    ],
    "domain": "Administration tâches",
    "columnCount": 6
  },
  {
    "name": "admintaches_lk_departements_users",
    "columns": [
      "iddepartement",
      "iduser",
      "role",
      "created_at",
      "updated_at",
      "id"
    ],
    "domain": "Administration tâches",
    "columnCount": 6
  },
  {
    "name": "admintaches_lk_task_user",
    "columns": [
      "created_at",
      "updated_at",
      "task_id",
      "user_id"
    ],
    "domain": "Administration tâches",
    "columnCount": 4
  },
  {
    "name": "admintaches_notes",
    "columns": [
      "content",
      "createdAt",
      "updatedAt",
      "taskId",
      "typenote",
      "createdBy",
      "id"
    ],
    "domain": "Administration tâches",
    "columnCount": 7
  },
  {
    "name": "admintaches_password",
    "columns": [
      "title",
      "username",
      "password",
      "tag",
      "siteWeb",
      "note",
      "note2",
      "createdAt",
      "updatedAt",
      "id"
    ],
    "domain": "Administration tâches",
    "columnCount": 10
  },
  {
    "name": "admintaches_requestia",
    "columns": [
      "task_id",
      "model",
      "messages",
      "client_data",
      "contracts_data",
      "created_at",
      "id"
    ],
    "domain": "Administration tâches",
    "columnCount": 7
  },
  {
    "name": "admintaches_responsesai",
    "columns": [
      "task_id",
      "priority",
      "option_name",
      "probability",
      "reasoning",
      "action",
      "validation_question",
      "bundle_details",
      "created_at",
      "response_json",
      "responseai",
      "id"
    ],
    "domain": "Administration tâches",
    "columnCount": 12
  },
  {
    "name": "admintaches_responsesai_prompts",
    "columns": [
      "task_id",
      "prompt_json",
      "created_at",
      "id"
    ],
    "domain": "Administration tâches",
    "columnCount": 4
  },
  {
    "name": "admintaches_task",
    "columns": [
      "title",
      "description",
      "status",
      "priority",
      "dueDate",
      "createdAt",
      "updatedAt",
      "clientId",
      "demandeurId",
      "provenance",
      "isApproved",
      "typeId",
      "departementId",
      "contratId",
      "id_ticket",
      "is_confidential",
      "id"
    ],
    "domain": "Administration tâches",
    "columnCount": 17
  },
  {
    "name": "admintaches_task_attachments",
    "columns": [
      "task_id",
      "filename",
      "graph_email_box_name",
      "graph_email_id",
      "graph_email_folder_id",
      "graph_email_attachment_id",
      "id_attachment"
    ],
    "domain": "Administration tâches",
    "columnCount": 7
  },
  {
    "name": "admintaches_type",
    "columns": [
      "name",
      "description",
      "promptAI",
      "createdAt",
      "updatedAt",
      "id"
    ],
    "domain": "Administration tâches",
    "columnCount": 6
  },
  {
    "name": "bak_clients_identifiants",
    "columns": [
      "identifiant",
      "id_client",
      "typeidentifiant",
      "ididentifiant"
    ],
    "domain": "Archives / backups",
    "columnCount": 4
  },
  {
    "name": "bak_grille",
    "columns": [
      "nomjob",
      "noclient",
      "nom",
      "icone",
      "resultats",
      "resultatsid",
      "idjob"
    ],
    "domain": "Archives / backups",
    "columnCount": 7
  },
  {
    "name": "bak_jobs",
    "columns": [
      "id_client",
      "idlogiciel",
      "idrecurrence",
      "nomjob",
      "retention",
      "jourdebut",
      "jourdelasem",
      "numjourdelasem",
      "jourshebdo",
      "mediaexterne",
      "jourshebdodata",
      "maintenance",
      "comment",
      "notes",
      "idjob"
    ],
    "domain": "Archives / backups",
    "columnCount": 15
  },
  {
    "name": "bak_logiciels",
    "columns": [
      "nomlogiciel",
      "icone",
      "idlogiciel"
    ],
    "domain": "Archives / backups",
    "columnCount": 3
  },
  {
    "name": "bak_params",
    "columns": [
      "email_errored_schedules_count",
      "email_errored_schedules_to",
      "id_param"
    ],
    "domain": "Archives / backups",
    "columnCount": 3
  },
  {
    "name": "bak_recurrences",
    "columns": [
      "recurrence",
      "shortname",
      "idrecurrence"
    ],
    "domain": "Archives / backups",
    "columnCount": 3
  },
  {
    "name": "bak_resultats",
    "columns": [
      "idjob",
      "daterecu",
      "resultat",
      "client_extrait",
      "logiciel_extrait",
      "nomjob_extrait",
      "idresultat"
    ],
    "domain": "Archives / backups",
    "columnCount": 7
  },
  {
    "name": "bak_resultats_job_date",
    "columns": [
      "idjob",
      "day",
      "status",
      "set_by",
      "id"
    ],
    "domain": "Archives / backups",
    "columnCount": 5
  },
  {
    "name": "bak_resultats_jobs_links",
    "columns": [
      "idresultat",
      "idjob",
      "resultat",
      "nomjob_extrait"
    ],
    "domain": "Archives / backups",
    "columnCount": 4
  },
  {
    "name": "bak_resultats_mail",
    "columns": [
      "header",
      "body",
      "ulen",
      "clen",
      "headerJSON",
      "creation_date",
      "status",
      "idresultat"
    ],
    "domain": "Archives / backups",
    "columnCount": 8
  },
  {
    "name": "bak_resultats_mail_attachments",
    "columns": [
      "id_attachment",
      "idresultat",
      "filename",
      "content_type",
      "content_text",
      "created_at"
    ],
    "domain": "Archives / backups",
    "columnCount": 6
  },
  {
    "name": "bzcomplaint",
    "columns": [
      "last_complaint"
    ],
    "domain": "Legacy / noyau divers",
    "columnCount": 1
  },
  {
    "name": "bzt_911_config_blocklist",
    "columns": [
      "id",
      "id_client",
      "id_succursale",
      "created_at",
      "updated_at"
    ],
    "domain": "Télécom BZT",
    "columnCount": 5
  },
  {
    "name": "bzt_911_customers",
    "columns": [
      "id_customer",
      "id_client",
      "id_succursale",
      "phone_number_911",
      "last_name",
      "first_name",
      "street_number",
      "suite_apt",
      "street_name",
      "city",
      "province_state",
      "postal_code_zip",
      "other_address_info"
    ],
    "domain": "Télécom BZT",
    "columnCount": 13
  },
  {
    "name": "bzt_911_signatures",
    "columns": [
      "id_signature",
      "uuid",
      "id_client",
      "client_name",
      "sent_to",
      "sent_date",
      "signed_date",
      "signer_name",
      "signature_data",
      "signer_user_agent",
      "id_succursale"
    ],
    "domain": "Télécom BZT",
    "columnCount": 11
  },
  {
    "name": "bzt_accounting_bids",
    "columns": [
      "id_bid",
      "bid_number",
      "reference",
      "description",
      "status",
      "id_client",
      "creation_date",
      "total",
      "subtotal",
      "tps",
      "tvq",
      "non_taxable",
      "is_archived",
      "id_seller",
      "id_user",
      "id_succursale",
      "succursale_name",
      "succursale_address",
      "succursale_city",
      "succursale_state",
      "succursale_postal_code",
      "succursale_phone",
      "client_name",
      "client_address",
      "client_city",
      "client_state",
      "client_postal_code",
      "client_phone",
      "deliver_to",
      "alternative_address",
      "id_ticket",
      "type",
      "id_order",
      "id_invoice",
      "sign_token_created_at",
      "created_at",
      "updated_at",
      "sans_prix"
    ],
    "domain": "Télécom BZT",
    "columnCount": 38
  },
  {
    "name": "bzt_accounting_bids_lines",
    "columns": [
      "id_bid_line",
      "id_bid",
      "line_type",
      "id_product",
      "id_category",
      "part_number",
      "description",
      "qty",
      "price",
      "qty_invoiced",
      "index",
      "is_eco_fees",
      "eco_fees_id_bid_line",
      "created_at",
      "updated_at"
    ],
    "domain": "Télécom BZT",
    "columnCount": 15
  },
  {
    "name": "bzt_accounting_bids_notes",
    "columns": [
      "id_bid_note",
      "id_bid",
      "id_user",
      "content",
      "created_at",
      "updated_at"
    ],
    "domain": "Télécom BZT",
    "columnCount": 6
  },
  {
    "name": "bzt_accounting_bids_signatures",
    "columns": [
      "id_bid_signature",
      "id_bid",
      "signer_name",
      "signer_email",
      "signature_data",
      "signed_at",
      "po_number",
      "created_at"
    ],
    "domain": "Télécom BZT",
    "columnCount": 8
  },
  {
    "name": "bzt_accounting_books",
    "columns": [
      "id_book",
      "name",
      "account_number",
      "active",
      "id_type",
      "is_km",
      "id_account_tactice",
      "created_at",
      "updated_at",
      "id_subtype",
      "description",
      "id_expense_account_taxe_category",
      "is_expense_account"
    ],
    "domain": "Télécom BZT",
    "columnCount": 13
  },
  {
    "name": "bzt_accounting_ccaisse",
    "columns": [
      "id",
      "logic_code",
      "data",
      "creation_date"
    ],
    "domain": "Télécom BZT",
    "columnCount": 4
  },
  {
    "name": "bzt_accounting_ecofees",
    "columns": [
      "description",
      "fees",
      "id_book",
      "id_ecofees"
    ],
    "domain": "Télécom BZT",
    "columnCount": 4
  },
  {
    "name": "bzt_accounting_invoices",
    "columns": [
      "id_invoice",
      "id_client",
      "id_user",
      "id_bid",
      "creation_date",
      "invoice_date",
      "status",
      "invoice_number",
      "description",
      "reference",
      "non_taxable",
      "subtotal",
      "tps",
      "tvq",
      "total",
      "paid",
      "balance",
      "type",
      "credited_id_invoice"
    ],
    "domain": "Télécom BZT",
    "columnCount": 19
  },
  {
    "name": "bzt_accounting_invoices_lines",
    "columns": [
      "id_invoice_line",
      "id_invoice",
      "line_type",
      "id_product",
      "id_category",
      "id_bid_line",
      "qty",
      "price",
      "part_number",
      "description",
      "index",
      "is_eco_fees",
      "eco_fees_id_invoice_line"
    ],
    "domain": "Télécom BZT",
    "columnCount": 13
  },
  {
    "name": "bzt_accounting_params",
    "columns": [
      "id_param",
      "society_name",
      "society_name_abbreviations",
      "institution_number",
      "organisation_number",
      "bank_number",
      "folio_number",
      "traitement_center_number",
      "creation_number",
      "ccaisse_email",
      "confirmation_email",
      "closing_period",
      "updated_at"
    ],
    "domain": "Télécom BZT",
    "columnCount": 13
  },
  {
    "name": "bzt_accounting_payments_suppliers",
    "columns": [
      "id_payment_supplier",
      "id_supplier",
      "id_credit_card",
      "creation_date",
      "payment_date",
      "payment_method",
      "reference",
      "notes",
      "total",
      "excess_before",
      "excess_created",
      "excess_used",
      "status",
      "payment_number",
      "created_at",
      "updated_at",
      "email_send"
    ],
    "domain": "Télécom BZT",
    "columnCount": 17
  },
  {
    "name": "bzt_accounting_payments_suppliers_details",
    "columns": [
      "id_payment_supplier_detail",
      "id_payment_supplier",
      "id_supplier_invoice",
      "id_credit_card_rapport",
      "amount",
      "discount",
      "created_at",
      "updated_at"
    ],
    "domain": "Télécom BZT",
    "columnCount": 8
  },
  {
    "name": "bzt_accounting_products",
    "columns": [
      "description",
      "part_number",
      "sku",
      "alias",
      "price",
      "contract_price",
      "id_ecofees",
      "id_account",
      "id_account_revenue",
      "id_account_expense",
      "product_group_code",
      "serial_number_required",
      "quantity_required",
      "quantity_pending",
      "quantity_received",
      "quantity_sold",
      "is_inventoried",
      "is_lot",
      "lot_id_product",
      "lot_quantity",
      "active",
      "definition",
      "is_km",
      "is_expense_account",
      "id_expense_account_taxe_category",
      "is_expense_config_visible",
      "is_expense_credit_card_visible",
      "id_product"
    ],
    "domain": "Télécom BZT",
    "columnCount": 28
  },
  {
    "name": "bzt_accounting_products_adjustement_logs",
    "columns": [
      "id_product",
      "field",
      "old_value",
      "new_value",
      "id_user",
      "date",
      "reason",
      "is_manual_log",
      "id_reception_log",
      "id_adjustement_log"
    ],
    "domain": "Télécom BZT",
    "columnCount": 10
  },
  {
    "name": "bzt_accounting_products_reception_logs",
    "columns": [
      "id_product",
      "id_invoice_line",
      "id_supplier_invoice_line",
      "id_supplier_invoice_line_credit",
      "id_supplier_purchase_line",
      "id_user",
      "cost_price",
      "date",
      "inactive",
      "id_serial_number",
      "id_invoice_line_credit",
      "is_internal_use",
      "id_reception_log"
    ],
    "domain": "Télécom BZT",
    "columnCount": 13
  },
  {
    "name": "bzt_accounting_products_serial_number_logs",
    "columns": [
      "serial_number_old",
      "serial_number",
      "id_user",
      "creation_date",
      "reason",
      "id_product_serial_number",
      "id_product",
      "id_product_serial_number_log"
    ],
    "domain": "Télécom BZT",
    "columnCount": 8
  },
  {
    "name": "bzt_accounting_products_serial_numbers",
    "columns": [
      "serial_number",
      "id_invoice_line",
      "id_purchase_line",
      "id_product",
      "id_reception_log",
      "id_product_serial_number"
    ],
    "domain": "Télécom BZT",
    "columnCount": 6
  },
  {
    "name": "bzt_accounting_purchases",
    "columns": [
      "id_purchase",
      "id_supplier",
      "id_user",
      "id_bid",
      "creation_date",
      "status",
      "purchase_number",
      "description",
      "reference",
      "is_expense",
      "non_taxable",
      "subtotal",
      "tps",
      "tvq",
      "total",
      "created_at",
      "updated_at",
      "closed_manually",
      "closed_at",
      "closed_id_user",
      "closed_note"
    ],
    "domain": "Télécom BZT",
    "columnCount": 21
  },
  {
    "name": "bzt_accounting_purchases_lines",
    "columns": [
      "id_purchase_line",
      "id_purchase",
      "id_product",
      "id_category",
      "id_bid_line",
      "is_expense",
      "qty",
      "qty_received",
      "price",
      "part_number",
      "description",
      "index",
      "created_at",
      "updated_at",
      "id_account"
    ],
    "domain": "Télécom BZT",
    "columnCount": 15
  },
  {
    "name": "bzt_accounting_purchases_receptions",
    "columns": [
      "id_reception",
      "id_purchase",
      "reception_date",
      "id_user",
      "note",
      "created_at",
      "updated_at"
    ],
    "domain": "Télécom BZT",
    "columnCount": 7
  },
  {
    "name": "bzt_accounting_purchases_receptions_files",
    "columns": [
      "id_reception_file",
      "id_reception",
      "name",
      "path",
      "size",
      "data",
      "filetype",
      "is_archived",
      "created_at",
      "updated_at"
    ],
    "domain": "Télécom BZT",
    "columnCount": 10
  },
  {
    "name": "bzt_accounting_purchases_receptions_lines",
    "columns": [
      "id_reception_line",
      "id_reception",
      "id_purchase_line",
      "qty_received",
      "unit_cost",
      "created_at",
      "updated_at"
    ],
    "domain": "Télécom BZT",
    "columnCount": 7
  },
  {
    "name": "bzt_accounting_subtype",
    "columns": [
      "id_subtype",
      "name",
      "id_type",
      "created_date",
      "updated_date"
    ],
    "domain": "Télécom BZT",
    "columnCount": 5
  },
  {
    "name": "bzt_accounting_suppliers",
    "columns": [
      "id_supplier",
      "name",
      "description",
      "address",
      "city",
      "province",
      "country",
      "postalcode",
      "phone",
      "fax",
      "website",
      "email",
      "payment_therm",
      "client_number",
      "minimum_purchase",
      "minimum_purchase_fees",
      "xml_service",
      "institution",
      "succursale",
      "account",
      "full_name",
      "is_active",
      "can_be_deleted",
      "id_book",
      "payment_excess"
    ],
    "domain": "Télécom BZT",
    "columnCount": 25
  },
  {
    "name": "bzt_accounting_suppliers_fcaisse",
    "columns": [
      "id",
      "logic_code",
      "data",
      "creation_date",
      "creation_number"
    ],
    "domain": "Télécom BZT",
    "columnCount": 5
  },
  {
    "name": "bzt_accounting_suppliers_invoices",
    "columns": [
      "id_supplier_invoice",
      "id_supplier",
      "id_user",
      "creation_date",
      "status",
      "invoice_number",
      "description",
      "reference",
      "non_taxable",
      "manual_taxes",
      "subtotal",
      "tps",
      "tvq",
      "total",
      "paid",
      "balance",
      "created_at",
      "updated_at"
    ],
    "domain": "Télécom BZT",
    "columnCount": 18
  },
  {
    "name": "bzt_accounting_suppliers_invoices_files",
    "columns": [
      "id_supplier_invoice_file",
      "id_supplier_invoice",
      "name",
      "path",
      "size",
      "data",
      "filetype",
      "is_archived",
      "created_at",
      "updated_at"
    ],
    "domain": "Télécom BZT",
    "columnCount": 10
  },
  {
    "name": "bzt_accounting_suppliers_invoices_lines",
    "columns": [
      "id_supplier_invoice_line",
      "id_supplier_invoice",
      "id_purchase_line",
      "id_supplier_invoice_line_source",
      "id_product",
      "id_category",
      "qty",
      "price",
      "part_number",
      "description",
      "index",
      "selected",
      "created_at",
      "updated_at"
    ],
    "domain": "Télécom BZT",
    "columnCount": 14
  },
  {
    "name": "bzt_accounting_suppliers_invoices_purchases",
    "columns": [
      "id_supplier_invoice_purchase",
      "id_supplier_invoice",
      "id_purchase",
      "created_at"
    ],
    "domain": "Télécom BZT",
    "columnCount": 4
  },
  {
    "name": "bzt_accounting_suppliers_params",
    "columns": [
      "id_supplier_param",
      "society_name",
      "society_name_abbreviations",
      "organisation_number",
      "institution_number",
      "bank_number",
      "folio_number",
      "traitement_center_number",
      "creation_number",
      "fcaisse_email",
      "confirmation_email"
    ],
    "domain": "Télécom BZT",
    "columnCount": 11
  },
  {
    "name": "bzt_accounting_suppliers_warehouse",
    "columns": [
      "id_warehouse",
      "id_supplier",
      "name",
      "address",
      "city",
      "state",
      "county",
      "postal_code",
      "fax_number",
      "phone_number",
      "status",
      "favorites"
    ],
    "domain": "Télécom BZT",
    "columnCount": 12
  },
  {
    "name": "bzt_accounting_types",
    "columns": [
      "id_type",
      "name",
      "created_date",
      "updated_date"
    ],
    "domain": "Télécom BZT",
    "columnCount": 4
  },
  {
    "name": "bzt_asterisk_params",
    "columns": [
      "id",
      "mysql_user_cleartext",
      "mysql_pass_cleartext",
      "updated_at"
    ],
    "domain": "Télécom BZT",
    "columnCount": 4
  },
  {
    "name": "bzt_client_bank_info",
    "columns": [
      "id",
      "id_client",
      "id_succursale",
      "society_full_name",
      "bank_institution",
      "bank_succursale",
      "bank_account"
    ],
    "domain": "Télécom BZT",
    "columnCount": 7
  },
  {
    "name": "bzt_contracts",
    "columns": [
      "title",
      "id_client",
      "id_succursale",
      "is_backup",
      "is_cyber",
      "is_prog",
      "is_tel",
      "is_ti",
      "next_billing_date",
      "billing_cycle",
      "start_date",
      "end_date",
      "closing_date",
      "birth_date",
      "duration",
      "extension_duration",
      "annual_increase_percent",
      "end_of_contract_percent",
      "status",
      "internal_notes",
      "internal_notes_read",
      "accounting_notes",
      "accounting_notes_read",
      "show_annexe_bundles",
      "show_annexe_did",
      "show_annexe_users",
      "created_date",
      "updated_date",
      "is_interurban",
      "show_annexe_interurban",
      "id_contract"
    ],
    "domain": "Télécom BZT",
    "columnCount": 31
  },
  {
    "name": "bzt_contracts_alerts",
    "columns": [
      "id_contract",
      "message",
      "renew_subscription",
      "license_365",
      "alert_date",
      "send_to",
      "status",
      "created_date",
      "updated_date",
      "id_alert"
    ],
    "domain": "Télécom BZT",
    "columnCount": 10
  },
  {
    "name": "bzt_contracts_contacts",
    "columns": [
      "id_contract",
      "email",
      "created_date",
      "id_contract_contact"
    ],
    "domain": "Télécom BZT",
    "columnCount": 4
  },
  {
    "name": "bzt_contracts_files",
    "columns": [
      "id_contract",
      "id_client",
      "path",
      "name",
      "size",
      "data",
      "preview",
      "filetype",
      "is_archived",
      "created_at",
      "updated_at",
      "id_contract_file"
    ],
    "domain": "Télécom BZT",
    "columnCount": 12
  },
  {
    "name": "bzt_contracts_imports_ingram_invoices",
    "columns": [
      "invoice_date",
      "line_number",
      "ingram_customer_id",
      "id_client",
      "client_name",
      "order_number",
      "service_description",
      "quantity",
      "unit_price",
      "discount",
      "duration_month",
      "total",
      "created_date",
      "id_ingram_import"
    ],
    "domain": "Télécom BZT",
    "columnCount": 14
  },
  {
    "name": "bzt_contracts_imports_sherweb_invoices",
    "columns": [
      "invoice_date",
      "sherweb_customer_id",
      "id_client",
      "client_name",
      "invoice_number",
      "product_description",
      "quantity",
      "unit_price",
      "discount",
      "duration_month",
      "total",
      "created_date",
      "id_sherweb_import"
    ],
    "domain": "Télécom BZT",
    "columnCount": 13
  },
  {
    "name": "bzt_contracts_invoices",
    "columns": [
      "id_contract",
      "id_client",
      "invoice_number",
      "title",
      "invoice_date",
      "status",
      "type",
      "description",
      "client_name",
      "client_address",
      "client_city",
      "client_state",
      "client_zip",
      "client_phone",
      "id_succursale",
      "succursale_name",
      "succursale_address",
      "succursale_city",
      "succursale_state",
      "succursale_zip",
      "succursale_phone",
      "subtotal",
      "tps",
      "tvq",
      "total",
      "is_taxable",
      "paid",
      "balance",
      "is_send",
      "show_annexe_bundles",
      "show_annexe_did",
      "show_annexe_users",
      "created_date",
      "updated_date",
      "interurban_fees",
      "show_annexe_interurban",
      "id_rapport",
      "id_invoice"
    ],
    "domain": "Télécom BZT",
    "columnCount": 38
  },
  {
    "name": "bzt_contracts_invoices_items",
    "columns": [
      "id_invoice",
      "title",
      "order",
      "type",
      "show_to_invoice",
      "id_bundle",
      "id_product",
      "automatique",
      "calculate_quantity",
      "quantity",
      "price",
      "show_annexe",
      "expend_bundle",
      "created_date",
      "updated_date",
      "id_invoice_item"
    ],
    "domain": "Télécom BZT",
    "columnCount": 16
  },
  {
    "name": "bzt_contracts_invoices_paiements",
    "columns": [
      "id_client",
      "paiement_method",
      "paiement_date",
      "excess_amount",
      "amount",
      "notes",
      "reference",
      "is_ppa_sent",
      "created_date",
      "updated_date",
      "id_paiement"
    ],
    "domain": "Télécom BZT",
    "columnCount": 11
  },
  {
    "name": "bzt_contracts_invoices_paiements_excess",
    "columns": [
      "id_paiement",
      "id_client",
      "paiement_date",
      "transaction_amount",
      "sold_amount",
      "created_date",
      "id_paiement_excess"
    ],
    "domain": "Télécom BZT",
    "columnCount": 7
  },
  {
    "name": "bzt_contracts_invoices_paiements_invoices",
    "columns": [
      "id_paiement",
      "id_invoice",
      "invoice_origin",
      "amount",
      "discount",
      "created_date",
      "updated_date",
      "id_paiement_invoice"
    ],
    "domain": "Télécom BZT",
    "columnCount": 8
  },
  {
    "name": "bzt_contracts_invoices_sequence_number",
    "columns": [
      "invoice_number",
      "id_sequence"
    ],
    "domain": "Télécom BZT",
    "columnCount": 2
  },
  {
    "name": "bzt_contracts_items",
    "columns": [
      "id_contract",
      "title",
      "order",
      "type",
      "show_to_invoice",
      "id_bundle",
      "id_product",
      "automatique",
      "calculate_quantity",
      "quantity",
      "price",
      "show_annexe",
      "expend_bundle",
      "created_date",
      "updated_date",
      "id_contract_item"
    ],
    "domain": "Télécom BZT",
    "columnCount": 16
  },
  {
    "name": "bzt_contracts_logs",
    "columns": [
      "id_contract",
      "id_contract_item",
      "part_number",
      "bundle_name",
      "id_user",
      "date",
      "field",
      "field_label",
      "old_value",
      "new_value",
      "id_contract_log"
    ],
    "domain": "Télécom BZT",
    "columnCount": 11
  },
  {
    "name": "bzt_contracts_notes_archive",
    "columns": [
      "id_contract",
      "notes",
      "read",
      "note_type",
      "archived_date",
      "id_notes_archive"
    ],
    "domain": "Télécom BZT",
    "columnCount": 6
  },
  {
    "name": "bzt_contracts_recevables_notes",
    "columns": [
      "id_client",
      "type",
      "level",
      "subject",
      "tos",
      "id_creator",
      "body",
      "is_sent",
      "sent_date",
      "archived",
      "created_date",
      "updated_date",
      "id_recevable_note"
    ],
    "domain": "Télécom BZT",
    "columnCount": 13
  },
  {
    "name": "bzt_contracts_recevables_notes_invoices",
    "columns": [
      "id_invoice",
      "id_recevable_note",
      "created_date",
      "updated_date",
      "id_recevable_note_invoice"
    ],
    "domain": "Télécom BZT",
    "columnCount": 5
  },
  {
    "name": "bzt_department_accounting_categories",
    "columns": [
      "id_department_accounting_category",
      "id_department",
      "id_book"
    ],
    "domain": "Télécom BZT",
    "columnCount": 3
  },
  {
    "name": "bzt_funnel_steps",
    "columns": [
      "id",
      "name",
      "description",
      "position",
      "out_of_funnel",
      "is_archived"
    ],
    "domain": "Télécom BZT",
    "columnCount": 6
  },
  {
    "name": "bzt_funnel_steps_change_logs",
    "columns": [
      "id",
      "lead_id",
      "funnel_step_id",
      "created_at"
    ],
    "domain": "Télécom BZT",
    "columnCount": 4
  },
  {
    "name": "bzt_inventory",
    "columns": [
      "id_inventory",
      "id_client",
      "id_user",
      "id_succursale",
      "id_type",
      "name",
      "hostname",
      "brand",
      "model",
      "serialnumber",
      "os_name",
      "os_version",
      "platform",
      "language",
      "departement",
      "cpuinfo",
      "express_service_tag",
      "distributor",
      "comments",
      "customfields",
      "status",
      "warranty_duration",
      "warranty_purchase",
      "warranty_end",
      "warranty_defect",
      "last_keepalive",
      "last_logged_user",
      "monitor1",
      "monitor2",
      "monitor3",
      "monitor4",
      "created_at"
    ],
    "domain": "Télécom BZT",
    "columnCount": 32
  },
  {
    "name": "bzt_inventory_software",
    "columns": [
      "id_inventory_software",
      "id_inventory",
      "software",
      "version",
      "license"
    ],
    "domain": "Télécom BZT",
    "columnCount": 5
  },
  {
    "name": "bzt_inventory_types",
    "columns": [
      "id_type",
      "type"
    ],
    "domain": "Télécom BZT",
    "columnCount": 2
  },
  {
    "name": "bzt_leads",
    "columns": [
      "id",
      "opportunity_name",
      "client_name",
      "client_type",
      "company",
      "address",
      "email",
      "phone",
      "opportunity_source_id",
      "funnel_step_id",
      "offer_type_id",
      "owner_seller_id",
      "opportunity_sponsor_id",
      "opportunity_amount",
      "closing_trust_percentage",
      "reminder_date",
      "reminder_sponsor",
      "is_reminder_sent",
      "is_archived",
      "is_cloned",
      "created_at",
      "updated_at"
    ],
    "domain": "Télécom BZT",
    "columnCount": 22
  },
  {
    "name": "bzt_leads_notes",
    "columns": [
      "id",
      "lead_id",
      "note",
      "author_display_name",
      "is_archived",
      "created_at",
      "updated_at"
    ],
    "domain": "Télécom BZT",
    "columnCount": 7
  },
  {
    "name": "bzt_offer_types",
    "columns": [
      "id",
      "name",
      "html_color",
      "is_archived"
    ],
    "domain": "Télécom BZT",
    "columnCount": 4
  },
  {
    "name": "bzt_opportunity_source_categories",
    "columns": [
      "id",
      "name",
      "is_archived"
    ],
    "domain": "Télécom BZT",
    "columnCount": 3
  },
  {
    "name": "bzt_opportunity_sources",
    "columns": [
      "id",
      "name",
      "category_id",
      "is_archived"
    ],
    "domain": "Télécom BZT",
    "columnCount": 4
  },
  {
    "name": "bzt_product_account_groups",
    "columns": [
      "group_code",
      "description",
      "revenue_account_number",
      "expense_account_number",
      "active",
      "created_at",
      "updated_at"
    ],
    "domain": "Télécom BZT",
    "columnCount": 7
  },
  {
    "name": "bzt_products_adjustments",
    "columns": [
      "id_adjustment",
      "id_product",
      "id_user",
      "adjustment_date",
      "reason",
      "old_quantity",
      "new_quantity",
      "cost_price",
      "department",
      "employee",
      "authorizer",
      "created_at"
    ],
    "domain": "Télécom BZT",
    "columnCount": 12
  },
  {
    "name": "bzt_products_lifecycles",
    "columns": [
      "id_lifecycle",
      "id_product",
      "id_reception",
      "cost_price",
      "supplier_purchase_date",
      "supplier_purchase_id_user",
      "id_supplier_purchase_line",
      "supplier_invoice_date",
      "supplier_invoice_id_user",
      "id_supplier_invoice_line",
      "supplier_invoice_credit_date",
      "id_supplier_invoice_line_credit",
      "manual_in_date",
      "manual_reason",
      "invoice_date",
      "invoice_id_user",
      "id_invoice_line",
      "invoice_credit_date",
      "id_invoice_line_credit",
      "internal_use_date",
      "internal_use_reason",
      "internal_use_id_user",
      "internal_use_credit_date",
      "internal_use_credit_reason",
      "manual_out_date",
      "inactive",
      "id_serial_number",
      "creation_id_user",
      "creation_date"
    ],
    "domain": "Télécom BZT",
    "columnCount": 29
  },
  {
    "name": "bzt_products_tags",
    "columns": [
      "id_product_tag",
      "id_product",
      "id_tag"
    ],
    "domain": "Télécom BZT",
    "columnCount": 3
  },
  {
    "name": "bzt_sellers",
    "columns": [
      "id",
      "name",
      "email",
      "is_archived"
    ],
    "domain": "Télécom BZT",
    "columnCount": 4
  },
  {
    "name": "bzt_sendmail_attachments",
    "columns": [
      "id_attachment",
      "filename",
      "filedata",
      "id_job"
    ],
    "domain": "Télécom BZT",
    "columnCount": 4
  },
  {
    "name": "bzt_sendmail_emails",
    "columns": [
      "id_email",
      "status",
      "destination",
      "send_date",
      "subject",
      "body_html",
      "id_job",
      "postfix_id",
      "postfix_log",
      "queue_date"
    ],
    "domain": "Télécom BZT",
    "columnCount": 10
  },
  {
    "name": "bzt_sendmail_jobs",
    "columns": [
      "id_job",
      "id_template",
      "template_variables",
      "job_config",
      "job_date",
      "status",
      "job_subject",
      "test_email",
      "avec_rappel",
      "subject_rappel",
      "date_rappel",
      "body_html",
      "job_body_html"
    ],
    "domain": "Télécom BZT",
    "columnCount": 13
  },
  {
    "name": "bzt_sendmail_jobs_products",
    "columns": [
      "id_job_product",
      "id_job",
      "id_product"
    ],
    "domain": "Télécom BZT",
    "columnCount": 3
  },
  {
    "name": "bzt_sendmail_templates",
    "columns": [
      "id_template",
      "template_subject",
      "template_body_html",
      "template_variables",
      "template_name"
    ],
    "domain": "Télécom BZT",
    "columnCount": 5
  },
  {
    "name": "bzt_supplier_contact",
    "columns": [
      "id_supplier_contact",
      "id_supplier",
      "name",
      "email",
      "phone",
      "title",
      "is_primary",
      "created_at",
      "updated_at"
    ],
    "domain": "Télécom BZT",
    "columnCount": 9
  },
  {
    "name": "bzt_tags",
    "columns": [
      "id_tag",
      "name"
    ],
    "domain": "Télécom BZT",
    "columnCount": 2
  },
  {
    "name": "bzt_text_correction_ai_prompts",
    "columns": [
      "id",
      "name",
      "text",
      "is_current",
      "created_at",
      "updated_at"
    ],
    "domain": "Télécom BZT",
    "columnCount": 6
  },
  {
    "name": "bzurgence",
    "columns": [
      "message",
      "last_urgence",
      "urgence"
    ],
    "domain": "Legacy / noyau divers",
    "columnCount": 3
  },
  {
    "name": "calendar",
    "columns": [
      "name",
      "default_week_price",
      "default_weekend_price",
      "default_monday_price",
      "default_tuesday_price",
      "default_wednesday_price",
      "default_thursday_price",
      "default_friday_price",
      "default_saturday_price",
      "default_sunday_price",
      "id_calendar"
    ],
    "domain": "Legacy / noyau divers",
    "columnCount": 11
  },
  {
    "name": "calendar_days",
    "columns": [
      "id_calendar",
      "day",
      "price",
      "id_user",
      "id_user_assigned",
      "date_booked",
      "is_paied",
      "id_day"
    ],
    "domain": "Legacy / noyau divers",
    "columnCount": 8
  },
  {
    "name": "cdae2019",
    "columns": [
      "id_intervention",
      "login",
      "duree",
      "type",
      "title",
      "date_intervention"
    ],
    "domain": "Legacy / noyau divers",
    "columnCount": 6
  },
  {
    "name": "cdae2020",
    "columns": [
      "id_intervention",
      "login",
      "duree",
      "type",
      "title",
      "date_intervention"
    ],
    "domain": "Legacy / noyau divers",
    "columnCount": 6
  },
  {
    "name": "changelogs",
    "columns": [
      "changelog_name",
      "changelog_date",
      "changelog_description",
      "id_changelog"
    ],
    "domain": "Legacy / noyau divers",
    "columnCount": 4
  },
  {
    "name": "chats",
    "columns": [
      "email",
      "creation_date",
      "name",
      "status",
      "source",
      "last_keepalive",
      "chat_uid",
      "id_ticket",
      "id_client",
      "seen",
      "id_chat"
    ],
    "domain": "Legacy / noyau divers",
    "columnCount": 11
  },
  {
    "name": "chats_messages",
    "columns": [
      "id_chat",
      "source",
      "message",
      "msgdate",
      "side",
      "id_user",
      "id_message"
    ],
    "domain": "Legacy / noyau divers",
    "columnCount": 7
  },
  {
    "name": "node659",
    "columns": [
      "id_client",
      "name",
      "email",
      "id_contact"
    ],
    "domain": "Legacy / noyau divers",
    "columnCount": 4
  },
  {
    "name": "cli_contrats",
    "columns": [
      "id_client",
      "titre",
      "first_month",
      "last_month",
      "mois_contrat",
      "notes",
      "contrat_acomba",
      "cycle",
      "locked",
      "mois_factures_to_be_deleted",
      "taken_care_of_id_user",
      "taken_care_of_date",
      "id_contrat"
    ],
    "domain": "Clients — legacy",
    "columnCount": 13
  },
  {
    "name": "cli_contrats_details",
    "columns": [
      "id_contrat",
      "description",
      "qte",
      "prix",
      "coutant",
      "type",
      "first_month",
      "last_month",
      "id_detail"
    ],
    "domain": "Clients — legacy",
    "columnCount": 9
  },
  {
    "name": "cli_contrats_email_queue",
    "columns": [
      "details",
      "id_cli_contrats_email_queue"
    ],
    "domain": "Clients — legacy",
    "columnCount": 2
  },
  {
    "name": "cli_contrats_files",
    "columns": [
      "id_contrat",
      "upload_data",
      "file_type_other",
      "id_file"
    ],
    "domain": "Clients — legacy",
    "columnCount": 4
  },
  {
    "name": "cli_contrats_history",
    "columns": [
      "id_contrat",
      "id_user",
      "history_date",
      "history_details",
      "id_history"
    ],
    "domain": "Clients — legacy",
    "columnCount": 5
  },
  {
    "name": "cli_contrats_invoice_lines",
    "columns": [
      "id_contrat",
      "description",
      "qty",
      "price",
      "id_book",
      "row_number",
      "id_contrat_invoice_line"
    ],
    "domain": "Clients — legacy",
    "columnCount": 7
  },
  {
    "name": "client_experience_credit_info",
    "columns": [
      "id_client",
      "start_date",
      "payment_therm",
      "id_client_experience_credit_info"
    ],
    "domain": "Legacy / noyau divers",
    "columnCount": 4
  },
  {
    "name": "clients",
    "columns": [
      "nom",
      "adresse",
      "ville",
      "province",
      "codepostal",
      "telephone",
      "telecopie",
      "contact",
      "taux",
      "taux_prog",
      "taux_tel",
      "taux_cyber",
      "taux_sa",
      "taux_bh",
      "bhquantite",
      "userid",
      "km",
      "courriel",
      "ressource",
      "cloud_tech",
      "cloud_prog",
      "cloud_tel",
      "cloud_cyber",
      "cloud_sa",
      "cloud_bh",
      "cloud_backup",
      "type_facturation",
      "attention",
      "prime_tech",
      "prime_vente",
      "asterisk_taux_canada",
      "asterisk_taux_usa",
      "asterisk_taux_international",
      "asterisk_taux_watt",
      "asterisk_exiting_prefix",
      "primary_id_succursale",
      "alias",
      "asterisk_accountcode",
      "contrat_taux_moyen",
      "contrat_taux_moyen_data",
      "licenses_inclus",
      "license_commentaire",
      "licenses_type",
      "address_support",
      "notes_special",
      "accounting_notes",
      "km_to_bz",
      "no_email_communications",
      "status",
      "payment_excess",
      "public_hash",
      "emails_account_statement",
      "society_full_name",
      "bank_institution",
      "bank_succursale",
      "bank_account",
      "jira_api_key",
      "is_soges_tech",
      "tax_exemption",
      "exemption_number",
      "billable_km",
      "price_vm",
      "price_vcpu",
      "price_ram",
      "price_hdd",
      "payment_therm",
      "ingram_marketplace_customer_id",
      "start_date",
      "id_stripe",
      "default_payment",
      "microsoft_domain_name",
      "autopay",
      "bids_popup_info",
      "is_cyber",
      "bz_score",
      "microsoft_score",
      "graph_cyber_tenant_id",
      "graph_cyber_client_id",
      "graph_cyber_secret",
      "assets_auto_creation_completed",
      "tickets_by_mail_use_ai",
      "tickets_by_mail_ai_priority",
      "send_recevable",
      "sherweb_api_customer_id",
      "nist_score_target",
      "nist_score_complete",
      "nist_target_complete",
      "nist_use_functions_as_is",
      "nist_use_categories_as_is",
      "spla",
      "nom_legal",
      "do_not_send_pickup_to_account_statement",
      "asterisk_bill_canada",
      "asterisk_bill_usa",
      "asterisk_free_min_canada",
      "asterisk_free_min_usa",
      "id_client",
      "noclient"
    ],
    "domain": "Clients & CRM",
    "columnCount": 98
  },
  {
    "name": "clients_access_managements",
    "columns": [
      "id_client",
      "title",
      "description",
      "user_group_management",
      "device_management",
      "physical_access",
      "network_management",
      "rest_data_encryption",
      "transit_secure_data",
      "monitoring_audit_management",
      "training",
      "created_at",
      "updated_at",
      "id_access_management"
    ],
    "domain": "Clients & CRM",
    "columnCount": 14
  },
  {
    "name": "clients_access_managements_assets_links",
    "columns": [
      "id_access_management",
      "id_asset",
      "id_access_management_asset_link"
    ],
    "domain": "Clients & CRM",
    "columnCount": 3
  },
  {
    "name": "clients_assets_identify",
    "columns": [
      "id_asset",
      "availability",
      "integrity",
      "confidentiality",
      "traceability",
      "authenticity",
      "non_repudiation",
      "commercial_value",
      "rto",
      "rpo",
      "risk_level_tolerance",
      "id_identify"
    ],
    "domain": "Clients & CRM",
    "columnCount": 12
  },
  {
    "name": "clients_asterisk",
    "columns": [
      "id_client",
      "id_asterisk",
      "source_enabled",
      "note",
      "created_at",
      "updated_at"
    ],
    "domain": "Clients & CRM",
    "columnCount": 6
  },
  {
    "name": "clients_backup_plans",
    "columns": [
      "id_client",
      "name",
      "software",
      "retention_days",
      "recurrence",
      "note",
      "external_media",
      "encrypted",
      "mfa",
      "monitored",
      "offline_media",
      "created_at",
      "updated_at",
      "id_backup_plan"
    ],
    "domain": "Clients & CRM",
    "columnCount": 14
  },
  {
    "name": "clients_backup_plans_assets_links",
    "columns": [
      "id_backup_plan",
      "id_asset",
      "id_backup_plan_asset_link"
    ],
    "domain": "Clients & CRM",
    "columnCount": 3
  },
  {
    "name": "clients_bh",
    "columns": [
      "id_client",
      "date",
      "description",
      "nbr_hours",
      "rate",
      "id_client_bh"
    ],
    "domain": "Clients & CRM",
    "columnCount": 6
  },
  {
    "name": "clients_calendars",
    "columns": [
      "id_client",
      "default_monday_price",
      "default_tuesday_price",
      "default_wednesday_price",
      "default_thursday_price",
      "default_friday_price",
      "default_saturday_price",
      "default_sunday_price",
      "enabled_technology",
      "free_pbx_auth_url",
      "free_pbx_gql_url",
      "free_pbx_client_id",
      "free_pbx_client_secret",
      "scoptel_auth_url",
      "scoptel_api_url",
      "scoptel_client_id",
      "scoptel_client_secret",
      "scoptel_tenant_name",
      "forwarding_extension",
      "created_date",
      "updated_date",
      "id_calendar"
    ],
    "domain": "Clients & CRM",
    "columnCount": 22
  },
  {
    "name": "clients_calendars_contacts",
    "columns": [
      "id_client",
      "name",
      "email",
      "phone",
      "created_date",
      "updated_date",
      "id_calendar_contact"
    ],
    "domain": "Clients & CRM",
    "columnCount": 7
  },
  {
    "name": "clients_calendars_schedules",
    "columns": [
      "id_calendar",
      "start_datetime",
      "end_datetime",
      "price",
      "id_contact_assigned",
      "id_user_assigned_by",
      "date_booked",
      "created_date",
      "updated_date",
      "id_schedule"
    ],
    "domain": "Clients & CRM",
    "columnCount": 10
  },
  {
    "name": "clients_catalog",
    "columns": [
      "id_client",
      "status",
      "creation_date",
      "version_name",
      "id_catalog"
    ],
    "domain": "Clients & CRM",
    "columnCount": 5
  },
  {
    "name": "clients_catalog_books",
    "columns": [
      "id_catalog",
      "book_code",
      "book_name",
      "id_catalog_book"
    ],
    "domain": "Clients & CRM",
    "columnCount": 4
  },
  {
    "name": "clients_catalog_dependencies",
    "columns": [
      "id_service_dependent",
      "id_service",
      "service_part",
      "id_dependency"
    ],
    "domain": "Clients & CRM",
    "columnCount": 4
  },
  {
    "name": "clients_catalog_direct_expenses",
    "columns": [
      "id_service",
      "description",
      "price",
      "quantity",
      "amortization",
      "next_year",
      "id_book",
      "id_direct_expense"
    ],
    "domain": "Clients & CRM",
    "columnCount": 8
  },
  {
    "name": "clients_catalog_groups",
    "columns": [
      "id_catalog",
      "group_level",
      "group_name",
      "id_group"
    ],
    "domain": "Clients & CRM",
    "columnCount": 4
  },
  {
    "name": "clients_catalog_services",
    "columns": [
      "service_name",
      "service_description",
      "service_security_score",
      "id_group",
      "operational",
      "ponderation",
      "id_service"
    ],
    "domain": "Clients & CRM",
    "columnCount": 7
  },
  {
    "name": "clients_changes_logs",
    "columns": [
      "id_client",
      "id_user",
      "datetime",
      "field",
      "field_label",
      "value_from",
      "value_to",
      "id_clients_changes_logs"
    ],
    "domain": "Clients & CRM",
    "columnCount": 8
  },
  {
    "name": "clients_complaints",
    "columns": [
      "title",
      "complainer",
      "description",
      "notes",
      "complaint_date",
      "treated",
      "creator_name",
      "id_client",
      "archived",
      "id_complaint"
    ],
    "domain": "Clients & CRM",
    "columnCount": 10
  },
  {
    "name": "clients_complaints_clients_links",
    "columns": [
      "id_complaint",
      "id_client",
      "id_complaint_client_link"
    ],
    "domain": "Clients & CRM",
    "columnCount": 3
  },
  {
    "name": "clients_contacts",
    "columns": [
      "name",
      "id_client",
      "company",
      "role",
      "email",
      "phone",
      "is_active",
      "contact_type",
      "id_contact"
    ],
    "domain": "Clients & CRM",
    "columnCount": 9
  },
  {
    "name": "clients_contacts_assets_links",
    "columns": [
      "id_contact",
      "id_asset",
      "id_contact_asset_link"
    ],
    "domain": "Clients & CRM",
    "columnCount": 3
  },
  {
    "name": "clients_contacts_assets_responsible_types_links",
    "columns": [
      "id_contact_asset_link",
      "id_responsible_type",
      "id_contact_asset_responsible_type_link"
    ],
    "domain": "Clients & CRM",
    "columnCount": 3
  },
  {
    "name": "clients_contacts_responsible_types",
    "columns": [
      "name",
      "archived",
      "id_responsible_type"
    ],
    "domain": "Clients & CRM",
    "columnCount": 3
  },
  {
    "name": "clients_contacts_responsible_types_links",
    "columns": [
      "id_contact_responsible_type_link",
      "id_contact",
      "id_responsible_type"
    ],
    "domain": "Clients & CRM",
    "columnCount": 3
  },
  {
    "name": "clients_cyber_uploads",
    "columns": [
      "name",
      "path",
      "id_client",
      "upload_date",
      "id_user",
      "official_version",
      "archived",
      "group_name",
      "id_cyber_upload"
    ],
    "domain": "Clients & CRM",
    "columnCount": 9
  },
  {
    "name": "clients_cyber_uploads_complaints_links",
    "columns": [
      "id_cyber_upload",
      "id_complaint",
      "id_cyber_upload_complaint_link"
    ],
    "domain": "Clients & CRM",
    "columnCount": 3
  },
  {
    "name": "clients_cyber_uploads_incidents_links",
    "columns": [
      "id_cyber_upload",
      "id_incident",
      "id_cyber_upload_incident_link"
    ],
    "domain": "Clients & CRM",
    "columnCount": 3
  },
  {
    "name": "clients_cyber_uploads_types",
    "columns": [
      "name",
      "archived",
      "id_cyber_upload_type"
    ],
    "domain": "Clients & CRM",
    "columnCount": 3
  },
  {
    "name": "clients_cyber_uploads_types_links",
    "columns": [
      "id_cyber_upload_type",
      "id_cyber_upload",
      "id_cyber_upload_type_link"
    ],
    "domain": "Clients & CRM",
    "columnCount": 3
  },
  {
    "name": "clients_data_destruction_frequencies",
    "columns": [
      "id",
      "name",
      "archived"
    ],
    "domain": "Clients & CRM",
    "columnCount": 3
  },
  {
    "name": "clients_data_destruction_frequencies_links",
    "columns": [
      "id",
      "id_data_destruction_plan",
      "id_data_destruction_frequency"
    ],
    "domain": "Clients & CRM",
    "columnCount": 3
  },
  {
    "name": "clients_data_destruction_methods",
    "columns": [
      "id",
      "name",
      "archived"
    ],
    "domain": "Clients & CRM",
    "columnCount": 3
  },
  {
    "name": "clients_data_destruction_methods_links",
    "columns": [
      "id",
      "id_data_destruction_plan",
      "id_data_destruction_method"
    ],
    "domain": "Clients & CRM",
    "columnCount": 3
  },
  {
    "name": "clients_data_destruction_plans",
    "columns": [
      "id_data_destruction_plan",
      "id_sensitive_data",
      "retention_period",
      "triggering_event",
      "id_data_destruction_status",
      "last_execution",
      "next_execution",
      "notes_and_checklist"
    ],
    "domain": "Clients & CRM",
    "columnCount": 8
  },
  {
    "name": "clients_data_destruction_statuses",
    "columns": [
      "id",
      "name",
      "archived"
    ],
    "domain": "Clients & CRM",
    "columnCount": 3
  },
  {
    "name": "clients_did",
    "columns": [
      "did",
      "id_asterisk",
      "id_client",
      "type",
      "destination",
      "id_did"
    ],
    "domain": "Clients & CRM",
    "columnCount": 6
  },
  {
    "name": "clients_documentary_settings",
    "columns": [
      "id_documentary_setting",
      "id_client",
      "cybersecurity_support",
      "id_cyber_upload",
      "different_client_name",
      "company_name_to_use",
      "disciplinary_measures_exist",
      "disciplinary_text",
      "disciplinary_source_date",
      "created_at",
      "updated_at"
    ],
    "domain": "Clients & CRM",
    "columnCount": 11
  },
  {
    "name": "clients_experience_credit_notes",
    "columns": [
      "id_client",
      "note",
      "date_created",
      "id_experience"
    ],
    "domain": "Clients & CRM",
    "columnCount": 4
  },
  {
    "name": "clients_graph_api",
    "columns": [
      "id_client",
      "tenant_id",
      "secret",
      "application_id",
      "id_graph_api"
    ],
    "domain": "Clients & CRM",
    "columnCount": 5
  },
  {
    "name": "clients_graph_api_microsoft_secret",
    "columns": [
      "id_client",
      "keyId",
      "applicationDisplayName",
      "credentialDisplayName",
      "endDateTime",
      "created_date",
      "updated_date",
      "archive",
      "id_clients_graph_api_microsoft_secret"
    ],
    "domain": "Clients & CRM",
    "columnCount": 9
  },
  {
    "name": "clients_graph_sync",
    "columns": [
      "id_graph_sync",
      "id_client",
      "graph_product_guid",
      "id_product",
      "quantity_assigned",
      "synced_at",
      "sync_error"
    ],
    "domain": "Clients & CRM",
    "columnCount": 7
  },
  {
    "name": "clients_guides_templates",
    "columns": [
      "title",
      "is_default",
      "id_guide_template"
    ],
    "domain": "Clients & CRM",
    "columnCount": 3
  },
  {
    "name": "clients_incident_assets_links",
    "columns": [
      "id_incident",
      "id_asset",
      "id_incident_asset_link"
    ],
    "domain": "Clients & CRM",
    "columnCount": 3
  },
  {
    "name": "clients_incident_checks_analyses",
    "columns": [
      "id_check_analysis",
      "name",
      "archived"
    ],
    "domain": "Clients & CRM",
    "columnCount": 3
  },
  {
    "name": "clients_incident_checks_analyses_links",
    "columns": [
      "id_check_analysis_link",
      "id_incident",
      "id_check_analysis"
    ],
    "domain": "Clients & CRM",
    "columnCount": 3
  },
  {
    "name": "clients_incident_clients_links",
    "columns": [
      "id_client",
      "id_incident",
      "id_incident_client_link"
    ],
    "domain": "Clients & CRM",
    "columnCount": 3
  },
  {
    "name": "clients_incident_consequences",
    "columns": [
      "name",
      "archived",
      "id_incident_consequence"
    ],
    "domain": "Clients & CRM",
    "columnCount": 3
  },
  {
    "name": "clients_incident_consequences_links",
    "columns": [
      "id_incident",
      "id_incident_consequence",
      "id_incident_consequence_link"
    ],
    "domain": "Clients & CRM",
    "columnCount": 3
  },
  {
    "name": "clients_incident_containment_measures",
    "columns": [
      "id_containment_measure",
      "name",
      "archived"
    ],
    "domain": "Clients & CRM",
    "columnCount": 3
  },
  {
    "name": "clients_incident_containment_measures_links",
    "columns": [
      "id_containment_measure_link",
      "id_incident",
      "id_containment_measure"
    ],
    "domain": "Clients & CRM",
    "columnCount": 3
  },
  {
    "name": "clients_incident_eradication_measures",
    "columns": [
      "id_eradication_measure",
      "name",
      "archived"
    ],
    "domain": "Clients & CRM",
    "columnCount": 3
  },
  {
    "name": "clients_incident_eradication_measures_links",
    "columns": [
      "id_eradication_measure_link",
      "id_incident",
      "id_eradication_measure"
    ],
    "domain": "Clients & CRM",
    "columnCount": 3
  },
  {
    "name": "clients_incident_impacts",
    "columns": [
      "name",
      "archived",
      "id_incident_impact"
    ],
    "domain": "Clients & CRM",
    "columnCount": 3
  },
  {
    "name": "clients_incident_impacts_links",
    "columns": [
      "id_incident",
      "id_incident_impact",
      "id_incident_impact_link"
    ],
    "domain": "Clients & CRM",
    "columnCount": 3
  },
  {
    "name": "clients_incident_interruption_time_options",
    "columns": [
      "id_interruption_time_option",
      "name",
      "position",
      "archived"
    ],
    "domain": "Clients & CRM",
    "columnCount": 4
  },
  {
    "name": "clients_incident_law25_info_types",
    "columns": [
      "id_law25_info_type",
      "name",
      "archived"
    ],
    "domain": "Clients & CRM",
    "columnCount": 3
  },
  {
    "name": "clients_incident_law25_info_types_links",
    "columns": [
      "id_law25_info_type_link",
      "id_incident",
      "id_law25_info_type"
    ],
    "domain": "Clients & CRM",
    "columnCount": 3
  },
  {
    "name": "clients_incident_law25_malicious_uses",
    "columns": [
      "name",
      "archived",
      "id_incident_malicious_use"
    ],
    "domain": "Clients & CRM",
    "columnCount": 3
  },
  {
    "name": "clients_incident_law25_malicious_uses_links",
    "columns": [
      "id_incident_malicious_use",
      "id_incident",
      "id_incident_malicious_use_link"
    ],
    "domain": "Clients & CRM",
    "columnCount": 3
  },
  {
    "name": "clients_incident_logs",
    "columns": [
      "id_incident_log",
      "id_incident",
      "date_time",
      "affected_asset",
      "completed_action",
      "desired_result"
    ],
    "domain": "Clients & CRM",
    "columnCount": 6
  },
  {
    "name": "clients_incident_probable_causes",
    "columns": [
      "id_probable_cause",
      "name",
      "archived"
    ],
    "domain": "Clients & CRM",
    "columnCount": 3
  },
  {
    "name": "clients_incident_probable_causes_links",
    "columns": [
      "id_probable_cause_link",
      "id_incident",
      "id_probable_cause"
    ],
    "domain": "Clients & CRM",
    "columnCount": 3
  },
  {
    "name": "clients_incident_reporting_methods",
    "columns": [
      "id_reporting_method",
      "name",
      "archived"
    ],
    "domain": "Clients & CRM",
    "columnCount": 3
  },
  {
    "name": "clients_incident_restoration_actions",
    "columns": [
      "id_restoration_action",
      "name",
      "archived"
    ],
    "domain": "Clients & CRM",
    "columnCount": 3
  },
  {
    "name": "clients_incident_restoration_actions_links",
    "columns": [
      "id_restoration_action_link",
      "id_incident",
      "id_restoration_action"
    ],
    "domain": "Clients & CRM",
    "columnCount": 3
  },
  {
    "name": "clients_incident_sensitive_data_links",
    "columns": [
      "id_incident",
      "id_sensitive_data",
      "id_incident_sensitive_data_link"
    ],
    "domain": "Clients & CRM",
    "columnCount": 3
  },
  {
    "name": "clients_incident_types_links",
    "columns": [
      "id_incident",
      "id_incident_type",
      "id_incident_type_link"
    ],
    "domain": "Clients & CRM",
    "columnCount": 3
  },
  {
    "name": "clients_incidents",
    "columns": [
      "name",
      "description",
      "id_client",
      "id_incident_type",
      "start_date",
      "end_date",
      "victims",
      "creator_name",
      "created_at",
      "updated_at",
      "communicated",
      "communication_date",
      "is_law_25",
      "archived",
      "measures",
      "law25_other_info_type",
      "receipt_date",
      "law25_info_type",
      "law25_people_number",
      "law25_circumstances",
      "law25_harm_evaluation",
      "law25_receipt_date",
      "law25_other_use",
      "law25_harmful_use_probability",
      "law25_harm",
      "law25_harm_explanation",
      "law25_consequences",
      "advised_people",
      "advising_date",
      "is_event",
      "other_incident_type",
      "id_reporting_method",
      "other_reporting_method",
      "other_probable_cause",
      "other_containment_measure",
      "other_check_analysis",
      "other_eradication_measure",
      "other_restoration_action",
      "id_interruption_time_option",
      "other_law25_info_type",
      "id_ticket",
      "report_date",
      "report_author",
      "received_message",
      "initial_call_tech",
      "incident_handler_tech",
      "excessive_rights_involved",
      "software_vulnerabilities_involved",
      "malware_involved",
      "resolution_date",
      "law25_sensitivity",
      "following_day_check",
      "following_day_check_explanation",
      "one_week_after_check",
      "one_week_after_check_explanation",
      "recommendations_and_comments",
      "report_approved",
      "id_incident"
    ],
    "domain": "Clients & CRM",
    "columnCount": 58
  },
  {
    "name": "clients_infrastructures",
    "columns": [
      "id_client",
      "name",
      "virtual_cpu",
      "cpu_speed",
      "ram_available",
      "ram_usage",
      "hdd_available",
      "hdd_used",
      "creation_date",
      "id_user_created",
      "deleted_date",
      "id_user_deleted",
      "type",
      "departement",
      "note",
      "id_infrastructure"
    ],
    "domain": "Clients & CRM",
    "columnCount": 16
  },
  {
    "name": "clients_infrastructures_logs",
    "columns": [
      "id_infrastructure",
      "id_user",
      "creation_date",
      "field",
      "value_from",
      "value_to",
      "id_log"
    ],
    "domain": "Clients & CRM",
    "columnCount": 7
  },
  {
    "name": "clients_ingram_invoices",
    "columns": [
      "sync_date",
      "commitment_date",
      "id_client",
      "id_product",
      "id_invoice",
      "quantity",
      "status",
      "renew",
      "id_client_ingram_invoice"
    ],
    "domain": "Clients & CRM",
    "columnCount": 9
  },
  {
    "name": "clients_ingram_sync",
    "columns": [
      "sync_date",
      "commitment_date",
      "id_client",
      "id_product",
      "quantity",
      "status",
      "renew",
      "id_ingram_sync"
    ],
    "domain": "Clients & CRM",
    "columnCount": 8
  },
  {
    "name": "clients_ingram_sync_v2",
    "columns": [
      "id_ingram_sync",
      "sync_date",
      "commitment_date",
      "id_client",
      "ingram_marketplace_customer_id",
      "ingram_name",
      "id_product",
      "quantity",
      "status",
      "renew"
    ],
    "domain": "Clients & CRM",
    "columnCount": 10
  },
  {
    "name": "clients_ingrams_notes",
    "columns": [
      "id_clients_ingrams_notes",
      "id_client",
      "notes",
      "notes_history",
      "created_date",
      "updated_date"
    ],
    "domain": "Clients & CRM",
    "columnCount": 6
  },
  {
    "name": "clients_ingrams_validations",
    "columns": [
      "id_validation",
      "action_date",
      "validation_status",
      "id_user",
      "id_client",
      "id_product"
    ],
    "domain": "Clients & CRM",
    "columnCount": 6
  },
  {
    "name": "clients_licenses",
    "columns": [
      "id_product",
      "qty_included",
      "qty_missing",
      "id_client",
      "type",
      "id_client_license"
    ],
    "domain": "Clients & CRM",
    "columnCount": 6
  },
  {
    "name": "clients_licenses_files",
    "columns": [
      "id_client",
      "file_name",
      "file_size",
      "file_data",
      "id_file"
    ],
    "domain": "Clients & CRM",
    "columnCount": 5
  },
  {
    "name": "clients_licenses_versions",
    "columns": [
      "id_client_license",
      "id_version",
      "qty",
      "id_client_license_version"
    ],
    "domain": "Clients & CRM",
    "columnCount": 4
  },
  {
    "name": "clients_links",
    "columns": [
      "id_parent_client",
      "id_child_client",
      "id_client_link"
    ],
    "domain": "Clients & CRM",
    "columnCount": 3
  },
  {
    "name": "clients_list_tags",
    "columns": [
      "name",
      "inactive",
      "id_tag"
    ],
    "domain": "Clients & CRM",
    "columnCount": 3
  },
  {
    "name": "clients_loi25",
    "columns": [
      "liable_person",
      "backup_person",
      "display",
      "incidents",
      "personal_information",
      "privacy_policy",
      "retention_policy",
      "roles_responsibilities",
      "complaints",
      "training",
      "notice_template",
      "consent_request",
      "intervention_plan",
      "portability_right",
      "id_client",
      "signature_contact_details",
      "biometrics_config",
      "biometrics_use",
      "biometrics_type_fingerprint",
      "biometrics_type_facial_recognition",
      "biometrics_type_other",
      "biometrics_location",
      "biometrics_declaration",
      "biometrics_measures",
      "biometrics_actions",
      "incidents_config",
      "incidents_responsible_is_liable_person",
      "incidents_responsible_name",
      "incidents_email_address",
      "incidents_phone_number",
      "incidents_processing_time",
      "portability_contact_email",
      "portability_responsible_person",
      "portability_customers_requests_enabled",
      "portability_customers_requests_procedure",
      "portability_employees_requests_procedure",
      "portability_refusal_possible_reasons",
      "portability_refusal_response_time",
      "id_loi25"
    ],
    "domain": "Clients & CRM",
    "columnCount": 39
  },
  {
    "name": "clients_new_incident_types",
    "columns": [
      "id_incident_type",
      "name",
      "archived"
    ],
    "domain": "Clients & CRM",
    "columnCount": 3
  },
  {
    "name": "clients_nist_categories_scores",
    "columns": [
      "id_client",
      "id_nist_category",
      "initial_score",
      "score",
      "target",
      "note",
      "not_applicable",
      "id_category_score"
    ],
    "domain": "Clients & CRM",
    "columnCount": 8
  },
  {
    "name": "clients_nist_functions_scores",
    "columns": [
      "id_client",
      "id_nist_function",
      "initial_score",
      "score",
      "target",
      "note",
      "not_applicable",
      "id_function_score"
    ],
    "domain": "Clients & CRM",
    "columnCount": 8
  },
  {
    "name": "clients_nist_subcategories_scores",
    "columns": [
      "id_client",
      "id_nist_subcategory",
      "initial_score",
      "score",
      "target",
      "note",
      "not_applicable",
      "id_subcategory_score"
    ],
    "domain": "Clients & CRM",
    "columnCount": 8
  },
  {
    "name": "clients_parrains",
    "columns": [
      "id_client",
      "id_user",
      "created_at"
    ],
    "domain": "Clients & CRM",
    "columnCount": 3
  },
  {
    "name": "clients_password_policies",
    "columns": [
      "id_client",
      "name",
      "description",
      "length",
      "special_characters",
      "expiration_delay",
      "attempts_number_limit",
      "password_history",
      "access_recovery",
      "vault",
      "mfa",
      "created_at",
      "updated_at",
      "id_password_policy"
    ],
    "domain": "Clients & CRM",
    "columnCount": 14
  },
  {
    "name": "clients_password_policies_assets_links",
    "columns": [
      "id_password_policy",
      "id_asset",
      "id_password_policy_asset_link"
    ],
    "domain": "Clients & CRM",
    "columnCount": 3
  },
  {
    "name": "clients_plans",
    "columns": [
      "id_plan",
      "id_client",
      "plan_type",
      "name",
      "description",
      "is_executed",
      "execution_date"
    ],
    "domain": "Clients & CRM",
    "columnCount": 7
  },
  {
    "name": "clients_plans_all_items",
    "columns": [
      "id_plan_item",
      "plan_type",
      "category",
      "number",
      "title",
      "description",
      "notes",
      "law_25",
      "id_client"
    ],
    "domain": "Clients & CRM",
    "columnCount": 9
  },
  {
    "name": "clients_plans_asset_links",
    "columns": [
      "id_plan_asset_link",
      "id_plan",
      "id_asset"
    ],
    "domain": "Clients & CRM",
    "columnCount": 3
  },
  {
    "name": "clients_plans_incident_links",
    "columns": [
      "id_plan_incident_link",
      "id_plan",
      "id_incident"
    ],
    "domain": "Clients & CRM",
    "columnCount": 3
  },
  {
    "name": "clients_plans_selected_items",
    "columns": [
      "id_plan_item",
      "id_plan",
      "category",
      "number",
      "title",
      "description",
      "notes",
      "law_25",
      "completed"
    ],
    "domain": "Clients & CRM",
    "columnCount": 9
  },
  {
    "name": "clients_plans_self_links",
    "columns": [
      "id_plan_self_link",
      "id_plan_1",
      "id_plan_2"
    ],
    "domain": "Clients & CRM",
    "columnCount": 3
  },
  {
    "name": "clients_primes",
    "columns": [
      "id_client",
      "id_user",
      "type",
      "isprimary"
    ],
    "domain": "Clients & CRM",
    "columnCount": 4
  },
  {
    "name": "clients_public_folders",
    "columns": [
      "id_client",
      "name",
      "path",
      "title",
      "content_html",
      "content_text",
      "date_uploaded",
      "date",
      "from",
      "to",
      "id_clients_public_folder"
    ],
    "domain": "Clients & CRM",
    "columnCount": 11
  },
  {
    "name": "clients_public_folders_files",
    "columns": [
      "id_clients_public_folder",
      "name",
      "content_type",
      "inline",
      "data",
      "id_clients_public_folders_file"
    ],
    "domain": "Clients & CRM",
    "columnCount": 6
  },
  {
    "name": "clients_quiz_answers",
    "columns": [
      "id_client",
      "id_quiz_question",
      "text_answer",
      "selected_item_answer",
      "multiple_answers",
      "notes",
      "id_quiz_answer"
    ],
    "domain": "Clients & CRM",
    "columnCount": 7
  },
  {
    "name": "clients_quiz_answers_items_links",
    "columns": [
      "id_quiz_answer",
      "id_quiz_item",
      "percentage_answer",
      "id_quiz_answer_item_link"
    ],
    "domain": "Clients & CRM",
    "columnCount": 4
  },
  {
    "name": "clients_quiz_categories",
    "columns": [
      "name",
      "position",
      "id_quiz_category"
    ],
    "domain": "Clients & CRM",
    "columnCount": 3
  },
  {
    "name": "clients_quiz_items",
    "columns": [
      "name",
      "id_quiz_question",
      "position",
      "id_quiz_item"
    ],
    "domain": "Clients & CRM",
    "columnCount": 4
  },
  {
    "name": "clients_quiz_question_types",
    "columns": [
      "unique_code",
      "name",
      "description",
      "id_quiz_question_type"
    ],
    "domain": "Clients & CRM",
    "columnCount": 4
  },
  {
    "name": "clients_quiz_questions",
    "columns": [
      "name",
      "id_quiz_category",
      "position",
      "id_quiz_question_type",
      "id_quiz_question"
    ],
    "domain": "Clients & CRM",
    "columnCount": 5
  },
  {
    "name": "clients_raci_categories",
    "columns": [
      "name",
      "id_raci_matrix",
      "position",
      "id_raci_category"
    ],
    "domain": "Clients & CRM",
    "columnCount": 4
  },
  {
    "name": "clients_raci_matrices",
    "columns": [
      "title",
      "id_client",
      "archived",
      "is_template",
      "created_at",
      "id_raci_matrix"
    ],
    "domain": "Clients & CRM",
    "columnCount": 6
  },
  {
    "name": "clients_raci_responsibilities",
    "columns": [
      "name",
      "description",
      "id_raci_matrix",
      "position",
      "id_raci_category",
      "id_raci_responsibility"
    ],
    "domain": "Clients & CRM",
    "columnCount": 6
  },
  {
    "name": "clients_raci_roles",
    "columns": [
      "name",
      "description",
      "id_contact",
      "id_raci_matrix",
      "signed",
      "is_client_contact",
      "position",
      "id_raci_role"
    ],
    "domain": "Clients & CRM",
    "columnCount": 8
  },
  {
    "name": "clients_raci_squares",
    "columns": [
      "id_raci_value",
      "id_raci_role",
      "id_raci_responsibility",
      "id_raci_square"
    ],
    "domain": "Clients & CRM",
    "columnCount": 4
  },
  {
    "name": "clients_raci_types",
    "columns": [
      "name",
      "archived",
      "id_raci_type"
    ],
    "domain": "Clients & CRM",
    "columnCount": 3
  },
  {
    "name": "clients_raci_types_links",
    "columns": [
      "id_raci_matrix",
      "id_raci_type",
      "id_raci_type_link"
    ],
    "domain": "Clients & CRM",
    "columnCount": 3
  },
  {
    "name": "clients_raci_values",
    "columns": [
      "letter",
      "archived",
      "title",
      "subtitle",
      "who",
      "mission",
      "particularity",
      "order",
      "id_raci_value"
    ],
    "domain": "Clients & CRM",
    "columnCount": 9
  },
  {
    "name": "clients_reduction_measures",
    "columns": [
      "id_reduction_measure",
      "id_client",
      "name",
      "status",
      "level",
      "domain",
      "estimated_end_date",
      "completed_date",
      "notes"
    ],
    "domain": "Clients & CRM",
    "columnCount": 9
  },
  {
    "name": "clients_reduction_measures_assets_links",
    "columns": [
      "id_reduction_measure_asset_link",
      "id_reduction_measure",
      "id_asset"
    ],
    "domain": "Clients & CRM",
    "columnCount": 3
  },
  {
    "name": "clients_reduction_measures_nist_subcategories_links",
    "columns": [
      "id_reduction_measure_nist_subcategory_link",
      "id_reduction_measure",
      "id_nist_subcategory",
      "points"
    ],
    "domain": "Clients & CRM",
    "columnCount": 4
  },
  {
    "name": "clients_reduction_measures_risks_links",
    "columns": [
      "id_reduction_measure_risk_link",
      "id_reduction_measure",
      "id_risk"
    ],
    "domain": "Clients & CRM",
    "columnCount": 3
  },
  {
    "name": "clients_remote_accesses",
    "columns": [
      "id_client",
      "title",
      "description",
      "protocol",
      "encryption",
      "cipher",
      "auth_password",
      "mfa",
      "zero_trust",
      "least_privilege",
      "granular_access",
      "endpoint_assets",
      "monitor_sessions",
      "employee_awareness",
      "created_at",
      "updated_at",
      "id_remote_access"
    ],
    "domain": "Clients & CRM",
    "columnCount": 17
  },
  {
    "name": "clients_remote_accesses_assets_links",
    "columns": [
      "id_remote_access",
      "id_asset",
      "id_remote_access_asset_link"
    ],
    "domain": "Clients & CRM",
    "columnCount": 3
  },
  {
    "name": "clients_risks",
    "columns": [
      "id_client",
      "id_asset",
      "id_risk_type",
      "threat_level",
      "threat_internal",
      "threat_external",
      "probability_occurrence",
      "probability_complexity",
      "vulnerability_level",
      "impact_availability",
      "impact_integrity",
      "impact_confidentiality",
      "impact_proofs",
      "impact_authenticity",
      "impact_non_repudiation",
      "consequence_service_interruption",
      "consequence_legal_sanctions",
      "consequence_confidentiality",
      "consequence_data_theft",
      "consequence_reputation",
      "consequence_operations",
      "consequence_cost",
      "id_risk"
    ],
    "domain": "Clients & CRM",
    "columnCount": 23
  },
  {
    "name": "clients_risks_types",
    "columns": [
      "id_risk_type",
      "name",
      "description",
      "cyber_attack",
      "archived"
    ],
    "domain": "Clients & CRM",
    "columnCount": 5
  },
  {
    "name": "clients_risks_vulnerabilities",
    "columns": [
      "name",
      "id_risk_vulnerability"
    ],
    "domain": "Clients & CRM",
    "columnCount": 2
  },
  {
    "name": "clients_risks_vulnerabilities_links",
    "columns": [
      "id_risk",
      "id_risk_vulnerability",
      "id_risk_vulnerability_link"
    ],
    "domain": "Clients & CRM",
    "columnCount": 3
  },
  {
    "name": "clients_scoptel",
    "columns": [
      "id_client",
      "tenant_name",
      "source_enabled",
      "synced_extensions_at",
      "note",
      "created_at",
      "updated_at"
    ],
    "domain": "Clients & CRM",
    "columnCount": 7
  },
  {
    "name": "clients_sensitive_data",
    "columns": [
      "name",
      "description",
      "security_level",
      "id_client",
      "id_type",
      "is_law_25",
      "is_detrimental",
      "other_information_retaining_purpose",
      "other_file_management_mode",
      "other_file_information_source",
      "other_file_information_people_categories",
      "other_file_access_people_categories",
      "other_file_retention_period",
      "archived",
      "id_sensitive_data"
    ],
    "domain": "Clients & CRM",
    "columnCount": 15
  },
  {
    "name": "clients_sensitive_data_asset_links",
    "columns": [
      "id_sensitive_data",
      "id_asset",
      "id_sensitive_data_asset_link"
    ],
    "domain": "Clients & CRM",
    "columnCount": 3
  },
  {
    "name": "clients_sensitive_data_categories",
    "columns": [
      "name",
      "group",
      "position",
      "archived",
      "id_sensitive_data_category"
    ],
    "domain": "Clients & CRM",
    "columnCount": 5
  },
  {
    "name": "clients_sensitive_data_category_links",
    "columns": [
      "id_sensitive_data",
      "id_sensitive_data_category",
      "id_sensitive_data_category_link"
    ],
    "domain": "Clients & CRM",
    "columnCount": 3
  },
  {
    "name": "clients_sensitive_data_types",
    "columns": [
      "name",
      "archived",
      "id_sensitive_data_type"
    ],
    "domain": "Clients & CRM",
    "columnCount": 3
  },
  {
    "name": "clients_sherweb_invoices",
    "columns": [
      "sync_date",
      "commitment_date",
      "id_client",
      "id_product",
      "id_invoice",
      "quantity",
      "status",
      "renew",
      "id_client_sherweb_invoice"
    ],
    "domain": "Clients & CRM",
    "columnCount": 9
  },
  {
    "name": "clients_sherweb_sync",
    "columns": [
      "sync_date",
      "commitment_date",
      "id_client",
      "id_product",
      "quantity",
      "status",
      "renew",
      "id_sherweb_sync"
    ],
    "domain": "Clients & CRM",
    "columnCount": 8
  },
  {
    "name": "clients_succursale",
    "columns": [
      "id_client",
      "nom",
      "adresse",
      "ville",
      "province",
      "codepostal",
      "telephone",
      "fax",
      "km_to_bz",
      "society_full_name",
      "bank_institution",
      "bank_succursale",
      "bank_account",
      "id_stripe",
      "default_payment",
      "autopay",
      "id_succursale"
    ],
    "domain": "Clients & CRM",
    "columnCount": 17
  },
  {
    "name": "clients_tags",
    "columns": [
      "id_client",
      "id_tag",
      "id_client_tag"
    ],
    "domain": "Clients & CRM",
    "columnCount": 3
  },
  {
    "name": "com_comptes_depenses",
    "columns": [
      "date",
      "type_depense_id",
      "client_id",
      "description",
      "km",
      "brut",
      "tps",
      "tvq",
      "net",
      "file_id",
      "periode_tech_id",
      "refacturation",
      "date_refacturation",
      "id_intervention",
      "compte_id"
    ],
    "domain": "Communications",
    "columnCount": 15
  },
  {
    "name": "com_files",
    "columns": [
      "upload_data",
      "file_id"
    ],
    "domain": "Communications",
    "columnCount": 2
  },
  {
    "name": "com_params",
    "columns": [
      "name",
      "description",
      "valeur1",
      "valeur2",
      "valeur3",
      "type_param",
      "param_id"
    ],
    "domain": "Communications",
    "columnCount": 7
  },
  {
    "name": "com_periodes",
    "columns": [
      "periode",
      "active",
      "periode_id"
    ],
    "domain": "Communications",
    "columnCount": 3
  },
  {
    "name": "com_periodes_techs",
    "columns": [
      "tech_id",
      "periode_id",
      "approbation",
      "numero",
      "date_creation",
      "date_approbation",
      "periode_tech_id"
    ],
    "domain": "Communications",
    "columnCount": 7
  },
  {
    "name": "com_types_depenses",
    "columns": [
      "nom",
      "tps",
      "tvq",
      "type_id"
    ],
    "domain": "Communications",
    "columnCount": 4
  },
  {
    "name": "committe_members",
    "columns": [
      "id_committe_member",
      "id_user",
      "id_committe",
      "joined_at"
    ],
    "domain": "Legacy / noyau divers",
    "columnCount": 4
  },
  {
    "name": "committes",
    "columns": [
      "id_committe",
      "code",
      "name",
      "description",
      "contact",
      "created_at"
    ],
    "domain": "Legacy / noyau divers",
    "columnCount": 6
  },
  {
    "name": "config_asterisk",
    "columns": [
      "nom",
      "description",
      "ip",
      "mysql_user",
      "mysql_pass",
      "id_asterisk"
    ],
    "domain": "Legacy / noyau divers",
    "columnCount": 6
  },
  {
    "name": "config_ratetable",
    "columns": [
      "prefix",
      "country",
      "id_rate"
    ],
    "domain": "Legacy / noyau divers",
    "columnCount": 3
  },
  {
    "name": "config_telecomstats",
    "columns": [
      "config_value",
      "config_key",
      "id_config"
    ],
    "domain": "Legacy / noyau divers",
    "columnCount": 3
  },
  {
    "name": "contracts",
    "columns": [
      "title",
      "id_client",
      "id_succursale",
      "is_backup",
      "is_cyber",
      "is_prog",
      "is_tel",
      "is_ti",
      "next_billing_date",
      "billing_cycle",
      "start_date",
      "end_date",
      "closing_date",
      "birth_date",
      "duration",
      "extension_duration",
      "annual_increase_percent",
      "end_of_contract_percent",
      "status",
      "internal_notes",
      "internal_notes_read",
      "accounting_notes",
      "accounting_notes_read",
      "show_annexe_bundles",
      "show_annexe_licences",
      "show_annexe_users",
      "created_date",
      "updated_date",
      "pending_invoice_verification",
      "id_contract"
    ],
    "domain": "Contrats",
    "columnCount": 30
  },
  {
    "name": "contracts_alerts",
    "columns": [
      "id_contract",
      "message",
      "renew_subscription",
      "license_365",
      "alert_date",
      "send_to",
      "status",
      "created_date",
      "updated_date",
      "id_alert"
    ],
    "domain": "Contrats",
    "columnCount": 10
  },
  {
    "name": "contracts_contacts",
    "columns": [
      "id_contract",
      "email",
      "created_date",
      "id_contract_contact"
    ],
    "domain": "Contrats",
    "columnCount": 4
  },
  {
    "name": "contracts_files",
    "columns": [
      "id_contract",
      "id_client",
      "path",
      "name",
      "size",
      "data",
      "preview",
      "filetype",
      "is_archived",
      "created_at",
      "updated_at",
      "id_contract_file"
    ],
    "domain": "Contrats",
    "columnCount": 12
  },
  {
    "name": "contracts_imports_ingram_invoices",
    "columns": [
      "invoice_date",
      "line_number",
      "ingram_customer_id",
      "id_client",
      "client_name",
      "order_number",
      "service_description",
      "quantity",
      "unit_price",
      "discount",
      "duration_month",
      "total",
      "created_date",
      "id_ingram_import"
    ],
    "domain": "Contrats",
    "columnCount": 14
  },
  {
    "name": "contracts_imports_sherweb_invoices",
    "columns": [
      "invoice_date",
      "sherweb_customer_id",
      "id_client",
      "client_name",
      "invoice_number",
      "product_description",
      "quantity",
      "unit_price",
      "discount",
      "duration_month",
      "total",
      "created_date",
      "id_sherweb_import"
    ],
    "domain": "Contrats",
    "columnCount": 13
  },
  {
    "name": "contracts_invoices",
    "columns": [
      "id_contract",
      "id_client",
      "invoice_number",
      "title",
      "invoice_date",
      "status",
      "type",
      "description",
      "client_name",
      "client_address",
      "client_city",
      "client_state",
      "client_zip",
      "client_phone",
      "id_succursale",
      "succursale_name",
      "succursale_address",
      "succursale_city",
      "succursale_state",
      "succursale_zip",
      "succursale_phone",
      "subtotal",
      "tps",
      "tvq",
      "total",
      "is_taxable",
      "paid",
      "balance",
      "is_send",
      "show_annexe_bundles",
      "show_annexe_licences",
      "show_annexe_users",
      "created_date",
      "updated_date",
      "id_user_prime_vente",
      "id_user_prime_vente_at_invoice",
      "subtotal_2",
      "tps_2",
      "tvq_2",
      "total_2",
      "id_invoice"
    ],
    "domain": "Contrats",
    "columnCount": 41
  },
  {
    "name": "contracts_invoices_items",
    "columns": [
      "id_invoice",
      "title",
      "order",
      "type",
      "show_to_invoice",
      "id_bundle",
      "id_product",
      "automatique",
      "calculate_quantity",
      "quantity",
      "price",
      "show_annexe",
      "expend_bundle",
      "created_date",
      "updated_date",
      "quantity2",
      "id_invoice_item"
    ],
    "domain": "Contrats",
    "columnCount": 17
  },
  {
    "name": "contracts_invoices_paiements",
    "columns": [
      "id_client",
      "paiement_method",
      "paiement_date",
      "excess_amount",
      "amount",
      "notes",
      "reference",
      "is_ppa_sent",
      "created_date",
      "updated_date",
      "paiement_date_future",
      "id_paiement"
    ],
    "domain": "Contrats",
    "columnCount": 12
  },
  {
    "name": "contracts_invoices_paiements_excess",
    "columns": [
      "id_paiement",
      "id_client",
      "paiement_date",
      "transaction_amount",
      "sold_amount",
      "created_date",
      "id_paiement_excess"
    ],
    "domain": "Contrats",
    "columnCount": 7
  },
  {
    "name": "contracts_invoices_paiements_invoices",
    "columns": [
      "id_paiement",
      "id_invoice",
      "amount",
      "discount",
      "created_date",
      "updated_date",
      "id_paiement_invoice"
    ],
    "domain": "Contrats",
    "columnCount": 7
  },
  {
    "name": "contracts_invoices_sequence_number",
    "columns": [
      "invoice_number",
      "id_sequence"
    ],
    "domain": "Contrats",
    "columnCount": 2
  },
  {
    "name": "contracts_items",
    "columns": [
      "id_contract",
      "title",
      "order",
      "type",
      "show_to_invoice",
      "id_bundle",
      "id_product",
      "automatique",
      "calculate_quantity",
      "quantity",
      "price",
      "show_annexe",
      "expend_bundle",
      "created_date",
      "updated_date",
      "quantity2",
      "id_contract_item"
    ],
    "domain": "Contrats",
    "columnCount": 17
  },
  {
    "name": "contracts_logs",
    "columns": [
      "id_contract",
      "id_contract_item",
      "part_number",
      "bundle_name",
      "id_user",
      "date",
      "field",
      "field_label",
      "old_value",
      "new_value",
      "id_contract_log"
    ],
    "domain": "Contrats",
    "columnCount": 11
  },
  {
    "name": "contracts_microsoft_graph_invoices",
    "columns": [
      "id_invoice",
      "id_client",
      "id_product",
      "quantity_ordered",
      "quantity_assigned",
      "created_date",
      "id_graph_invoice"
    ],
    "domain": "Contrats",
    "columnCount": 7
  },
  {
    "name": "contracts_microsoft_graph_invoices_users",
    "columns": [
      "id_invoice",
      "id_product",
      "graph_user_guid",
      "graph_user_email",
      "graph_user_name",
      "id_graph_invoice_user"
    ],
    "domain": "Contrats",
    "columnCount": 6
  },
  {
    "name": "contracts_notes_archive",
    "columns": [
      "id_contract",
      "notes",
      "read",
      "note_type",
      "archived_date",
      "id_notes_archive"
    ],
    "domain": "Contrats",
    "columnCount": 6
  },
  {
    "name": "contracts_recevables_notes",
    "columns": [
      "id_client",
      "type",
      "level",
      "subject",
      "tos",
      "id_creator",
      "body",
      "is_sent",
      "sent_date",
      "archived",
      "created_date",
      "updated_date",
      "id_recevable_note"
    ],
    "domain": "Contrats",
    "columnCount": 13
  },
  {
    "name": "contracts_recevables_notes_invoices",
    "columns": [
      "id_invoice",
      "id_recevable_note",
      "created_date",
      "updated_date",
      "id_recevable_note_invoice"
    ],
    "domain": "Contrats",
    "columnCount": 5
  },
  {
    "name": "cyberops_ai_prompts",
    "columns": [
      "id_cyber_prompt",
      "name",
      "text",
      "type_id",
      "type_name",
      "is_archived",
      "created_at"
    ],
    "domain": "Legacy / noyau divers",
    "columnCount": 7
  },
  {
    "name": "data",
    "columns": [
      "index",
      "nom",
      "type",
      "amount",
      "dt"
    ],
    "domain": "Legacy / noyau divers",
    "columnCount": 5
  },
  {
    "name": "departement",
    "columns": [
      "nom",
      "idDepartement"
    ],
    "domain": "Legacy / noyau divers",
    "columnCount": 2
  },
  {
    "name": "department_director",
    "columns": [
      "id_department",
      "id_user",
      "id_department_director"
    ],
    "domain": "Legacy / noyau divers",
    "columnCount": 3
  },
  {
    "name": "departments",
    "columns": [
      "id_department",
      "name",
      "description"
    ],
    "domain": "Legacy / noyau divers",
    "columnCount": 3
  },
  {
    "name": "departments_accounting_categories",
    "columns": [
      "id_department_accounting_category",
      "id_department",
      "id_category"
    ],
    "domain": "Legacy / noyau divers",
    "columnCount": 3
  },
  {
    "name": "departments_users",
    "columns": [
      "id_department_user",
      "id_department",
      "id_user",
      "is_director"
    ],
    "domain": "Legacy / noyau divers",
    "columnCount": 4
  },
  {
    "name": "documentation_catalog_dependencies",
    "columns": [
      "id_parent_asset",
      "id_child_asset",
      "id_dependency"
    ],
    "domain": "Documentation",
    "columnCount": 3
  },
  {
    "name": "documentation_catalog_groups",
    "columns": [
      "id_client",
      "level",
      "name",
      "description",
      "id_group"
    ],
    "domain": "Documentation",
    "columnCount": 5
  },
  {
    "name": "documentation_clientpermissions",
    "columns": [
      "id_client",
      "id_user",
      "id_documentation_clientpermissions"
    ],
    "domain": "Documentation",
    "columnCount": 3
  },
  {
    "name": "documentation_clients_rights",
    "columns": [
      "id_client",
      "id_documentation_group",
      "id_user",
      "is_group",
      "is_deny",
      "id_documentation_client_right"
    ],
    "domain": "Documentation",
    "columnCount": 6
  },
  {
    "name": "documentation_document_views",
    "columns": [
      "id_view",
      "id_user",
      "viewtime",
      "id_document"
    ],
    "domain": "Documentation",
    "columnCount": 4
  },
  {
    "name": "documentation_documents",
    "columns": [
      "id_type",
      "name",
      "link",
      "description",
      "creationdate",
      "id_client",
      "id_succursale",
      "active",
      "archived",
      "is_master",
      "id_master",
      "information",
      "is_asset",
      "id_group",
      "renewal_guarantee",
      "renewal_serial_number",
      "renewal_notes",
      "is_doc",
      "importance_level",
      "id_document"
    ],
    "domain": "Documentation",
    "columnCount": 20
  },
  {
    "name": "documentation_documents_clients",
    "columns": [
      "id_document",
      "id_client",
      "id_documentationclient"
    ],
    "domain": "Documentation",
    "columnCount": 3
  },
  {
    "name": "documentation_documents_data",
    "columns": [
      "id_document",
      "id_field",
      "data",
      "id_client",
      "id_data"
    ],
    "domain": "Documentation",
    "columnCount": 5
  },
  {
    "name": "documentation_documents_fields",
    "columns": [
      "id_document",
      "id_field",
      "id_docfield"
    ],
    "domain": "Documentation",
    "columnCount": 3
  },
  {
    "name": "documentation_documents_rights",
    "columns": [
      "id_document",
      "id_documentation_group",
      "id_user",
      "is_group",
      "is_deny",
      "id_documentation_document_right"
    ],
    "domain": "Documentation",
    "columnCount": 6
  },
  {
    "name": "documentation_fields",
    "columns": [
      "name",
      "type",
      "id_field"
    ],
    "domain": "Documentation",
    "columnCount": 3
  },
  {
    "name": "documentation_files",
    "columns": [
      "id_document",
      "id_file",
      "creationdate",
      "id_client",
      "id_documentationfile"
    ],
    "domain": "Documentation",
    "columnCount": 5
  },
  {
    "name": "documentation_files_logs",
    "columns": [
      "id_documentationfile",
      "id_file",
      "id_user",
      "datetime",
      "file_name",
      "id_document",
      "action",
      "id_documentation_files_logs"
    ],
    "domain": "Documentation",
    "columnCount": 8
  },
  {
    "name": "documentation_groups",
    "columns": [
      "name",
      "description",
      "is_archived",
      "id_documentation_group"
    ],
    "domain": "Documentation",
    "columnCount": 4
  },
  {
    "name": "documentation_groups_users",
    "columns": [
      "id_documentation_group",
      "id_user",
      "id_documentation_group_user"
    ],
    "domain": "Documentation",
    "columnCount": 3
  },
  {
    "name": "documentation_guides",
    "columns": [
      "title",
      "value",
      "id_client",
      "is_template",
      "is_archived",
      "id_guide"
    ],
    "domain": "Documentation",
    "columnCount": 6
  },
  {
    "name": "documentation_ip_addresses",
    "columns": [
      "ip_address",
      "description",
      "netmask",
      "gateway",
      "id_document",
      "is_archived",
      "id_ip_address"
    ],
    "domain": "Documentation",
    "columnCount": 7
  },
  {
    "name": "documentation_logs",
    "columns": [
      "id_item",
      "item_name",
      "id_client",
      "field",
      "old_value",
      "new_value",
      "id_user",
      "date",
      "item_type",
      "action",
      "id_documentation_log"
    ],
    "domain": "Documentation",
    "columnCount": 11
  },
  {
    "name": "documentation_network_drives",
    "columns": [
      "id_network_drive",
      "network_path",
      "description",
      "id_document",
      "is_archived"
    ],
    "domain": "Documentation",
    "columnCount": 5
  },
  {
    "name": "documentation_notes",
    "columns": [
      "id_document",
      "creationdate",
      "id_user",
      "note",
      "id_client",
      "suivi",
      "suivi_status",
      "suivi_id_ticket",
      "id_note"
    ],
    "domain": "Documentation",
    "columnCount": 9
  },
  {
    "name": "documentation_password_user_unlock_requests",
    "columns": [
      "id",
      "id_user",
      "reason",
      "status",
      "views_at_request",
      "requested_at",
      "reviewed_by",
      "reviewed_at",
      "bonus_views",
      "bonus_expires_at"
    ],
    "domain": "Documentation",
    "columnCount": 10
  },
  {
    "name": "documentation_password_user_view_limit_assignments",
    "columns": [
      "id_user",
      "id_config",
      "max_views",
      "period",
      "alert_max_views",
      "alert_period",
      "alert_email",
      "updated_by",
      "updated_at"
    ],
    "domain": "Documentation",
    "columnCount": 9
  },
  {
    "name": "documentation_password_view_limit_config_rules",
    "columns": [
      "id",
      "id_config",
      "kind",
      "max_views",
      "period",
      "sort_order"
    ],
    "domain": "Documentation",
    "columnCount": 6
  },
  {
    "name": "documentation_password_view_limit_configs",
    "columns": [
      "id",
      "name",
      "alert_emails",
      "is_default",
      "updated_by",
      "created_at",
      "updated_at"
    ],
    "domain": "Documentation",
    "columnCount": 7
  },
  {
    "name": "documentation_passwords",
    "columns": [
      "id_document",
      "locked",
      "active",
      "id_user",
      "creationdate",
      "data",
      "username",
      "description",
      "iv",
      "id_client",
      "last_modified_date",
      "last_modified_id_user",
      "id_parent",
      "id_password"
    ],
    "domain": "Documentation",
    "columnCount": 14
  },
  {
    "name": "documentation_passwords_rights",
    "columns": [
      "id_password",
      "id_documentation_group",
      "id_user",
      "is_group",
      "is_deny",
      "id_documentation_password_right"
    ],
    "domain": "Documentation",
    "columnCount": 6
  },
  {
    "name": "documentation_passwords_views",
    "columns": [
      "id_user",
      "viewtime",
      "id_password",
      "id_view"
    ],
    "domain": "Documentation",
    "columnCount": 4
  },
  {
    "name": "documentation_types",
    "columns": [
      "name",
      "is_archived",
      "id_type"
    ],
    "domain": "Documentation",
    "columnCount": 3
  },
  {
    "name": "documentation_types_fields",
    "columns": [
      "id_type",
      "id_field",
      "fieldOrder",
      "id_typefield"
    ],
    "domain": "Documentation",
    "columnCount": 4
  },
  {
    "name": "duo_client_assignements",
    "columns": [
      "client_id",
      "account_id",
      "status",
      "created_at",
      "updated_at",
      "notes",
      "notes_history",
      "ticket_link",
      "is_primary",
      "assignement_id"
    ],
    "domain": "Legacy / noyau divers",
    "columnCount": 10
  },
  {
    "name": "duo_invoice_details",
    "columns": [
      "duo_invoice_detail_id",
      "duo_invoice_id",
      "account_id",
      "sub_account",
      "parent_account",
      "edition",
      "number_of_user",
      "invoice_date",
      "price_date",
      "unit_cost",
      "cost",
      "created_at",
      "updated_at"
    ],
    "domain": "Legacy / noyau divers",
    "columnCount": 13
  },
  {
    "name": "duo_invoices",
    "columns": [
      "duo_invoice_id",
      "duo_invoice_serie",
      "total_cost",
      "total_user",
      "invoice_date",
      "original_file_name",
      "imported_at",
      "created_at",
      "updated_at"
    ],
    "domain": "Legacy / noyau divers",
    "columnCount": 9
  },
  {
    "name": "email_ai_prompts",
    "columns": [
      "id_prompt",
      "prompt_name",
      "prompt_description",
      "is_active",
      "prompt_static",
      "prompt_modifiable",
      "app_pages",
      "created_by",
      "created_at",
      "updated_by",
      "updated_at"
    ],
    "domain": "Legacy / noyau divers",
    "columnCount": 11
  },
  {
    "name": "emails_logs",
    "columns": [
      "datetime",
      "from",
      "to",
      "cc",
      "subject",
      "module",
      "template",
      "data",
      "response_code",
      "response",
      "state",
      "id_email_log"
    ],
    "domain": "Legacy / noyau divers",
    "columnCount": 12
  },
  {
    "name": "erp_alerts",
    "columns": [
      "trigger_type",
      "action_type",
      "id_user_creator",
      "title",
      "creation_date",
      "inactive",
      "trigger_intervention_type_id_customer",
      "trigger_intervention_type_id_intervention_type",
      "trigger_intervention_type_times",
      "trigger_intervention_type_frequency",
      "trigger_intervention_type_period",
      "trigger_project_date_id_customer",
      "trigger_project_date_id_task",
      "trigger_project_date_id_project",
      "trigger_project_date_datetime",
      "action_sleep",
      "action_email_self",
      "action_email_customer_primes",
      "action_email_others",
      "action_email_message",
      "action_email_send_frequency",
      "action_email_send_period",
      "action_email_send_last",
      "id_alert"
    ],
    "domain": "ERP noyau",
    "columnCount": 24
  },
  {
    "name": "erp_chats",
    "columns": [
      "client_name",
      "client_email",
      "client_business",
      "tech_id_user",
      "id_ticket",
      "created_at",
      "id_chat"
    ],
    "domain": "ERP noyau",
    "columnCount": 7
  },
  {
    "name": "erp_chats_messages",
    "columns": [
      "id_chat",
      "message",
      "color",
      "bz_staff",
      "tech_id_user",
      "user_name",
      "sent_at",
      "id_chat_message"
    ],
    "domain": "ERP noyau",
    "columnCount": 8
  },
  {
    "name": "erp_com_comptes",
    "columns": [
      "status",
      "id_user",
      "creation_date",
      "approbation_date",
      "refusal_date",
      "refusal_note",
      "id_invoice",
      "approval_date",
      "payment_date",
      "id_compte"
    ],
    "domain": "ERP noyau",
    "columnCount": 10
  },
  {
    "name": "erp_com_comptes_depenses",
    "columns": [
      "date",
      "id_type",
      "description",
      "km",
      "brut",
      "tps",
      "tvq",
      "net",
      "periode_tech_id",
      "refacturation",
      "date_refacturation",
      "id_intervention",
      "id_compte",
      "id_client",
      "file_path",
      "id_succursale",
      "tps_id_tx",
      "tvq_id_tx",
      "tx_id_book",
      "mime_type",
      "id_comptes_depenses"
    ],
    "domain": "ERP noyau",
    "columnCount": 21
  },
  {
    "name": "erp_com_comptes_files",
    "columns": [
      "id_comptes_depenses",
      "file_path",
      "file_name",
      "id_file"
    ],
    "domain": "ERP noyau",
    "columnCount": 4
  },
  {
    "name": "erp_com_km_exceptions",
    "columns": [
      "frais",
      "description",
      "id_user",
      "id_exception"
    ],
    "domain": "ERP noyau",
    "columnCount": 4
  },
  {
    "name": "erp_com_params",
    "columns": [
      "name",
      "valeur",
      "param_id"
    ],
    "domain": "ERP noyau",
    "columnCount": 3
  },
  {
    "name": "erp_com_types_depenses",
    "columns": [
      "nom",
      "tps",
      "tvq",
      "read_only",
      "id_category",
      "id_type"
    ],
    "domain": "ERP noyau",
    "columnCount": 6
  },
  {
    "name": "expense_account_credit_card",
    "columns": [
      "name",
      "last_4_number",
      "created_at",
      "updated_at",
      "archived",
      "id_expense_account_credit_card"
    ],
    "domain": "Dépenses",
    "columnCount": 6
  },
  {
    "name": "expense_account_credit_card_users",
    "columns": [
      "id_expense_account_credit_card",
      "id_user",
      "created_at",
      "updated_at",
      "id_expense_account_credit_card_users"
    ],
    "domain": "Dépenses",
    "columnCount": 5
  },
  {
    "name": "expense_accounts",
    "columns": [
      "name",
      "description",
      "date",
      "status",
      "archived",
      "created_date",
      "updated_date",
      "id365",
      "is_credit",
      "id_expense_account"
    ],
    "domain": "Dépenses",
    "columnCount": 10
  },
  {
    "name": "expense_accounts_2",
    "columns": [
      "name",
      "description",
      "status",
      "archived",
      "created_date",
      "updated_date",
      "id_expense_account"
    ],
    "domain": "Dépenses",
    "columnCount": 7
  },
  {
    "name": "expense_accounts_2_banking_informations",
    "columns": [
      "id_expense_account_banking_information",
      "institution_number",
      "cash_register_number",
      "folio_number",
      "id_user"
    ],
    "domain": "Dépenses",
    "columnCount": 5
  },
  {
    "name": "expense_accounts_2_items",
    "columns": [
      "id_expense_account_item",
      "supplier",
      "type",
      "description",
      "id_client",
      "id_client_succusale",
      "is_billable",
      "is_waiting_additional_info",
      "approved_by_admin",
      "approved_by_director",
      "id_user",
      "id_department",
      "id_expense_account",
      "user_expense_date",
      "bz_expense_date",
      "paid_date",
      "invoice_number",
      "created_date",
      "update_date"
    ],
    "domain": "Dépenses",
    "columnCount": 19
  },
  {
    "name": "expense_accounts_2_items_accounts",
    "columns": [
      "id_expense_account_item_account",
      "total_ti",
      "total_ht",
      "tps",
      "tvq",
      "km",
      "id_category",
      "id_expense_account_item",
      "id_product"
    ],
    "domain": "Dépenses",
    "columnCount": 9
  },
  {
    "name": "expense_accounts_2_items_files",
    "columns": [
      "id_expense_account_item_file",
      "id_file",
      "id_expense_account_item"
    ],
    "domain": "Dépenses",
    "columnCount": 3
  },
  {
    "name": "expense_accounts_2_taxe_categories",
    "columns": [
      "id_expense_account_taxe_category",
      "name",
      "tps",
      "tvq"
    ],
    "domain": "Dépenses",
    "columnCount": 4
  },
  {
    "name": "expense_accounts_admin",
    "columns": [
      "name",
      "amout",
      "id_expense_accounts_admin"
    ],
    "domain": "Dépenses",
    "columnCount": 3
  },
  {
    "name": "expense_accounts_banking_information",
    "columns": [
      "id_department",
      "institution_number",
      "cash_register_number",
      "folio_number",
      "id_user",
      "id_expense_accounts_banking_information"
    ],
    "domain": "Dépenses",
    "columnCount": 6
  },
  {
    "name": "expense_accounts_credit_cards",
    "columns": [
      "id_credit_card",
      "label",
      "last_four",
      "card_holder_name",
      "active",
      "expiration_date",
      "id_credit_card_type",
      "created_at",
      "updated_at",
      "company",
      "payment_excess"
    ],
    "domain": "Dépenses",
    "columnCount": 11
  },
  {
    "name": "expense_accounts_credit_cards_books",
    "columns": [
      "id_credit_card_book",
      "id_credit_card",
      "id_book_telecom",
      "id_book_bz",
      "id_product_bz",
      "id_product_telecom",
      "id_product_ko"
    ],
    "domain": "Dépenses",
    "columnCount": 7
  },
  {
    "name": "expense_accounts_credit_cards_expenses",
    "columns": [
      "id_credit_card_expense",
      "id_user",
      "id_credit_card",
      "id_purchase",
      "id_invoice_accounting",
      "id_contract_invoice",
      "supplier_name",
      "description",
      "amount_excl_taxes",
      "tps_amount",
      "tvq_amount",
      "total_amount",
      "user_expense_date",
      "created_at",
      "updated_at",
      "purchase_origin"
    ],
    "domain": "Dépenses",
    "columnCount": 16
  },
  {
    "name": "expense_accounts_credit_cards_expenses_books",
    "columns": [
      "id_credit_card_expense_book",
      "id_credit_card_expense",
      "id_book_telecom",
      "id_book_bz",
      "before_tax_total",
      "tps_amount",
      "tvq_amount",
      "total_amount",
      "id_product_bz",
      "id_product_telecom",
      "id_product_ko"
    ],
    "domain": "Dépenses",
    "columnCount": 11
  },
  {
    "name": "expense_accounts_credit_cards_expenses_files",
    "columns": [
      "id_credit_card_expense_file",
      "id_credit_card_expense",
      "id_file"
    ],
    "domain": "Dépenses",
    "columnCount": 3
  },
  {
    "name": "expense_accounts_credit_cards_rapports",
    "columns": [
      "id_credit_card_rapport",
      "id_user",
      "id_credit_card",
      "date_start",
      "date_stop",
      "total_amount",
      "created_at",
      "updated_at",
      "is_closed"
    ],
    "domain": "Dépenses",
    "columnCount": 9
  },
  {
    "name": "expense_accounts_credit_cards_rapports_expenses",
    "columns": [
      "id_rapport_expense",
      "id_credit_card_rapport",
      "id_credit_card_expense"
    ],
    "domain": "Dépenses",
    "columnCount": 3
  },
  {
    "name": "expense_accounts_credit_cards_rapports_files",
    "columns": [
      "id_credit_card_rapport",
      "id_file"
    ],
    "domain": "Dépenses",
    "columnCount": 2
  },
  {
    "name": "expense_accounts_credit_cards_types",
    "columns": [
      "id_credit_card_type",
      "name"
    ],
    "domain": "Dépenses",
    "columnCount": 2
  },
  {
    "name": "expense_accounts_credit_cards_users",
    "columns": [
      "id_credit_card_user",
      "id_credit_card",
      "id_user"
    ],
    "domain": "Dépenses",
    "columnCount": 3
  },
  {
    "name": "expense_accounts_departments",
    "columns": [
      "id_expense_account",
      "id_department",
      "id_expense_accounts_department"
    ],
    "domain": "Dépenses",
    "columnCount": 3
  },
  {
    "name": "expense_accounts_items",
    "columns": [
      "supplier",
      "type",
      "description",
      "date",
      "succursale",
      "price_excluding_tax",
      "tps",
      "tvq",
      "total",
      "id_client",
      "km",
      "is_billable",
      "id_expense_account",
      "director_approval",
      "admin_approval",
      "created_date",
      "updated_date",
      "id365",
      "invoice_number",
      "id_expense_account_credit_card",
      "invoice_date",
      "admin_approval_date",
      "id_expense_account_item"
    ],
    "domain": "Dépenses",
    "columnCount": 23
  },
  {
    "name": "expense_accounts_items_accounts",
    "columns": [
      "id_expense_account_item",
      "id_account",
      "amount",
      "TPS",
      "TVQ",
      "price_excluding_tax",
      "id_expense_accounts_items_accounts"
    ],
    "domain": "Dépenses",
    "columnCount": 7
  },
  {
    "name": "expense_accounts_items_files",
    "columns": [
      "id_expense_account_item",
      "id_file",
      "id"
    ],
    "domain": "Dépenses",
    "columnCount": 3
  },
  {
    "name": "expense_accounts_kilometers",
    "columns": [
      "id_expense_account_kilometer",
      "value"
    ],
    "domain": "Dépenses",
    "columnCount": 2
  },
  {
    "name": "expense_accounts_taxes_account",
    "columns": [
      "id_account",
      "TPS",
      "TVQ",
      "id_expense_accounts_taxes_account"
    ],
    "domain": "Dépenses",
    "columnCount": 4
  },
  {
    "name": "files",
    "columns": [
      "path",
      "name",
      "size",
      "data",
      "preview",
      "filetype",
      "is_archived",
      "id_file"
    ],
    "domain": "Legacy / noyau divers",
    "columnCount": 8
  },
  {
    "name": "getSumOfQtyAssigned",
    "columns": [
      "id_contract_subscription",
      "id_contract",
      "id_product",
      "id_subscription_ingram",
      "id_bundle_line",
      "qty_ordered",
      "qty_assigned",
      "total_line",
      "total_assigned"
    ],
    "domain": "Legacy / noyau divers",
    "columnCount": 9
  },
  {
    "name": "get_clients_succursale_with_basics",
    "columns": [
      "id_succursale",
      "id_client",
      "nom",
      "adresse",
      "ville",
      "province",
      "codepostal",
      "telephone",
      "fax",
      "km_to_bz"
    ],
    "domain": "Exports / vues",
    "columnCount": 10
  },
  {
    "name": "get_clients_with_basics",
    "columns": [
      "id_client",
      "nom",
      "alias",
      "cloud_sa",
      "cloud_tel",
      "cloud_backup",
      "cloud_tech",
      "cloud_cyber",
      "cloud_bh",
      "cloud_prog",
      "status",
      "is_cyber",
      "taux",
      "taux_prog",
      "taux_tel",
      "taux_cyber",
      "taux_sa",
      "taux_bh"
    ],
    "domain": "Exports / vues",
    "columnCount": 18
  },
  {
    "name": "get_contracts_lines_with_subs_qty",
    "columns": [
      "id_contract_detail",
      "id_contract",
      "description",
      "id_product",
      "price",
      "quantity",
      "id_category",
      "is_bundle",
      "id_bundle_from",
      "index",
      "bundle_multiple_quantity",
      "part_number",
      "id_bundle_contract_line",
      "is_bundle_O365",
      "is_billable_month",
      "is_primary"
    ],
    "domain": "Exports / vues",
    "columnCount": 16
  },
  {
    "name": "get_contracts_primary_lines_with_qty_365",
    "columns": [
      "id_contract_detail",
      "id_contract",
      "description",
      "id_product",
      "price",
      "quantity",
      "id_category",
      "is_bundle",
      "id_bundle_from",
      "index",
      "bundle_multiple_quantity",
      "part_number",
      "id_bundle_contract_line",
      "is_bundle_O365",
      "is_billable_month",
      "is_primary"
    ],
    "domain": "Exports / vues",
    "columnCount": 16
  },
  {
    "name": "get_contracts_slave_lines_with_qty_365",
    "columns": [
      "id_contract_detail",
      "id_contract",
      "description",
      "id_product",
      "price",
      "quantity",
      "id_category",
      "is_bundle",
      "id_bundle_from",
      "index",
      "bundle_multiple_quantity",
      "part_number",
      "id_bundle_contract_line",
      "is_bundle_O365",
      "is_billable_month",
      "is_primary"
    ],
    "domain": "Exports / vues",
    "columnCount": 16
  },
  {
    "name": "get_count_assigned_sub_per_subscription",
    "columns": [
      "qty_assigned",
      "id_contract_subscription"
    ],
    "domain": "Exports / vues",
    "columnCount": 2
  },
  {
    "name": "get_subs_qty_per_contract_line",
    "columns": [
      "id_contract_subscription",
      "id_contract",
      "id_product",
      "id_subscription_ingram",
      "id_bundle_line",
      "qty_ordered",
      "qty_assigned"
    ],
    "domain": "Exports / vues",
    "columnCount": 7
  },
  {
    "name": "get_total_of_365_lines_for_non_365_products",
    "columns": [
      "id_contract",
      "id_contract_detail",
      "is_billable_month",
      "qty_assigned",
      "qty_ordered",
      "id_bundle_contract_line"
    ],
    "domain": "Exports / vues",
    "columnCount": 6
  },
  {
    "name": "get_users_with_basics",
    "columns": [
      "id_user",
      "surname",
      "name",
      "bz_staff",
      "active",
      "id_client",
      "categoriescontact"
    ],
    "domain": "Exports / vues",
    "columnCount": 7
  },
  {
    "name": "google_maps_request_queue",
    "columns": [
      "request",
      "id_client",
      "id_succursale",
      "id_google_maps_request_queue"
    ],
    "domain": "Legacy / noyau divers",
    "columnCount": 4
  },
  {
    "name": "groups",
    "columns": [
      "name",
      "display_name",
      "tic_assign_to",
      "id_group"
    ],
    "domain": "Legacy / noyau divers",
    "columnCount": 4
  },
  {
    "name": "guides_clients",
    "columns": [
      "id_guide",
      "id_client",
      "id_clientguide"
    ],
    "domain": "Guides",
    "columnCount": 3
  },
  {
    "name": "guides_guides",
    "columns": [
      "titre",
      "html",
      "id_guide"
    ],
    "domain": "Guides",
    "columnCount": 3
  },
  {
    "name": "guides_vardata",
    "columns": [
      "id_variable",
      "id_client",
      "data",
      "id_vardata"
    ],
    "domain": "Guides",
    "columnCount": 4
  },
  {
    "name": "guides_variables",
    "columns": [
      "variable",
      "default",
      "id_variable"
    ],
    "domain": "Guides",
    "columnCount": 3
  },
  {
    "name": "id_users_types_logs",
    "columns": [
      "id_user",
      "id_type",
      "log_type",
      "datetime",
      "id_user_type_log"
    ],
    "domain": "Legacy / noyau divers",
    "columnCount": 5
  },
  {
    "name": "ingram_detail",
    "columns": [
      "reseller_plan_id",
      "reseller_plan_aps_id",
      "reseller_plan_period_id",
      "reseller_resource_id",
      "reseller_resource_aps_id",
      "reseller_detail_id",
      "reseller_detail_sku",
      "reseller_detail_type",
      "reseller_detail_description",
      "reseller_detail_start_date",
      "reseller_detail_end_date",
      "reseller_detail_unit_price",
      "reseller_detail_unit_price_period_type",
      "reseller_detail_qty",
      "reseller_detail_qty_uom",
      "reseller_detail_duration",
      "reseller_detail_discount_amount",
      "reseller_detail_discount_rate",
      "reseller_detail_excl_tax_total",
      "reseller_detail_tax_total",
      "reseller_detail_tax_rate",
      "reseller_detail_total",
      "reseller_order_id",
      "reseller_order_total",
      "reseller_invoice_id",
      "reseller_invoice_number",
      "reseller_invoice_date",
      "reseller_invoice_due_date",
      "reseller_invoice_net_total",
      "reseller_invoice_tax_total",
      "reseller_invoice_total",
      "reseller_account_attributes",
      "reseller_order_attributes",
      "customer_account_id",
      "customer_account_bss_aps_id",
      "customer_name",
      "customer_account_statement_day",
      "customer_plan_id",
      "customer_plan_aps_id",
      "customer_plan_period_id",
      "customer_resource_id",
      "customer_resource_aps_id",
      "customer_detail_id",
      "customer_detail_sku",
      "customer_detail_type",
      "customer_detail_description",
      "customer_detail_start_date",
      "customer_detail_end_date",
      "customer_detail_unit_price",
      "customer_detail_unit_price_period_type",
      "customer_detail_qty",
      "customer_detail_qty_uom",
      "customer_detail_duration",
      "customer_detail_promotion_id",
      "customer_detail_promotion_value",
      "customer_detail_tax_category",
      "customer_detail_excl_tax_total",
      "customer_detail_tax_total",
      "customer_detail_total",
      "customer_order_id",
      "customer_order_aps_id",
      "customer_order_number",
      "customer_order_type",
      "customer_order_date",
      "customer_order_created_by",
      "customer_order_net_total",
      "customer_order_tax_total",
      "customer_order_total",
      "customer_invoice_id",
      "customer_invoice_number",
      "customer_invoice_date",
      "customer_invoice_due_date",
      "customer_invoice_net_total",
      "customer_invoice_tax_total",
      "customer_invoice_total",
      "customer_account_attributes",
      "customer_order_attributes",
      "subscription_id",
      "subscription_name",
      "subscription_start_date",
      "subscription_expiration_date",
      "subscription_billing_period",
      "subscription_billing_period_type",
      "subscription_period",
      "subscription_period_type",
      "subscription_last_billing_date",
      "subscription_next_billing_date",
      "subscription_status",
      "subscription_attributes",
      "reseller_class",
      "customer_class",
      "reseller_invoice_week_number",
      "customer_invoice_week_number",
      "reseller_detail_provisioned_date",
      "vendor_subscription_number",
      "reseller_resource_vendor_id",
      "reseller_resource_mpn",
      "reseller_plan_category_id",
      "reseller_detail_net_total",
      "customer_address1",
      "customer_plan_category_id",
      "customer_detail_net_total",
      "reseller_resource_product_id",
      "reseller_discount_breakdown",
      "reseller_detail_sales_unit_price",
      "reseller_detail_sales_discount_amount",
      "reseller_detail_sales_excl_tax_total",
      "reseller_detail_sales_tax_total",
      "reseller_detail_sales_total",
      "reseller_detail_sales_net_total",
      "customer_detail_sales_unit_price",
      "customer_detail_sales_promotion_value",
      "customer_detail_sales_excl_tax_total",
      "customer_detail_sales_tax_total",
      "customer_detail_sales_total",
      "customer_detail_sales_net_total",
      "created_at",
      "updated_at",
      "erp_invoice_number",
      "manual_po_number",
      "id"
    ],
    "domain": "Ingram",
    "columnCount": 121
  },
  {
    "name": "ingram_import_files",
    "columns": [
      "file_name",
      "imported_at",
      "user_id",
      "file_path",
      "invoice_number",
      "id"
    ],
    "domain": "Ingram",
    "columnCount": 6
  },
  {
    "name": "ingram_product_group_assignments",
    "columns": [
      "group_id",
      "reseller_resource_mpn",
      "subscription_billing_period_type",
      "created_at",
      "updated_at",
      "id"
    ],
    "domain": "Ingram",
    "columnCount": 6
  },
  {
    "name": "ingram_product_groups",
    "columns": [
      "name",
      "description",
      "created_at",
      "updated_at",
      "id"
    ],
    "domain": "Ingram",
    "columnCount": 5
  },
  {
    "name": "inv_not_balenced",
    "columns": [
      "total_lines",
      "total_inv",
      "invoice_number",
      "creation_date",
      "diff"
    ],
    "domain": "Legacy / noyau divers",
    "columnCount": 5
  },
  {
    "name": "inventory",
    "columns": [
      "id_client",
      "id_user",
      "id_succursale",
      "id_type",
      "brand",
      "model",
      "serialnumber",
      "warranty_purchase",
      "warranty_duration",
      "warranty_end",
      "warranty_age",
      "warranty_expired",
      "warranty_defect",
      "language",
      "monitorhash",
      "uptime",
      "freemem",
      "totalmem",
      "cpuinfo",
      "diskinfo",
      "gpuinfo",
      "name",
      "last_update",
      "last_keepalive",
      "customfields",
      "comments",
      "os_version",
      "platform",
      "os_name",
      "hostname",
      "status",
      "departement",
      "express_service_tag",
      "monitor1",
      "monitor2",
      "monitor3",
      "monitor4",
      "distributor",
      "last_logged_user",
      "id_inventory"
    ],
    "domain": "Inventaire",
    "columnCount": 40
  },
  {
    "name": "inventory_software",
    "columns": [
      "id_inventory",
      "software",
      "version",
      "language",
      "license",
      "id_inventory_software"
    ],
    "domain": "Inventaire",
    "columnCount": 6
  },
  {
    "name": "inventory_templates",
    "columns": [
      "id_client",
      "template",
      "id_type",
      "id_template"
    ],
    "domain": "Inventaire",
    "columnCount": 4
  },
  {
    "name": "inventory_types",
    "columns": [
      "type",
      "id_type"
    ],
    "domain": "Inventaire",
    "columnCount": 2
  },
  {
    "name": "invoice_with_same_invoice_number",
    "columns": [
      "id_invoice",
      "id_contract",
      "id_client",
      "invoice_number",
      "title",
      "invoice_date",
      "status",
      "type",
      "description",
      "client_name",
      "client_address",
      "client_city",
      "client_state",
      "client_zip",
      "client_phone",
      "id_succursale",
      "succursale_name",
      "succursale_address",
      "succursale_city",
      "succursale_state",
      "succursale_zip",
      "succursale_phone",
      "subtotal",
      "tps",
      "tvq",
      "total",
      "is_taxable",
      "paid",
      "balance",
      "is_send",
      "show_annexe_bundles",
      "show_annexe_licences",
      "show_annexe_users",
      "created_date",
      "updated_date"
    ],
    "domain": "Legacy / noyau divers",
    "columnCount": 35
  },
  {
    "name": "ip_list",
    "columns": [
      "description",
      "debut",
      "fin",
      "notation_cidr",
      "masque",
      "wildcard",
      "ip_id"
    ],
    "domain": "Legacy / noyau divers",
    "columnCount": 7
  },
  {
    "name": "jeffs",
    "columns": [
      "id_user",
      "period",
      "date",
      "id_client",
      "id_jeffs"
    ],
    "domain": "Legacy / noyau divers",
    "columnCount": 5
  },
  {
    "name": "ko_accounting_books",
    "columns": [
      "id_book",
      "name",
      "account_number",
      "description",
      "active",
      "is_km",
      "id_type",
      "id_account_tactice",
      "created_at",
      "updated_at"
    ],
    "domain": "Legacy / noyau divers",
    "columnCount": 10
  },
  {
    "name": "ko_accounting_products",
    "columns": [
      "id_product",
      "description",
      "part_number",
      "sku",
      "price",
      "contract_price",
      "id_ecofees",
      "id_account",
      "serial_number_required",
      "quantity_required",
      "quantity_pending",
      "quantity_received",
      "is_inventoried",
      "active",
      "definition",
      "is_km",
      "is_expense_account",
      "id_expense_account_taxe_category",
      "is_expense_config_visible",
      "is_expense_credit_card_visible"
    ],
    "domain": "Legacy / noyau divers",
    "columnCount": 20
  },
  {
    "name": "ko_department_accounting_categories",
    "columns": [
      "id_department_accounting_category",
      "id_department",
      "id_book"
    ],
    "domain": "Legacy / noyau divers",
    "columnCount": 3
  },
  {
    "name": "lic_archive_contracts",
    "columns": [
      "id_client",
      "id_contract",
      "title",
      "date_start",
      "date_end",
      "date_billed",
      "bill_cycle",
      "internal_notes",
      "users_sold",
      "frozen_price",
      "email_from",
      "date_archived",
      "id_archive_contract"
    ],
    "domain": "Licences & contrats",
    "columnCount": 13
  },
  {
    "name": "lic_archive_contracts_details",
    "columns": [
      "id_archive_contract",
      "description",
      "quantity",
      "id_product",
      "price",
      "id_archive_contract_detail"
    ],
    "domain": "Licences & contrats",
    "columnCount": 6
  },
  {
    "name": "lic_archive_contracts_files",
    "columns": [
      "id_archive_contract",
      "filetype",
      "filename",
      "filepath",
      "id_archive_contract_file"
    ],
    "domain": "Licences & contrats",
    "columnCount": 5
  },
  {
    "name": "lic_archive_contracts_tos",
    "columns": [
      "id_archive_contract",
      "to",
      "id_archive_contract_to"
    ],
    "domain": "Licences & contrats",
    "columnCount": 3
  },
  {
    "name": "lic_archive_licenses",
    "columns": [
      "id_client",
      "id_license",
      "notes",
      "users_count",
      "date_archived",
      "lic_archive_licensescol",
      "id_archive_license"
    ],
    "domain": "Licences & contrats",
    "columnCount": 7
  },
  {
    "name": "lic_archive_licenses_users_types",
    "columns": [
      "id_archive_license",
      "title",
      "description",
      "notes",
      "product_quantity",
      "id_archive_license_user_type"
    ],
    "domain": "Licences & contrats",
    "columnCount": 6
  },
  {
    "name": "lic_archive_users_types_details",
    "columns": [
      "id_archive_license_user_type",
      "id_license_product",
      "product_price",
      "notes",
      "id_archive_user_type_detail"
    ],
    "domain": "Licences & contrats",
    "columnCount": 5
  },
  {
    "name": "lic_contracts",
    "columns": [
      "id_client",
      "title",
      "date_start",
      "date_end",
      "date_billed",
      "bill_cycle",
      "internal_notes",
      "users_sold",
      "frozen_price",
      "email_from",
      "last_archived_on",
      "verified_state",
      "type",
      "sub_total_monthly",
      "sub_total",
      "total",
      "tps",
      "tvq",
      "terminates_on",
      "terminated_on",
      "ended",
      "non_taxable",
      "annual_increase",
      "id_succursale",
      "succursale_name",
      "succursale_address",
      "succursale_city",
      "succursale_state",
      "succursale_postal_code",
      "succursale_phone",
      "is_period_in_advance",
      "billing_custom_period",
      "is_sherweb",
      "date_renewal",
      "id_contract"
    ],
    "domain": "Licences & contrats",
    "columnCount": 35
  },
  {
    "name": "lic_contracts_alerts",
    "columns": [
      "id_contract",
      "created_date",
      "created_id_user",
      "alert_date",
      "message",
      "done_date",
      "done_id_user",
      "id_contract_alert"
    ],
    "domain": "Licences & contrats",
    "columnCount": 8
  },
  {
    "name": "lic_contracts_alerts_tags",
    "columns": [
      "id_tag",
      "id_alert",
      "id_tag_alert"
    ],
    "domain": "Licences & contrats",
    "columnCount": 3
  },
  {
    "name": "lic_contracts_details",
    "columns": [
      "id_contract",
      "description",
      "quantity",
      "id_product",
      "price",
      "id_category",
      "is_bundle",
      "id_bundle_from",
      "bundle_discount",
      "bundle_discount_type",
      "index",
      "bundle_multiple_quantity",
      "part_number",
      "id_bundle_contract_line",
      "is_bundle_O365",
      "id_product_slave",
      "product_slave_price",
      "is_billable_month",
      "part_number_slave",
      "id_contract_detail"
    ],
    "domain": "Licences & contrats",
    "columnCount": 20
  },
  {
    "name": "lic_contracts_details_tags",
    "columns": [
      "id_contract_detail",
      "id_tag",
      "id_contract_detail_tag"
    ],
    "domain": "Licences & contrats",
    "columnCount": 3
  },
  {
    "name": "lic_contracts_files",
    "columns": [
      "id_contract",
      "filetype",
      "filename",
      "filepath",
      "id_contract_file"
    ],
    "domain": "Licences & contrats",
    "columnCount": 5
  },
  {
    "name": "lic_contracts_ingram_subscriptions",
    "columns": [
      "id_contract",
      "id_product",
      "id_subscription_ingram",
      "id_bundle_line",
      "qty_ordered",
      "id_contract_subscription"
    ],
    "domain": "Licences & contrats",
    "columnCount": 6
  },
  {
    "name": "lic_contracts_logs",
    "columns": [
      "id_contract",
      "id_contract_detail",
      "id_user",
      "datetime",
      "field",
      "field_label",
      "value_from",
      "value_to",
      "id_contract_log"
    ],
    "domain": "Licences & contrats",
    "columnCount": 9
  },
  {
    "name": "lic_contracts_subscriptions_users_assignments",
    "columns": [
      "id_contract_subscription",
      "id_user",
      "id_assignment"
    ],
    "domain": "Licences & contrats",
    "columnCount": 3
  },
  {
    "name": "lic_contracts_tags_alert_list",
    "columns": [
      "name",
      "active",
      "id_tag"
    ],
    "domain": "Licences & contrats",
    "columnCount": 3
  },
  {
    "name": "lic_contracts_tos",
    "columns": [
      "id_contract",
      "to",
      "id_contract_to"
    ],
    "domain": "Licences & contrats",
    "columnCount": 3
  },
  {
    "name": "lic_contracts_verified_log",
    "columns": [
      "id_contract",
      "id_user",
      "date",
      "user_log_contract_status",
      "id_contracts_verified_log"
    ],
    "domain": "Licences & contrats",
    "columnCount": 5
  },
  {
    "name": "lic_flags",
    "columns": [
      "id_user_type_detail",
      "created_date",
      "acknowledged_by",
      "acknowledged_when",
      "id_flag"
    ],
    "domain": "Licences & contrats",
    "columnCount": 5
  },
  {
    "name": "lic_licenses",
    "columns": [
      "id_client",
      "notes",
      "users_count",
      "id_license"
    ],
    "domain": "Licences & contrats",
    "columnCount": 4
  },
  {
    "name": "lic_licenses_products",
    "columns": [
      "part_number",
      "name",
      "price",
      "notes",
      "active",
      "id_license_product"
    ],
    "domain": "Licences & contrats",
    "columnCount": 6
  },
  {
    "name": "lic_licenses_transactions",
    "columns": [
      "id_client",
      "id_contract",
      "creation_date",
      "status",
      "creation_id_user",
      "status_id_user",
      "status_date",
      "id_contract_detail",
      "id_transaction"
    ],
    "domain": "Licences & contrats",
    "columnCount": 9
  },
  {
    "name": "lic_licenses_transactions_details",
    "columns": [
      "id_transaction",
      "id_product",
      "qty",
      "id_transaction_detail"
    ],
    "domain": "Licences & contrats",
    "columnCount": 4
  },
  {
    "name": "lic_licenses_users_types",
    "columns": [
      "id_license",
      "title",
      "description",
      "notes",
      "id_license_user_type"
    ],
    "domain": "Licences & contrats",
    "columnCount": 5
  },
  {
    "name": "lic_licenses_users_types_users",
    "columns": [
      "id_license_user_type",
      "id_user",
      "license_notes",
      "id_license_user_type_user"
    ],
    "domain": "Licences & contrats",
    "columnCount": 4
  },
  {
    "name": "lic_users_types_details",
    "columns": [
      "id_license_user_type",
      "id_license_product",
      "notes",
      "id_user_type_detail"
    ],
    "domain": "Licences & contrats",
    "columnCount": 4
  },
  {
    "name": "license_products",
    "columns": [
      "name",
      "price_open",
      "price_spla",
      "id_product"
    ],
    "domain": "Legacy / noyau divers",
    "columnCount": 4
  },
  {
    "name": "license_products_versions",
    "columns": [
      "id_product",
      "version",
      "id_version"
    ],
    "domain": "Legacy / noyau divers",
    "columnCount": 3
  },
  {
    "name": "liv_items",
    "columns": [
      "id_livraison",
      "quantity",
      "description",
      "id_item"
    ],
    "domain": "Legacy / noyau divers",
    "columnCount": 4
  },
  {
    "name": "liv_livraisons",
    "columns": [
      "id_client",
      "id_succursale",
      "contact",
      "address",
      "city",
      "province",
      "postal_code",
      "shipped",
      "signature_filename",
      "signature_filetype",
      "created_by",
      "shipped_by",
      "created_date",
      "shipped_date",
      "signature_date",
      "notes",
      "id_livraison"
    ],
    "domain": "Legacy / noyau divers",
    "columnCount": 17
  },
  {
    "name": "mysql_users_inventory",
    "columns": [
      "User",
      "Host"
    ],
    "domain": "Legacy / noyau divers",
    "columnCount": 2
  },
  {
    "name": "new_table",
    "columns": [
      "status",
      "config",
      "destination",
      "wav_filepath",
      "sendtime",
      "id_email"
    ],
    "domain": "Legacy / noyau divers",
    "columnCount": 6
  },
  {
    "name": "news",
    "columns": [
      "posted_by",
      "datetime",
      "title",
      "content",
      "content_stripped",
      "category",
      "is_important",
      "id_news"
    ],
    "domain": "Legacy / noyau divers",
    "columnCount": 8
  },
  {
    "name": "news_comments",
    "columns": [
      "id_user",
      "id_news",
      "content",
      "datetime",
      "id_parent_comment",
      "id_news_comment"
    ],
    "domain": "Legacy / noyau divers",
    "columnCount": 6
  },
  {
    "name": "news_likes",
    "columns": [
      "id_news",
      "id_user",
      "datetime",
      "id_news_like"
    ],
    "domain": "Legacy / noyau divers",
    "columnCount": 4
  },
  {
    "name": "news_users_seen",
    "columns": [
      "id_news",
      "id_user",
      "datetime",
      "id_news_seen"
    ],
    "domain": "Legacy / noyau divers",
    "columnCount": 4
  },
  {
    "name": "nist_categories",
    "columns": [
      "identifier",
      "name",
      "description",
      "id_nist_function",
      "id_nist_category"
    ],
    "domain": "Legacy / noyau divers",
    "columnCount": 5
  },
  {
    "name": "nist_functions",
    "columns": [
      "identifier",
      "name",
      "description",
      "id_nist_function"
    ],
    "domain": "Legacy / noyau divers",
    "columnCount": 4
  },
  {
    "name": "nist_subcategories",
    "columns": [
      "identifier",
      "description",
      "id_nist_category",
      "id_nist_subcategory"
    ],
    "domain": "Legacy / noyau divers",
    "columnCount": 4
  },
  {
    "name": "notifications",
    "columns": [
      "id_user",
      "date_creation",
      "notification",
      "title",
      "id_ticket",
      "status",
      "id_user_source",
      "id_notification"
    ],
    "domain": "Legacy / noyau divers",
    "columnCount": 8
  },
  {
    "name": "outils_ring_groups",
    "columns": [
      "ring_group_name",
      "extension",
      "id"
    ],
    "domain": "Legacy / noyau divers",
    "columnCount": 3
  },
  {
    "name": "params",
    "columns": [
      "chat_auto_reply",
      "chat_auto_reply_message",
      "tickets_auto_reply",
      "tickets_auto_reply_message_fr",
      "tickets_auto_reply_message_en",
      "price_vm",
      "price_vcpu",
      "price_ram",
      "price_hdd",
      "erp_version",
      "updating_erp",
      "next_update_date",
      "tickets_by_mail_ai_barometer",
      "id_param"
    ],
    "domain": "Legacy / noyau divers",
    "columnCount": 14
  },
  {
    "name": "partner_service_types",
    "columns": [
      "id",
      "name",
      "is_archived"
    ],
    "domain": "Legacy / noyau divers",
    "columnCount": 3
  },
  {
    "name": "partner_services",
    "columns": [
      "id",
      "id_partner",
      "name",
      "id_service_type",
      "is_archived",
      "remuneration"
    ],
    "domain": "Legacy / noyau divers",
    "columnCount": 6
  },
  {
    "name": "partners",
    "columns": [
      "id",
      "name",
      "type",
      "is_archived",
      "contact_first_name",
      "contact_last_name",
      "contact_email",
      "contact_phone",
      "contact_address"
    ],
    "domain": "Legacy / noyau divers",
    "columnCount": 9
  },
  {
    "name": "pbi_interventions",
    "columns": [
      "id_intervention",
      "date_intervention",
      "deplacement",
      "type"
    ],
    "domain": "Legacy / noyau divers",
    "columnCount": 4
  },
  {
    "name": "pickup_available_days",
    "columns": [
      "id",
      "day_of_week",
      "is_available",
      "created_date",
      "updated_date"
    ],
    "domain": "Legacy / noyau divers",
    "columnCount": 5
  },
  {
    "name": "pickup_signatures",
    "columns": [
      "id_signature",
      "id_pickup",
      "file_path",
      "file_name",
      "signature_data",
      "signed_by",
      "signed_at",
      "created_date",
      "updated_date"
    ],
    "domain": "Legacy / noyau divers",
    "columnCount": 9
  },
  {
    "name": "pickup_specific_dates",
    "columns": [
      "id",
      "date",
      "is_available",
      "created_date",
      "updated_date"
    ],
    "domain": "Legacy / noyau divers",
    "columnCount": 5
  },
  {
    "name": "pickups",
    "columns": [
      "id_pickup",
      "type",
      "id_client",
      "client_name",
      "id_succursale",
      "address_other",
      "reference",
      "applicant",
      "description",
      "id_ticket",
      "invoice_ids",
      "validation",
      "id_user_approved",
      "eta",
      "id_user",
      "id_user_returned",
      "use_bz_address",
      "adresse",
      "ville",
      "province",
      "codepostal",
      "created_date",
      "updated_date",
      "content_desc"
    ],
    "domain": "Legacy / noyau divers",
    "columnCount": 24
  },
  {
    "name": "pro_projects",
    "columns": [
      "id_client",
      "name",
      "description",
      "dead_line",
      "status",
      "id_departement",
      "date_last_communication",
      "creation_date",
      "last_modification_date",
      "close_project_communication_date",
      "id_project"
    ],
    "domain": "Production / projets",
    "columnCount": 11
  },
  {
    "name": "pro_projects_contacts",
    "columns": [
      "id_project",
      "id_user",
      "id_contact"
    ],
    "domain": "Production / projets",
    "columnCount": 3
  },
  {
    "name": "pro_projects_files",
    "columns": [
      "id_project",
      "name",
      "path",
      "size",
      "type",
      "id_file"
    ],
    "domain": "Production / projets",
    "columnCount": 6
  },
  {
    "name": "pro_projects_managers",
    "columns": [
      "id_project",
      "id_user",
      "id_manager"
    ],
    "domain": "Production / projets",
    "columnCount": 3
  },
  {
    "name": "pro_projects_phases",
    "columns": [
      "id_project",
      "name",
      "description",
      "dead_line",
      "id_phase"
    ],
    "domain": "Production / projets",
    "columnCount": 5
  },
  {
    "name": "pro_projects_phases_tasks",
    "columns": [
      "id_phase",
      "id_ticket",
      "status",
      "name",
      "estimated_time",
      "description",
      "id_task"
    ],
    "domain": "Production / projets",
    "columnCount": 7
  },
  {
    "name": "pro_projects_sellers",
    "columns": [
      "id_project",
      "id_user",
      "id_seller"
    ],
    "domain": "Production / projets",
    "columnCount": 3
  },
  {
    "name": "pro_projects_settings",
    "columns": [
      "closed_project_id_survey",
      "surey_wait_days",
      "id_setting"
    ],
    "domain": "Production / projets",
    "columnCount": 3
  },
  {
    "name": "procedure_steps",
    "columns": [
      "id_procedure_step",
      "id_procedure",
      "step_number",
      "title",
      "description",
      "icon_name",
      "details"
    ],
    "domain": "Legacy / noyau divers",
    "columnCount": 7
  },
  {
    "name": "procedures",
    "columns": [
      "id_procedure",
      "code",
      "name",
      "description",
      "is_active"
    ],
    "domain": "Legacy / noyau divers",
    "columnCount": 5
  },
  {
    "name": "process_tickets_ai",
    "columns": [
      "step",
      "action",
      "explanation",
      "responsible",
      "approbation",
      "consulted",
      "informed",
      "openai_assistant_id",
      "created_date",
      "updated_date",
      "prompt_key",
      "system_prompt",
      "id_process_tickets_ai"
    ],
    "domain": "Legacy / noyau divers",
    "columnCount": 13
  },
  {
    "name": "profitability",
    "columns": [
      "id_client",
      "tic_type_value",
      "notes",
      "notes_history",
      "created_date",
      "updated_date",
      "id_profitability"
    ],
    "domain": "Legacy / noyau divers",
    "columnCount": 7
  },
  {
    "name": "projects",
    "columns": [
      "name",
      "description",
      "eta",
      "status",
      "id_client",
      "total_hours",
      "total_done",
      "id_user_prime",
      "forfait",
      "montant_forfait",
      "id_ticket",
      "id_departement",
      "id_project"
    ],
    "domain": "Projets",
    "columnCount": 13
  },
  {
    "name": "projects_assigned",
    "columns": [
      "id_project",
      "id_user",
      "id_assigned"
    ],
    "domain": "Projets",
    "columnCount": 3
  },
  {
    "name": "projects_contacts",
    "columns": [
      "id_user",
      "id_project",
      "id_project_contact"
    ],
    "domain": "Projets",
    "columnCount": 3
  },
  {
    "name": "projects_files",
    "columns": [
      "id_project",
      "name",
      "size",
      "type",
      "data",
      "id_file"
    ],
    "domain": "Projets",
    "columnCount": 6
  },
  {
    "name": "projects_subproject",
    "columns": [
      "id_project",
      "name",
      "description",
      "estimated_hours",
      "status",
      "id_ticket",
      "completed_hours",
      "id_user_prime",
      "id_subproject"
    ],
    "domain": "Projets",
    "columnCount": 9
  },
  {
    "name": "projects_subproject_assigned",
    "columns": [
      "id_user",
      "id_subproject",
      "id_assigned"
    ],
    "domain": "Projets",
    "columnCount": 3
  },
  {
    "name": "projects_subproject_contacts",
    "columns": [
      "id_subproject",
      "id_user",
      "id_contact"
    ],
    "domain": "Projets",
    "columnCount": 3
  },
  {
    "name": "projects_subproject_tasks",
    "columns": [
      "id_subproject",
      "name",
      "id_intervention",
      "id_task"
    ],
    "domain": "Projets",
    "columnCount": 4
  },
  {
    "name": "prompt_comparison_emails",
    "columns": [
      "id_prompt_comparison_emails",
      "id_current_prompt_comparison_prompt",
      "id_testing_prompt_comparison_prompt",
      "subject",
      "content",
      "current_prompt_result",
      "testing_prompt_result",
      "expected_result",
      "is_archived",
      "prompt_comparison_history",
      "created_by",
      "updated_by",
      "created_date",
      "updated_date"
    ],
    "domain": "Legacy / noyau divers",
    "columnCount": 14
  },
  {
    "name": "prompt_comparison_prompts",
    "columns": [
      "id_prompt_comparison_prompts",
      "title",
      "description",
      "prompt",
      "version",
      "is_active",
      "status",
      "created_by",
      "updated_by",
      "created_date",
      "updated_date"
    ],
    "domain": "Legacy / noyau divers",
    "columnCount": 11
  },
  {
    "name": "punch",
    "columns": [
      "id_user",
      "start_time",
      "end_time",
      "id_punch"
    ],
    "domain": "Legacy / noyau divers",
    "columnCount": 4
  },
  {
    "name": "punch_tickets",
    "columns": [
      "id_user",
      "id_ticket",
      "start_time",
      "end_time",
      "id_punch_ticket"
    ],
    "domain": "Legacy / noyau divers",
    "columnCount": 5
  },
  {
    "name": "rapports_hours",
    "columns": [
      "data",
      "id_rapport"
    ],
    "domain": "Legacy / noyau divers",
    "columnCount": 2
  },
  {
    "name": "rapports_telecom",
    "columns": [
      "date_start",
      "date_stop",
      "date_creation",
      "id_client",
      "id_rapport"
    ],
    "domain": "Legacy / noyau divers",
    "columnCount": 5
  },
  {
    "name": "rapports_telecom_data",
    "columns": [
      "call_date",
      "duration",
      "destination",
      "cout",
      "type",
      "id_rapport",
      "source",
      "location",
      "extension",
      "cout_catalogue",
      "id_data"
    ],
    "domain": "Legacy / noyau divers",
    "columnCount": 11
  },
  {
    "name": "rd_logs",
    "columns": [
      "id_project",
      "details",
      "id_user",
      "logtime",
      "duree",
      "id_log"
    ],
    "domain": "Legacy / noyau divers",
    "columnCount": 6
  },
  {
    "name": "rd_projects",
    "columns": [
      "name",
      "id_project"
    ],
    "domain": "Legacy / noyau divers",
    "columnCount": 2
  },
  {
    "name": "receivable_process",
    "columns": [
      "period",
      "step",
      "planned_action",
      "message_subject",
      "dispatch_time",
      "example_monthly",
      "example_purchase",
      "send_type",
      "recipient",
      "responsible_team",
      "internal_notified",
      "accounts_role",
      "sales_role",
      "finance_role",
      "managed_services_role",
      "id_receivable_process"
    ],
    "domain": "Legacy / noyau divers",
    "columnCount": 16
  },
  {
    "name": "receivable_process_files",
    "columns": [
      "id_receivable_process",
      "id_file",
      "id"
    ],
    "domain": "Legacy / noyau divers",
    "columnCount": 3
  },
  {
    "name": "rh_billing_info",
    "columns": [
      "id_user",
      "institution_number",
      "bank_number",
      "folio_number",
      "id_billing_info"
    ],
    "domain": "RH",
    "columnCount": 5
  },
  {
    "name": "rh_conditions",
    "columns": [
      "id_user",
      "annee",
      "salaire",
      "vacances",
      "depenses",
      "maladies",
      "fonction",
      "description_taches",
      "extras",
      "date_creation",
      "transfert",
      "id_condition"
    ],
    "domain": "RH",
    "columnCount": 12
  },
  {
    "name": "rh_conges",
    "columns": [
      "id_user",
      "type",
      "date_conge_debut",
      "heures",
      "note",
      "date_conge_fin",
      "id_conge"
    ],
    "domain": "RH",
    "columnCount": 7
  },
  {
    "name": "rh_departements",
    "columns": [
      "departement",
      "pc_charge_projet",
      "pc_depassement_projet",
      "id_departement"
    ],
    "domain": "RH",
    "columnCount": 4
  },
  {
    "name": "rh_discussions",
    "columns": [
      "date_creation",
      "type",
      "date_suivi",
      "auteur",
      "commentaire",
      "id_user",
      "statut",
      "date_intervention",
      "mailsent",
      "id_discussion"
    ],
    "domain": "RH",
    "columnCount": 10
  },
  {
    "name": "rh_documents",
    "columns": [
      "titre",
      "id_file",
      "actif",
      "date_fichier",
      "id_document"
    ],
    "domain": "RH",
    "columnCount": 5
  },
  {
    "name": "rh_documents_discussions",
    "columns": [
      "id_file",
      "id_discussion",
      "date_fichier",
      "id_document"
    ],
    "domain": "RH",
    "columnCount": 4
  },
  {
    "name": "rh_documents_employes",
    "columns": [
      "titre",
      "id_file",
      "id_user",
      "date_fichier",
      "id_document"
    ],
    "domain": "RH",
    "columnCount": 5
  },
  {
    "name": "rh_documents_publications",
    "columns": [
      "id_file",
      "id_publication",
      "date_fichier",
      "titre",
      "id_document"
    ],
    "domain": "RH",
    "columnCount": 5
  },
  {
    "name": "rh_employes",
    "columns": [
      "adresse",
      "suite",
      "ville",
      "province",
      "codepostal",
      "pays",
      "telephone_perso",
      "courriel_perso",
      "date_embauche",
      "date_naissance",
      "nas",
      "photo_path",
      "fonction",
      "id_departement",
      "description_taches",
      "telephone",
      "laptop",
      "entente_salariale_lien",
      "entente_salariale_accepte",
      "entente_salariale_date_envoie",
      "actif",
      "courriel_secondaire",
      "photo_data",
      "id_user"
    ],
    "domain": "RH",
    "columnCount": 24
  },
  {
    "name": "rh_evaluation",
    "columns": [
      "id_user",
      "date_evaluation",
      "commentaire",
      "id_evaluation"
    ],
    "domain": "RH",
    "columnCount": 4
  },
  {
    "name": "rh_evaluation_files",
    "columns": [
      "id_evaluation",
      "id_file",
      "titre",
      "id_evaluation_file"
    ],
    "domain": "RH",
    "columnCount": 4
  },
  {
    "name": "rh_files",
    "columns": [
      "path",
      "name",
      "size",
      "id_file"
    ],
    "domain": "RH",
    "columnCount": 4
  },
  {
    "name": "rh_files_upload",
    "columns": [
      "path",
      "name",
      "size",
      "data",
      "preview",
      "filetype",
      "id_file"
    ],
    "domain": "RH",
    "columnCount": 7
  },
  {
    "name": "rh_fonctions",
    "columns": [
      "fonction",
      "description",
      "id_fonction"
    ],
    "domain": "RH",
    "columnCount": 3
  },
  {
    "name": "rh_historique",
    "columns": [
      "id_user",
      "auteur_id_user",
      "message",
      "date_creation",
      "id_historique"
    ],
    "domain": "RH",
    "columnCount": 5
  },
  {
    "name": "rh_holiday",
    "columns": [
      "holidaydate",
      "label",
      "id_holiday"
    ],
    "domain": "RH",
    "columnCount": 3
  },
  {
    "name": "rh_procedure_employe",
    "columns": [
      "id_user",
      "procedure",
      "check",
      "type",
      "id_procedure"
    ],
    "domain": "RH",
    "columnCount": 5
  },
  {
    "name": "rh_procedures",
    "columns": [
      "procedure",
      "type",
      "id_procedure"
    ],
    "domain": "RH",
    "columnCount": 3
  },
  {
    "name": "rh_publication_checklist",
    "columns": [
      "id_publication",
      "id_procedure"
    ],
    "domain": "RH",
    "columnCount": 2
  },
  {
    "name": "rh_publication_employe",
    "columns": [
      "id_publication",
      "id_user",
      "accepte",
      "date_accepte",
      "lien",
      "date_email_envoyer",
      "id_publication_employe"
    ],
    "domain": "RH",
    "columnCount": 7
  },
  {
    "name": "rh_publications",
    "columns": [
      "titre",
      "date",
      "id_publication"
    ],
    "domain": "RH",
    "columnCount": 3
  },
  {
    "name": "rh_suivi",
    "columns": [
      "id_user",
      "id_type",
      "date_suivi",
      "date_prochain_suivi",
      "commentaire",
      "path",
      "id_suivi"
    ],
    "domain": "RH",
    "columnCount": 7
  },
  {
    "name": "rh_type_suivi",
    "columns": [
      "type_suivi",
      "id_type_suivi"
    ],
    "domain": "RH",
    "columnCount": 2
  },
  {
    "name": "rh_types_discussions",
    "columns": [
      "type",
      "id_type"
    ],
    "domain": "RH",
    "columnCount": 2
  },
  {
    "name": "rh_uploads",
    "columns": [
      "id_user",
      "file_name",
      "file_location",
      "datetime",
      "id_upload"
    ],
    "domain": "RH",
    "columnCount": 5
  },
  {
    "name": "route_optimization_prompts",
    "columns": [
      "id_prompt",
      "prompt_name",
      "prompt_description",
      "is_active",
      "prompt_static",
      "prompt_modifiable",
      "created_by",
      "created_at",
      "updated_by",
      "updated_at"
    ],
    "domain": "Legacy / noyau divers",
    "columnCount": 10
  },
  {
    "name": "sec_a_sql_scripts",
    "columns": [
      "script_name",
      "id"
    ],
    "domain": "Sécurité & conformité",
    "columnCount": 2
  },
  {
    "name": "sec_catalog_loi25_people",
    "columns": [
      "liable_person",
      "backup_person",
      "display",
      "id_catalog",
      "id_loi25"
    ],
    "domain": "Sécurité & conformité",
    "columnCount": 5
  },
  {
    "name": "sec_catalog_service_incidents",
    "columns": [
      "title",
      "incident_date",
      "id_incident_type",
      "victims",
      "communicated",
      "communication_date",
      "id_catalog",
      "id_incident"
    ],
    "domain": "Sécurité & conformité",
    "columnCount": 8
  },
  {
    "name": "sec_catalog_service_sensitive_data",
    "columns": [
      "title",
      "id_service",
      "security",
      "id_type",
      "id_data"
    ],
    "domain": "Sécurité & conformité",
    "columnCount": 5
  },
  {
    "name": "sec_clients_catalog_group_services_scores",
    "columns": [
      "id_outcome",
      "id_service",
      "score",
      "value",
      "weight",
      "score_source",
      "title",
      "id_score"
    ],
    "domain": "Sécurité & conformité",
    "columnCount": 8
  },
  {
    "name": "sec_clients_catalog_services_nist_identify",
    "columns": [
      "id_service",
      "id_availability",
      "id_integrity",
      "id_confidentiality",
      "id_tracability",
      "id_authenticity",
      "id_non_repudiation",
      "id_commercial_value",
      "id_rto",
      "id_rpo",
      "id_risk_level_tolerance",
      "id_identify"
    ],
    "domain": "Sécurité & conformité",
    "columnCount": 12
  },
  {
    "name": "sec_clients_catalog_services_risks",
    "columns": [
      "id_service",
      "id_risk_type",
      "threat_level",
      "threat_internal",
      "threat_external",
      "probability_occurrence",
      "probability_complexity",
      "vulnerability_level",
      "vul_surveillance",
      "vul_messaging",
      "vul_access_rights",
      "vul_prog_obsolescence",
      "vul_remote_access",
      "vul_BYOD",
      "vul_data_storage",
      "vul_update_management",
      "vul_default_installation",
      "vul_os_app_hardware_network",
      "vul_awareness_training",
      "impact_availability",
      "impact_integrity",
      "impact_confidentiality",
      "impact_proofs",
      "impact_authenticity",
      "impact_non_repudiation",
      "cons_service_interruption",
      "cons_legal_sanctions",
      "cons_confidentiality",
      "cons_data_theft",
      "cons_reputation",
      "cons_operations",
      "cons_cost",
      "draft",
      "id_security_risk"
    ],
    "domain": "Sécurité & conformité",
    "columnCount": 34
  },
  {
    "name": "sec_contacts",
    "columns": [
      "company",
      "description",
      "website",
      "name",
      "email",
      "office_phone",
      "cell_phone",
      "draft",
      "id_contact"
    ],
    "domain": "Sécurité & conformité",
    "columnCount": 9
  },
  {
    "name": "sec_contacts_roles_and_responsibilities",
    "columns": [
      "description",
      "control_access",
      "network_admin",
      "executive_chief",
      "trained",
      "signature",
      "signature_date",
      "id_contact",
      "id_service",
      "draft",
      "id_role_and_responsibility"
    ],
    "domain": "Sécurité & conformité",
    "columnCount": 11
  },
  {
    "name": "sec_identify_authenticities",
    "columns": [
      "french_value",
      "english_value",
      "id_authenticity"
    ],
    "domain": "Sécurité & conformité",
    "columnCount": 3
  },
  {
    "name": "sec_identify_availabilities",
    "columns": [
      "french_value",
      "english_value",
      "id_availability"
    ],
    "domain": "Sécurité & conformité",
    "columnCount": 3
  },
  {
    "name": "sec_identify_commercial_values",
    "columns": [
      "french_value",
      "english_value",
      "id_commercial_value"
    ],
    "domain": "Sécurité & conformité",
    "columnCount": 3
  },
  {
    "name": "sec_identify_confidentialities",
    "columns": [
      "french_value",
      "english_value",
      "id_confidentiality"
    ],
    "domain": "Sécurité & conformité",
    "columnCount": 3
  },
  {
    "name": "sec_identify_integrities",
    "columns": [
      "french_value",
      "english_value",
      "id_integrity"
    ],
    "domain": "Sécurité & conformité",
    "columnCount": 3
  },
  {
    "name": "sec_identify_non_repudations",
    "columns": [
      "french_value",
      "english_value",
      "id_non_repudation"
    ],
    "domain": "Sécurité & conformité",
    "columnCount": 3
  },
  {
    "name": "sec_identify_rpos",
    "columns": [
      "french_value",
      "english_value",
      "id_rpo"
    ],
    "domain": "Sécurité & conformité",
    "columnCount": 3
  },
  {
    "name": "sec_identify_rtos",
    "columns": [
      "french_value",
      "english_value",
      "id_rto"
    ],
    "domain": "Sécurité & conformité",
    "columnCount": 3
  },
  {
    "name": "sec_identify_tracabilities",
    "columns": [
      "french_value",
      "english_value",
      "id_tracability"
    ],
    "domain": "Sécurité & conformité",
    "columnCount": 3
  },
  {
    "name": "sec_importance_levels",
    "columns": [
      "french_name",
      "english_name",
      "id_importance_level"
    ],
    "domain": "Sécurité & conformité",
    "columnCount": 3
  },
  {
    "name": "sec_incident_types",
    "columns": [
      "name_english",
      "name_french",
      "id_incident_type"
    ],
    "domain": "Sécurité & conformité",
    "columnCount": 3
  },
  {
    "name": "sec_nist_function",
    "columns": [
      "title",
      "french_title",
      "id_function"
    ],
    "domain": "Sécurité & conformité",
    "columnCount": 3
  },
  {
    "name": "sec_nist_function_category",
    "columns": [
      "title",
      "id_function",
      "french_title",
      "english_title",
      "french_description",
      "english_description",
      "id_category"
    ],
    "domain": "Sécurité & conformité",
    "columnCount": 7
  },
  {
    "name": "sec_nist_function_category_outcomes",
    "columns": [
      "title",
      "id_category",
      "french_title",
      "english_title",
      "video_link",
      "id_outcome"
    ],
    "domain": "Sécurité & conformité",
    "columnCount": 6
  },
  {
    "name": "sec_outcomes_reduction_measures",
    "columns": [
      "id_outcome",
      "id_reduction_measure",
      "id_outcome_reduction_measure"
    ],
    "domain": "Sécurité & conformité",
    "columnCount": 3
  },
  {
    "name": "sec_password_lengths",
    "columns": [
      "value_english",
      "value_french",
      "minimum",
      "maximum",
      "id_length"
    ],
    "domain": "Sécurité & conformité",
    "columnCount": 5
  },
  {
    "name": "sec_raci",
    "columns": [
      "title",
      "id_contact",
      "draft",
      "id_raci"
    ],
    "domain": "Sécurité & conformité",
    "columnCount": 4
  },
  {
    "name": "sec_raci_categories",
    "columns": [
      "id_raci",
      "id_category",
      "raci_value",
      "id_raci_category"
    ],
    "domain": "Sécurité & conformité",
    "columnCount": 4
  },
  {
    "name": "sec_reduction_measures",
    "columns": [
      "action_name",
      "id_importance_level",
      "status",
      "eta",
      "cost",
      "completed",
      "notes",
      "id_catalog",
      "draft",
      "id_reduction_measure"
    ],
    "domain": "Sécurité & conformité",
    "columnCount": 10
  },
  {
    "name": "sec_risk_levels",
    "columns": [
      "french_name",
      "english_name",
      "css_class",
      "id_risk_level"
    ],
    "domain": "Sécurité & conformité",
    "columnCount": 4
  },
  {
    "name": "sec_risk_types",
    "columns": [
      "title",
      "cyber_attaque",
      "explanation",
      "id_risk_type"
    ],
    "domain": "Sécurité & conformité",
    "columnCount": 4
  },
  {
    "name": "sec_risks_reduction_measures",
    "columns": [
      "id_risk",
      "id_reduction_measure",
      "id_risk_reduction_measure"
    ],
    "domain": "Sécurité & conformité",
    "columnCount": 3
  },
  {
    "name": "sec_sensitive_types",
    "columns": [
      "value_english",
      "value_french",
      "id_type"
    ],
    "domain": "Sécurité & conformité",
    "columnCount": 3
  },
  {
    "name": "sec_service_ponderation",
    "columns": [
      "french_name",
      "english_name",
      "css_class",
      "id_ponderation"
    ],
    "domain": "Sécurité & conformité",
    "columnCount": 4
  },
  {
    "name": "sec_services_passwords",
    "columns": [
      "id_length",
      "special_characters",
      "expires",
      "limited_tries",
      "saved_history",
      "restore_password",
      "vault",
      "mfa",
      "id_service",
      "description",
      "id_password"
    ],
    "domain": "Sécurité & conformité",
    "columnCount": 11
  },
  {
    "name": "sec_services_reduction_measures",
    "columns": [
      "id_reduction_measure",
      "id_service",
      "id_service_reduction_measure"
    ],
    "domain": "Sécurité & conformité",
    "columnCount": 3
  },
  {
    "name": "sendmail_attachments",
    "columns": [
      "filename",
      "filedata",
      "id_job",
      "id_attachment"
    ],
    "domain": "Courriels",
    "columnCount": 4
  },
  {
    "name": "sendmail_emails",
    "columns": [
      "status",
      "destination",
      "send_date",
      "subject",
      "body_html",
      "id_job",
      "postfix_id",
      "postfix_log",
      "queue_date",
      "id_email"
    ],
    "domain": "Courriels",
    "columnCount": 10
  },
  {
    "name": "sendmail_jobs",
    "columns": [
      "id_template",
      "template_variables",
      "job_config",
      "job_date",
      "status",
      "job_subject",
      "test_email",
      "avec_rappel",
      "subject_rappel",
      "date_rappel",
      "body_html",
      "job_body_html",
      "id_job"
    ],
    "domain": "Courriels",
    "columnCount": 13
  },
  {
    "name": "sendmail_jobs_products",
    "columns": [
      "id_job_product",
      "id_job",
      "id_product"
    ],
    "domain": "Courriels",
    "columnCount": 3
  },
  {
    "name": "sendmail_templates",
    "columns": [
      "template_subject",
      "template_body_html",
      "template_variables",
      "template_name",
      "id_template"
    ],
    "domain": "Courriels",
    "columnCount": 5
  },
  {
    "name": "sessions",
    "columns": [
      "sess",
      "expired",
      "sid"
    ],
    "domain": "Legacy / noyau divers",
    "columnCount": 3
  },
  {
    "name": "sherweb_client_assignements",
    "columns": [
      "assignement_id",
      "client_id",
      "sub_account",
      "status",
      "notes",
      "notes_history",
      "ticket_link",
      "is_primary",
      "created_at",
      "updated_at"
    ],
    "domain": "Legacy / noyau divers",
    "columnCount": 10
  },
  {
    "name": "sherweb_invoice_details",
    "columns": [
      "sherweb_invoice_detail_id",
      "sherweb_invoice_id",
      "id_client",
      "sub_account",
      "invoice_date",
      "invoice_period_from",
      "invoice_period_to",
      "service_period_from",
      "service_period_to",
      "po_number",
      "number_of_user",
      "description",
      "sku",
      "discounted_price_not_prorated",
      "list_price",
      "unit_cost",
      "cost",
      "organization_sub_total",
      "account_id",
      "created_at",
      "updated_at"
    ],
    "domain": "Legacy / noyau divers",
    "columnCount": 21
  },
  {
    "name": "sherweb_invoices",
    "columns": [
      "sherweb_invoice_id",
      "invoice_date",
      "original_file_name",
      "imported_at",
      "total_cost",
      "total_user",
      "invoice_sub_total",
      "hst",
      "pst",
      "gst",
      "grand_total",
      "currency",
      "created_at",
      "updated_at"
    ],
    "domain": "Legacy / noyau divers",
    "columnCount": 14
  },
  {
    "name": "surv_adjectives",
    "columns": [
      "adjective",
      "active",
      "id_adjective"
    ],
    "domain": "Surveillance",
    "columnCount": 3
  },
  {
    "name": "surv_emails",
    "columns": [
      "subject",
      "message",
      "id_email"
    ],
    "domain": "Surveillance",
    "columnCount": 3
  },
  {
    "name": "surv_hashes",
    "columns": [
      "hash",
      "id_survey",
      "id_ticket",
      "id_project",
      "id_user_created_by",
      "id_email",
      "to_email",
      "to_name",
      "visited",
      "visited_date",
      "completed",
      "completed_date",
      "created_date",
      "id_hash"
    ],
    "domain": "Surveillance",
    "columnCount": 14
  },
  {
    "name": "surv_questions_adjectives",
    "columns": [
      "id_section_question",
      "id_adjective",
      "id_questions_adjectives"
    ],
    "domain": "Surveillance",
    "columnCount": 3
  },
  {
    "name": "surv_results",
    "columns": [
      "id_hash",
      "id_section_question",
      "bool",
      "id_adjective",
      "selected",
      "id_scale",
      "value",
      "text",
      "id_result"
    ],
    "domain": "Surveillance",
    "columnCount": 9
  },
  {
    "name": "surv_scales",
    "columns": [
      "name",
      "description",
      "active",
      "id_scale"
    ],
    "domain": "Surveillance",
    "columnCount": 4
  },
  {
    "name": "surv_scales_details",
    "columns": [
      "id_scale",
      "name",
      "index",
      "value",
      "active",
      "id_scale_detail"
    ],
    "domain": "Surveillance",
    "columnCount": 6
  },
  {
    "name": "surv_sections",
    "columns": [
      "id_survey",
      "title",
      "description",
      "id_section"
    ],
    "domain": "Surveillance",
    "columnCount": 4
  },
  {
    "name": "surv_sections_questions",
    "columns": [
      "id_section",
      "type",
      "id_scale",
      "title",
      "description",
      "id_section_question"
    ],
    "domain": "Surveillance",
    "columnCount": 6
  },
  {
    "name": "surv_surveys",
    "columns": [
      "title",
      "description",
      "active",
      "created_date",
      "id_survey"
    ],
    "domain": "Surveillance",
    "columnCount": 5
  },
  {
    "name": "sys_migrations",
    "columns": [
      "name",
      "batch",
      "migration_time",
      "id"
    ],
    "domain": "Legacy / noyau divers",
    "columnCount": 4
  },
  {
    "name": "sys_migrations_lock",
    "columns": [
      "is_locked",
      "index"
    ],
    "domain": "Legacy / noyau divers",
    "columnCount": 2
  },
  {
    "name": "sys_sessions",
    "columns": [
      "sess",
      "expired",
      "id_session"
    ],
    "domain": "Legacy / noyau divers",
    "columnCount": 3
  },
  {
    "name": "tac_clients",
    "columns": [
      "id_client",
      "notes",
      "nom",
      "description",
      "occurrence",
      "id_tacheclient"
    ],
    "domain": "TactiCE",
    "columnCount": 6
  },
  {
    "name": "tac_historique",
    "columns": [
      "date_fait",
      "note",
      "id_client",
      "id_user",
      "id_tache",
      "id_historique"
    ],
    "domain": "TactiCE",
    "columnCount": 6
  },
  {
    "name": "tac_recurrences",
    "columns": [
      "id_client",
      "id_template",
      "id_user",
      "nom",
      "description",
      "notes",
      "occurrence",
      "interval",
      "type",
      "auto_create_ticket",
      "start_date",
      "end_date",
      "date_prochain",
      "statut",
      "sent_date",
      "creation_date",
      "week_day",
      "unique_per_ticket",
      "id_tac_origin",
      "id_tac_recurrence"
    ],
    "domain": "TactiCE",
    "columnCount": 20
  },
  {
    "name": "tac_recurrences_assign",
    "columns": [
      "id_assign_to",
      "is_primary",
      "id_tac_recurrence",
      "id_tac_recurrences_assign"
    ],
    "domain": "TactiCE",
    "columnCount": 4
  },
  {
    "name": "tac_recurrences_tickets",
    "columns": [
      "id_ticket",
      "id_tac_origin",
      "id_tac_recurrence",
      "id_tac_ticket"
    ],
    "domain": "TactiCE",
    "columnCount": 4
  },
  {
    "name": "tac_taches",
    "columns": [
      "nom",
      "description",
      "occurrence",
      "id_client",
      "date_prochain",
      "id_tacheclient",
      "id_user",
      "statut",
      "id_tache"
    ],
    "domain": "TactiCE",
    "columnCount": 9
  },
  {
    "name": "tac_templates",
    "columns": [
      "nom",
      "description",
      "occurrence",
      "interval",
      "id_template"
    ],
    "domain": "TactiCE",
    "columnCount": 5
  },
  {
    "name": "tac_templates_assign",
    "columns": [
      "id_assign_to",
      "is_primary",
      "id_template",
      "id_tac_templates_assign"
    ],
    "domain": "TactiCE",
    "columnCount": 4
  },
  {
    "name": "tac_tickets",
    "columns": [
      "id_ticket",
      "id_tacheclient",
      "id_tacticket"
    ],
    "domain": "TactiCE",
    "columnCount": 3
  },
  {
    "name": "tasks_admin",
    "columns": [
      "name",
      "description",
      "priority",
      "department",
      "type",
      "status",
      "end_date",
      "creator_user_id",
      "created_date",
      "updated_date",
      "approved",
      "id_task_admin"
    ],
    "domain": "Legacy / noyau divers",
    "columnCount": 12
  },
  {
    "name": "tasks_admin_assigned_users",
    "columns": [
      "id_task_admin",
      "id_user",
      "created_date",
      "updated_date",
      "id_tasks_admin_assigned_users"
    ],
    "domain": "Legacy / noyau divers",
    "columnCount": 5
  },
  {
    "name": "tasks_admin_notes",
    "columns": [
      "id_task_admin",
      "note",
      "creator",
      "created_date",
      "updated_date",
      "id_tasks_admin_notes"
    ],
    "domain": "Legacy / noyau divers",
    "columnCount": 6
  },
  {
    "name": "telecom_documentation",
    "columns": [
      "id_client",
      "name",
      "ip",
      "vlan",
      "public_ip",
      "root_password",
      "root_password_iv",
      "web_password",
      "web_password_iv",
      "mysql_user",
      "mysql_password",
      "sip_port",
      "rtp_port",
      "id_telecom_documentation"
    ],
    "domain": "Legacy / noyau divers",
    "columnCount": 14
  },
  {
    "name": "telecom_stats",
    "columns": [
      "server_name",
      "avg_ping",
      "stat_date",
      "active_calls",
      "alerted",
      "payload",
      "id_stat"
    ],
    "domain": "Legacy / noyau divers",
    "columnCount": 7
  },
  {
    "name": "ti_availability",
    "columns": [
      "id_availability",
      "id_user",
      "availability_date",
      "period",
      "location_type",
      "id_client",
      "id_client_key",
      "status",
      "reviewed_at",
      "created_at",
      "updated_at"
    ],
    "domain": "Legacy / noyau divers",
    "columnCount": 11
  },
  {
    "name": "ti_settings",
    "columns": [
      "id",
      "client_visit_limit",
      "remote_work_limit"
    ],
    "domain": "Legacy / noyau divers",
    "columnCount": 3
  },
  {
    "name": "node581",
    "columns": [
      "id_ticket",
      "id_user",
      "dt",
      "description",
      "id_alarm"
    ],
    "domain": "Legacy / noyau divers",
    "columnCount": 5
  },
  {
    "name": "tic_appointment_cancel_tokens",
    "columns": [
      "id",
      "token_hash",
      "id_rdv",
      "id_ticket",
      "id_event_365",
      "expires_at",
      "cancelled_at",
      "created_at"
    ],
    "domain": "Tickets TI",
    "columnCount": 8
  },
  {
    "name": "tic_assign",
    "columns": [
      "id_ticket",
      "id_assign_to",
      "id_assign"
    ],
    "domain": "Tickets TI",
    "columnCount": 3
  },
  {
    "name": "tic_assign_to",
    "columns": [
      "id",
      "display_name",
      "full_name",
      "type",
      "email"
    ],
    "domain": "Tickets TI",
    "columnCount": 5
  },
  {
    "name": "tic_bids",
    "columns": [
      "id_ticket",
      "id_bid",
      "id_tic_bids"
    ],
    "domain": "Tickets TI",
    "columnCount": 3
  },
  {
    "name": "tic_categories",
    "columns": [
      "name",
      "id_type",
      "id_category"
    ],
    "domain": "Tickets TI",
    "columnCount": 3
  },
  {
    "name": "tic_changelog",
    "columns": [
      "id_ticket",
      "id_user",
      "change_dt",
      "property",
      "new_value",
      "source",
      "id_changelog"
    ],
    "domain": "Tickets TI",
    "columnCount": 7
  },
  {
    "name": "tic_cloud_users",
    "columns": [
      "id_ticket",
      "id_client",
      "name",
      "username",
      "email",
      "description",
      "type",
      "type_name",
      "date",
      "sent",
      "id_intervention",
      "id_tic_cloud_user"
    ],
    "domain": "Tickets TI",
    "columnCount": 12
  },
  {
    "name": "tic_color_code",
    "columns": [
      "id_tic_color_code",
      "name",
      "legend",
      "color_hex",
      "position",
      "is_active",
      "created_at",
      "updated_at"
    ],
    "domain": "Tickets TI",
    "columnCount": 8
  },
  {
    "name": "tic_contacts",
    "columns": [
      "id_ticket",
      "id_user",
      "id_tic_contacts"
    ],
    "domain": "Tickets TI",
    "columnCount": 3
  },
  {
    "name": "tic_contacts_bak",
    "columns": [
      "id_ticket",
      "name",
      "email",
      "id_tic_contact"
    ],
    "domain": "Tickets TI",
    "columnCount": 4
  },
  {
    "name": "tic_documents",
    "columns": [
      "id_ticket",
      "id_document",
      "id_tic_document"
    ],
    "domain": "Tickets TI",
    "columnCount": 3
  },
  {
    "name": "tic_emails",
    "columns": [
      "emailsource",
      "emaildate",
      "subject",
      "source",
      "type",
      "id_email"
    ],
    "domain": "Tickets TI",
    "columnCount": 6
  },
  {
    "name": "tic_emails_365",
    "columns": [
      "from",
      "to",
      "cc",
      "subject",
      "body",
      "id_ticket",
      "received_date",
      "status",
      "is_read",
      "id_email_365",
      "internet_message_id",
      "conversation_id",
      "last_graph_message_id",
      "last_folder_name",
      "processing_status",
      "processing_error",
      "processed_at",
      "ai_reply_status",
      "email_box_name",
      "is_first_email",
      "created_date",
      "ai_reply_attempts",
      "ai_thread_rotations",
      "ai_reply_next_attempt_at",
      "ai_reply_last_error",
      "ai_thread_id",
      "ai_replied_at",
      "id_email"
    ],
    "domain": "Tickets TI",
    "columnCount": 28
  },
  {
    "name": "tic_eta_and_response_grid",
    "columns": [
      "priority",
      "barometer",
      "eta_in_hours",
      "id_tic_eta_and_response_grid"
    ],
    "domain": "Tickets TI",
    "columnCount": 4
  },
  {
    "name": "tic_factures",
    "columns": [
      "date_creation",
      "numero_acomba",
      "commande_acomba",
      "print",
      "total_forfait",
      "total_horaire",
      "total_heures",
      "id_client",
      "type",
      "id_facture"
    ],
    "domain": "Tickets TI",
    "columnCount": 10
  },
  {
    "name": "tic_files",
    "columns": [
      "id_ticket",
      "name",
      "path",
      "size",
      "type",
      "data",
      "id_file"
    ],
    "domain": "Tickets TI",
    "columnCount": 7
  },
  {
    "name": "tic_files_old",
    "columns": [
      "upload_data",
      "id_ticket",
      "done",
      "id_file"
    ],
    "domain": "Tickets TI",
    "columnCount": 4
  },
  {
    "name": "tic_flags",
    "columns": [
      "id_user",
      "flagcode",
      "id_ticket",
      "flagdate",
      "email_sent",
      "reminder_note",
      "reminder_completed_at",
      "id_flag"
    ],
    "domain": "Tickets TI",
    "columnCount": 8
  },
  {
    "name": "tic_internal_interventions",
    "columns": [
      "date_intervention",
      "id_ticket",
      "id_technician",
      "description",
      "jira_comment_id",
      "id_internal_intervention"
    ],
    "domain": "Tickets TI",
    "columnCount": 6
  },
  {
    "name": "tic_interventions",
    "columns": [
      "date_intervention",
      "id_ticket",
      "id_technician",
      "description",
      "duree",
      "deplacement",
      "route",
      "id_succursale",
      "id_intervention_type",
      "alerts_intervention_type_status",
      "gitlab_note_id",
      "jira_comment_id",
      "is_km_billable",
      "time_and_half",
      "id_intervention"
    ],
    "domain": "Tickets TI",
    "columnCount": 15
  },
  {
    "name": "tic_interventions_types",
    "columns": [
      "type",
      "is_active",
      "id_intervention_type"
    ],
    "domain": "Tickets TI",
    "columnCount": 3
  },
  {
    "name": "tic_jours_feries",
    "columns": [
      "jour_ferie",
      "description",
      "id_jours_feries"
    ],
    "domain": "Tickets TI",
    "columnCount": 3
  },
  {
    "name": "tic_priority",
    "columns": [
      "priority_name",
      "priority_short",
      "priority"
    ],
    "domain": "Tickets TI",
    "columnCount": 3
  },
  {
    "name": "tic_rdv_reminder_group_users",
    "columns": [
      "id_reminder_group",
      "id_user",
      "created_at"
    ],
    "domain": "Tickets TI",
    "columnCount": 3
  },
  {
    "name": "tic_rdv_reminder_groups",
    "columns": [
      "id_reminder_group",
      "name",
      "is_dispatch_ti_default",
      "is_active",
      "created_at",
      "updated_at"
    ],
    "domain": "Tickets TI",
    "columnCount": 6
  },
  {
    "name": "tic_status",
    "columns": [
      "status_name",
      "status"
    ],
    "domain": "Tickets TI",
    "columnCount": 2
  },
  {
    "name": "tic_tasks",
    "columns": [
      "id_ticket",
      "status",
      "label",
      "id_assign_to",
      "completed_by",
      "completed_at",
      "id_task"
    ],
    "domain": "Tickets TI",
    "columnCount": 7
  },
  {
    "name": "tic_ticket_filter_favorites",
    "columns": [
      "id_ticket_filter_favorite",
      "id_user",
      "name",
      "filters_json",
      "is_default",
      "is_legacy_import",
      "created_at",
      "updated_at",
      "system_key",
      "is_protected"
    ],
    "domain": "Tickets TI",
    "columnCount": 10
  },
  {
    "name": "tic_ticket_states",
    "columns": [
      "id_ticket_state",
      "code",
      "label",
      "position",
      "is_active",
      "created_at",
      "updated_at"
    ],
    "domain": "Tickets TI",
    "columnCount": 7
  },
  {
    "name": "tic_tickets",
    "columns": [
      "date_creation",
      "title",
      "description",
      "contact_name",
      "contact_email",
      "status",
      "id_client",
      "id_creator",
      "id_assign_to",
      "priority",
      "accuse_reception",
      "is_outside_working_hours",
      "is_urgent_request",
      "type",
      "forfait",
      "montant_forfait",
      "facturable",
      "taux_horaire",
      "last_intervention",
      "id_bid",
      "id_order",
      "id_invoice",
      "id_client_bh",
      "id_facture",
      "id_parent",
      "total_duree",
      "total_montant",
      "commande_acomba",
      "dev_status",
      "dev_priority",
      "recurrence",
      "recurrence_options",
      "date_start",
      "date_eta",
      "date_engagement",
      "ticket_private",
      "last_modified",
      "gitlab_project_id",
      "gitlab_project_name",
      "gitlab_issue_id",
      "gitlab_issue_iid",
      "gitlab_issue_weburl",
      "gitlab_branch_ref",
      "is_soges",
      "is_from_backup",
      "is_hotline",
      "is_swat_team",
      "is_specialist",
      "jira_issue_id",
      "jira_issue_url",
      "jira_issue_key",
      "jira_issue_reporter_name",
      "jira_issue_reporter_email",
      "jira_issue_reporter_phone",
      "jira_issue_request_type",
      "jira_issue_assignee_name",
      "jira_issue_assignee_account_id",
      "is_team_proactive",
      "id_bak_job",
      "id_category",
      "renewal_id",
      "asana_task_gid",
      "ai_countdown_start_date",
      "ai_countdown_duration_in_hours",
      "ticket_ai_logs",
      "id_tic_color_code",
      "id_ticket_state",
      "id_ticket"
    ],
    "domain": "Tickets TI",
    "columnCount": 68
  },
  {
    "name": "tic_tickets_ateliers",
    "columns": [
      "id_ticket",
      "status",
      "eta",
      "id_ticket_atelier"
    ],
    "domain": "Tickets TI",
    "columnCount": 4
  },
  {
    "name": "tic_tickets_groups",
    "columns": [
      "id_group",
      "id_user",
      "id"
    ],
    "domain": "Tickets TI",
    "columnCount": 3
  },
  {
    "name": "tic_tickets_rdv",
    "columns": [
      "name",
      "start_date",
      "end_date",
      "id_ticket",
      "id_event_365",
      "id_rdv"
    ],
    "domain": "Tickets TI",
    "columnCount": 6
  },
  {
    "name": "tic_tickets_rdv_reminder_state",
    "columns": [
      "id_rdv_reminder_state",
      "id_rdv",
      "id_user",
      "action",
      "dismissed_until",
      "claimed_by_user",
      "created_at",
      "updated_at"
    ],
    "domain": "Tickets TI",
    "columnCount": 8
  },
  {
    "name": "tic_tickets_rdv_users",
    "columns": [
      "id_user",
      "id_rdv",
      "id_rdv_user"
    ],
    "domain": "Tickets TI",
    "columnCount": 3
  },
  {
    "name": "tic_types",
    "columns": [
      "label",
      "name",
      "value"
    ],
    "domain": "Tickets TI",
    "columnCount": 3
  },
  {
    "name": "tic_users_vacations",
    "columns": [
      "id_user",
      "day",
      "times",
      "period",
      "id_user_vacation"
    ],
    "domain": "Tickets TI",
    "columnCount": 5
  },
  {
    "name": "unavailable_dates",
    "columns": [
      "id_unavailable_date",
      "start_date",
      "end_date"
    ],
    "domain": "Legacy / noyau divers",
    "columnCount": 3
  },
  {
    "name": "usecure_assignements_notes",
    "columns": [
      "note_id",
      "assignement_id",
      "content",
      "is_current",
      "created_at",
      "updated_at"
    ],
    "domain": "Legacy / noyau divers",
    "columnCount": 6
  },
  {
    "name": "usecure_client_assignements",
    "columns": [
      "assignement_id",
      "company_id",
      "client_id",
      "status",
      "ticket_link",
      "is_primary",
      "created_at",
      "updated_at"
    ],
    "domain": "Legacy / noyau divers",
    "columnCount": 8
  },
  {
    "name": "usecure_invoice_details",
    "columns": [
      "usecure_invoice_detail_id",
      "usecure_invoice_id",
      "company_id",
      "company_name",
      "domain",
      "status",
      "qty_ubreach",
      "qty_ulearn",
      "invoice_date",
      "created_at",
      "updated_at"
    ],
    "domain": "Legacy / noyau divers",
    "columnCount": 11
  },
  {
    "name": "usecure_invoices",
    "columns": [
      "usecure_invoice_id",
      "invoice_date",
      "original_file_name",
      "total_ubreach",
      "total_ulearn",
      "total_accounts",
      "imported_at",
      "created_at",
      "updated_at"
    ],
    "domain": "Legacy / noyau divers",
    "columnCount": 9
  },
  {
    "name": "user_procedure_progress",
    "columns": [
      "id_user_procedure_progress",
      "id_user",
      "id_procedure_step",
      "is_completed"
    ],
    "domain": "Legacy / noyau divers",
    "columnCount": 4
  },
  {
    "name": "users",
    "columns": [
      "login",
      "surname",
      "name",
      "password",
      "display_name",
      "extension",
      "bz_staff",
      "active",
      "new_type",
      "new_assign",
      "id_client",
      "cellulaire",
      "bureau",
      "is_tic_admin",
      "is_tic_user",
      "info",
      "signature",
      "backup_favoris",
      "categoriescontact",
      "passwordHash",
      "password_requires_reset",
      "passwordsLimits",
      "lastPermissionTemplateUsed",
      "currentPermissionTemplate",
      "lastPermissionTemplateUsedGlobal",
      "savedTicketSearch",
      "notifications_email_tic_open",
      "notifications_email_tic_closed",
      "notifications_email_tic_updated",
      "contact_type",
      "jeffsboard",
      "monbz_email_new_ticket",
      "monbz_email_update_ticket",
      "monbz_email_report_opentickets",
      "notifications_email_tic_intervention",
      "contact_type_notes",
      "webchat_status",
      "is_punch_allowed",
      "aliases",
      "monbz_telecom_extension",
      "monbz_telecom_passwordhash",
      "gitlab_user_id",
      "gitlab_user_name",
      "2fa_required",
      "2fa_temp",
      "2fa_temp_expiration",
      "2fa_secret",
      "2fa_chances",
      "2fa_errors",
      "telecom_is_contact",
      "id_license_user_type",
      "license_notes",
      "chat_user",
      "chat_online",
      "chat_socket_id",
      "ticket_accuse_reception",
      "monbz_2fa_secret",
      "monbz_2fa_secret_temp",
      "monbz_2fa_secret_temp_expiration",
      "monbz_2fa_token",
      "monbz_2fa_expiration",
      "monbz_2fa_chances",
      "monbz_2fa_errors",
      "monbz_reset_password_token",
      "monbz_reset_password_expiration",
      "is_twenty_four_seven",
      "favorites_pages",
      "backup_company_bz",
      "backup_company_sgt",
      "jira_user_id",
      "is_beta_tester",
      "id_365",
      "created_date",
      "api_key_fallback",
      "tickets_by_mail_ai_priority",
      "favorites_admin_report",
      "favorites_pages_ventes",
      "id_user"
    ],
    "domain": "Identités & accès",
    "columnCount": 78
  },
  {
    "name": "users_backups_favoris",
    "columns": [
      "id_user",
      "id_client",
      "id"
    ],
    "domain": "Identités & accès",
    "columnCount": 3
  },
  {
    "name": "users_clients_favoris",
    "columns": [
      "id_user",
      "id_client",
      "id"
    ],
    "domain": "Identités & accès",
    "columnCount": 3
  },
  {
    "name": "users_clients_view_counts",
    "columns": [
      "id_user",
      "id_client",
      "view_count",
      "last_counted_at"
    ],
    "domain": "Identités & accès",
    "columnCount": 4
  },
  {
    "name": "users_clients_views",
    "columns": [
      "id_users_clients_views",
      "id_user",
      "id_client",
      "type",
      "created_date",
      "last_seen_at"
    ],
    "domain": "Identités & accès",
    "columnCount": 6
  },
  {
    "name": "users_credentials_authorization_code",
    "columns": [
      "authorization_code",
      "id_authorization_code"
    ],
    "domain": "Identités & accès",
    "columnCount": 2
  },
  {
    "name": "users_credentials_tokens",
    "columns": [
      "access_token",
      "refresh_token",
      "expires_at",
      "created_date",
      "id_token"
    ],
    "domain": "Identités & accès",
    "columnCount": 5
  },
  {
    "name": "users_groups",
    "columns": [
      "id_user",
      "id_group",
      "id"
    ],
    "domain": "Identités & accès",
    "columnCount": 3
  },
  {
    "name": "users_linked_clients",
    "columns": [
      "id_user",
      "id_client"
    ],
    "domain": "Identités & accès",
    "columnCount": 2
  },
  {
    "name": "users_passwords_permissions",
    "columns": [
      "id_password",
      "id_user",
      "write_permission",
      "id_users_permissions_groupe",
      "id_users_passwords_permission"
    ],
    "domain": "Identités & accès",
    "columnCount": 5
  },
  {
    "name": "users_permissions_groupes",
    "columns": [
      "name",
      "id_users_permissions_groupe"
    ],
    "domain": "Identités & accès",
    "columnCount": 2
  },
  {
    "name": "users_permissions_groupes_users",
    "columns": [
      "id_users_permissions_groupe",
      "id_user",
      "id_users_permissions_groupes_user"
    ],
    "domain": "Identités & accès",
    "columnCount": 3
  },
  {
    "name": "users_restricted_clients",
    "columns": [
      "id_user",
      "id_client",
      "id_user_restricted_client"
    ],
    "domain": "Identités & accès",
    "columnCount": 3
  },
  {
    "name": "users_scope_user",
    "columns": [
      "id_user",
      "id_scope",
      "id_users_scope_user"
    ],
    "domain": "Identités & accès",
    "columnCount": 3
  },
  {
    "name": "users_scopes",
    "columns": [
      "scope",
      "description",
      "label",
      "application",
      "id_scope"
    ],
    "domain": "Identités & accès",
    "columnCount": 5
  },
  {
    "name": "users_scopes_models",
    "columns": [
      "id_scopes_template",
      "icon_id",
      "name",
      "description",
      "scope_id_list",
      "app_name"
    ],
    "domain": "Identités & accès",
    "columnCount": 6
  },
  {
    "name": "users_scopes_templates",
    "columns": [
      "name",
      "template",
      "id_client",
      "global",
      "id_template"
    ],
    "domain": "Identités & accès",
    "columnCount": 5
  },
  {
    "name": "users_sessions",
    "columns": [
      "session_hash",
      "id_user",
      "creation_date",
      "is_mobile",
      "id_session"
    ],
    "domain": "Identités & accès",
    "columnCount": 5
  },
  {
    "name": "users_tech_favoris",
    "columns": [
      "id_user",
      "id_assign_to",
      "id"
    ],
    "domain": "Identités & accès",
    "columnCount": 3
  },
  {
    "name": "users_temp",
    "columns": [
      "id_client",
      "login",
      "name",
      "surname",
      "cellulaire",
      "bureau",
      "info",
      "id_user_temp"
    ],
    "domain": "Identités & accès",
    "columnCount": 8
  },
  {
    "name": "users_types",
    "columns": [
      "id_user",
      "id_type",
      "id_user_type"
    ],
    "domain": "Identités & accès",
    "columnCount": 3
  },
  {
    "name": "users_types_list",
    "columns": [
      "name",
      "id_type"
    ],
    "domain": "Identités & accès",
    "columnCount": 2
  },
  {
    "name": "users_types_logs",
    "columns": [
      "id_user",
      "id_type",
      "log_type",
      "datetime",
      "id_user_log_type"
    ],
    "domain": "Identités & accès",
    "columnCount": 5
  },
  {
    "name": "veeam_api_data",
    "columns": [
      "veeam_client_id",
      "productType",
      "licenseEdition",
      "workloadType",
      "description",
      "quantity",
      "new_count",
      "date",
      "sku",
      "usedPoint",
      "created_at",
      "updated_at",
      "veeam_client_name",
      "id_veeam_api_data"
    ],
    "domain": "Sauvegardes",
    "columnCount": 14
  },
  {
    "name": "veeam_api_data_details",
    "columns": [
      "id_veeam_api_data_detail",
      "id_veeam_api_data",
      "license_id",
      "source_uid",
      "source_name",
      "quantity",
      "new_count",
      "usedPoint",
      "created_at",
      "updated_at"
    ],
    "domain": "Sauvegardes",
    "columnCount": 10
  },
  {
    "name": "veeam_bz_notes",
    "columns": [
      "id_veeam_bz_note",
      "sku_erp",
      "notes",
      "notes_history",
      "modified_by",
      "modified_at"
    ],
    "domain": "Sauvegardes",
    "columnCount": 6
  },
  {
    "name": "veeam_bz_quantities",
    "columns": [
      "id_veeam_bz_quantity",
      "sku_erp",
      "date",
      "quantity",
      "points",
      "modified_by",
      "modified_at"
    ],
    "domain": "Sauvegardes",
    "columnCount": 7
  },
  {
    "name": "veeam_client_links",
    "columns": [
      "id_client",
      "veeam_client_id",
      "created_at",
      "updated_at",
      "is_primary",
      "parent_link_id",
      "id_veeam_client_link"
    ],
    "domain": "Sauvegardes",
    "columnCount": 7
  },
  {
    "name": "veeam_client_notes",
    "columns": [
      "id_veeam_client_note",
      "id_client",
      "notes",
      "notes_history",
      "ticket_link",
      "created_at",
      "updated_at"
    ],
    "domain": "Sauvegardes",
    "columnCount": 7
  },
  {
    "name": "veeam_clients",
    "columns": [
      "id_veeam_client",
      "veeam_client_id",
      "name",
      "created_at",
      "updated_at"
    ],
    "domain": "Sauvegardes",
    "columnCount": 5
  },
  {
    "name": "veeam_data",
    "columns": [
      "id_client",
      "quantity",
      "date",
      "created_at",
      "updated_at",
      "sku",
      "id_veeam_data"
    ],
    "domain": "Sauvegardes",
    "columnCount": 7
  },
  {
    "name": "veeam_quantity_overrides",
    "columns": [
      "id_veeam_quantity_override",
      "id_client",
      "id_veeam_client",
      "sku_erp",
      "date",
      "quantity",
      "points",
      "modified_by",
      "modified_at"
    ],
    "domain": "Sauvegardes",
    "columnCount": 9
  },
  {
    "name": "veeam_sku_links",
    "columns": [
      "sku_erp",
      "sku_veeam",
      "created_at",
      "updated_at",
      "id_veeam_sku_link"
    ],
    "domain": "Sauvegardes",
    "columnCount": 5
  },
  {
    "name": "ven_pd_details",
    "columns": [
      "id_plan",
      "id_section",
      "id_createur",
      "date_modifie",
      "description",
      "prix",
      "quantite",
      "valide_pour",
      "nouveaute",
      "next_year",
      "date_achat",
      "id_detail"
    ],
    "domain": "Ventes",
    "columnCount": 12
  },
  {
    "name": "ven_pd_ms_groupes",
    "columns": [
      "id_plan",
      "nom_groupe",
      "prix",
      "qte_utilise",
      "qte_acquise",
      "id_groupe"
    ],
    "domain": "Ventes",
    "columnCount": 6
  },
  {
    "name": "ven_pd_ms_produits",
    "columns": [
      "id_groupe",
      "nom_produit",
      "quantite",
      "id_produit"
    ],
    "domain": "Ventes",
    "columnCount": 4
  },
  {
    "name": "ven_pd_notes",
    "columns": [
      "id_plan",
      "id_section",
      "note",
      "id_note"
    ],
    "domain": "Ventes",
    "columnCount": 4
  },
  {
    "name": "ven_pd_section_params",
    "columns": [
      "id_plan",
      "id_section",
      "mois",
      "id_param"
    ],
    "domain": "Ventes",
    "columnCount": 4
  },
  {
    "name": "ven_pd_sections",
    "columns": [
      "nom",
      "image",
      "color1",
      "color2",
      "class",
      "materiel",
      "id_section"
    ],
    "domain": "Ventes",
    "columnCount": 7
  },
  {
    "name": "ven_plan_directeur",
    "columns": [
      "id_client",
      "id_createur",
      "nom",
      "date_modifie",
      "active",
      "id_plan"
    ],
    "domain": "Ventes",
    "columnCount": 6
  },
  {
    "name": "vente_actions",
    "columns": [
      "id_client",
      "id_user",
      "id_config",
      "id_category",
      "name",
      "completed",
      "completed_date",
      "created_by",
      "archived",
      "created_date",
      "updated_date",
      "id_action"
    ],
    "domain": "Ventes",
    "columnCount": 12
  },
  {
    "name": "vente_categories",
    "columns": [
      "name",
      "id_config",
      "archived",
      "order",
      "created_date",
      "updated_date",
      "id_category"
    ],
    "domain": "Ventes",
    "columnCount": 7
  },
  {
    "name": "vente_client_tags",
    "columns": [
      "id_tag",
      "name",
      "inactive",
      "created_at"
    ],
    "domain": "Ventes",
    "columnCount": 4
  },
  {
    "name": "vente_clients_tags",
    "columns": [
      "id_client",
      "id_tag",
      "created_at"
    ],
    "domain": "Ventes",
    "columnCount": 3
  },
  {
    "name": "vente_config",
    "columns": [
      "name",
      "created_by",
      "archived",
      "created_date",
      "updated_date",
      "id_config"
    ],
    "domain": "Ventes",
    "columnCount": 6
  },
  {
    "name": "vente_config_users",
    "columns": [
      "id_config",
      "id_user",
      "archived",
      "created_date",
      "updated_date",
      "id_config_user"
    ],
    "domain": "Ventes",
    "columnCount": 6
  },
  {
    "name": "vente_notes",
    "columns": [
      "id_client",
      "id_user",
      "id_config",
      "note",
      "archived",
      "created_date",
      "updated_date",
      "id_note"
    ],
    "domain": "Ventes",
    "columnCount": 8
  },
  {
    "name": "vente_removed_client_users",
    "columns": [
      "id_client",
      "id_user",
      "id_config",
      "created_date",
      "updated_date",
      "id_removed_client_user"
    ],
    "domain": "Ventes",
    "columnCount": 6
  },
  {
    "name": "vocalytic_call_analyses",
    "columns": [
      "id",
      "provider_name",
      "scoptel_uniqueid",
      "id_user",
      "prompt_version_id",
      "extension_snapshot",
      "call_started_at",
      "call_duration_seconds",
      "global_score_100",
      "clarity_score",
      "empathy_score",
      "communication_quality_score",
      "technical_accuracy_score",
      "llm_result_json",
      "analysis_status",
      "error_message",
      "created_at",
      "updated_at",
      "is_deleted",
      "deleted_at",
      "deleted_by",
      "delete_reason",
      "scoptel_history_source"
    ],
    "domain": "Vocalytic",
    "columnCount": 23
  },
  {
    "name": "vocalytic_deleted_call_analyses_logs",
    "columns": [
      "id",
      "call_analysis_id",
      "deleted_by",
      "deleted_at",
      "delete_reason",
      "snapshot_json"
    ],
    "domain": "Vocalytic",
    "columnCount": 6
  },
  {
    "name": "vocalytic_employee_monthly_recommendations",
    "columns": [
      "id",
      "id_user",
      "period_start",
      "period_end",
      "recommendations_json",
      "source_artifact_path",
      "model_name",
      "prompt_version_id",
      "input_snapshot_json",
      "created_at"
    ],
    "domain": "Vocalytic",
    "columnCount": 10
  },
  {
    "name": "vocalytic_prompt_configs",
    "columns": [
      "id",
      "prompt_key",
      "display_name",
      "description",
      "is_active",
      "created_by",
      "updated_by",
      "created_at",
      "updated_at",
      "active_version_id"
    ],
    "domain": "Vocalytic",
    "columnCount": 10
  },
  {
    "name": "vocalytic_prompt_versions",
    "columns": [
      "id",
      "prompt_config_id",
      "version_number",
      "system_prompt",
      "user_prompt_template",
      "model_name",
      "temperature",
      "top_p",
      "transcription_model",
      "transcription_language",
      "transcription_prompt",
      "is_active_version",
      "activated_at",
      "created_by",
      "created_at",
      "updated_at",
      "transcription_carry_prior_context",
      "transcription_skip_silence_remove",
      "transcription_enable_highpass",
      "transcription_enable_dynaudnorm",
      "transcription_highpass_hz"
    ],
    "domain": "Vocalytic",
    "columnCount": 21
  },
  {
    "name": "vocalytic_tracked_users",
    "columns": [
      "id",
      "id_user",
      "tracking_enabled",
      "assigned_by",
      "assigned_at",
      "unassigned_at",
      "notes",
      "created_at",
      "updated_at",
      "scoptel_acd_agent_extension"
    ],
    "domain": "Vocalytic",
    "columnCount": 10
  }
];

export const bzDatabaseRelations: string[] = [
  "accounting_recevables_notes  -->  clients : id_client",
  "accounting_recevables_notes  -->  users : id_creator:id_user",
  "admintaches_contact  -->  admintaches_departement : departementId:id",
  "admintaches_contact  -->  clients : clientId:id_client",
  "admintaches_contextai  -->  clients : idclient:id_client",
  "admintaches_contextai  -->  contracts : idcontrat:id_contract",
  "admintaches_contextai  -->  users : idcontact:id_user",
  "admintaches_lk_departements_users  -->  admintaches_departement : iddepartement:id",
  "admintaches_lk_departements_users  -->  users : iduser:id_user",
  "admintaches_notes  -->  admintaches_task : taskId:id",
  "admintaches_responsesai  -->  admintaches_task : task_id:id",
  "admintaches_responsesai_prompts  -->  admintaches_task : task_id:id",
  "admintaches_task  -->  admintaches_departement : departementId:id",
  "admintaches_task  -->  admintaches_type : typeId:id",
  "admintaches_task  -->  clients : clientId:id_client",
  "admintaches_task  -->  users : demandeurId:id_user",
  "bzt_contracts  -->  clients : id_client",
  "bzt_contracts  -->  clients_succursale : id_succursale",
  "bzt_contracts_alerts  -->  contracts : id_contract",
  "bzt_contracts_contacts  -->  bzt_contracts : id_contract",
  "bzt_contracts_invoices  -->  clients : id_client",
  "bzt_contracts_invoices  -->  clients_succursale : id_succursale",
  "bzt_contracts_invoices  -->  contracts : id_contract",
  "bzt_contracts_invoices_items  -->  bzt_accounting_products : id_product",
  "bzt_contracts_invoices_items  -->  bzt_contracts_invoices : id_invoice",
  "bzt_contracts_invoices_items  -->  bzt_contracts_invoices_items : id_bundle:id_invoice_item",
  "bzt_contracts_invoices_paiements  -->  clients : id_client",
  "bzt_contracts_invoices_paiements_excess  -->  clients : id_client",
  "bzt_contracts_invoices_paiements_invoices  -->  bzt_contracts_invoices : id_invoice",
  "bzt_contracts_invoices_paiements_invoices  -->  bzt_contracts_invoices_paiements : id_paiement",
  "bzt_contracts_items  -->  bzt_accounting_products : id_product",
  "bzt_contracts_items  -->  bzt_contracts : id_contract",
  "bzt_contracts_items  -->  bzt_contracts_items : id_bundle:id_contract_item",
  "bzt_contracts_logs  -->  bzt_contracts : id_contract",
  "bzt_contracts_logs  -->  bzt_contracts_items : id_contract_item",
  "bzt_contracts_logs  -->  users : id_user",
  "bzt_contracts_recevables_notes  -->  clients : id_client",
  "bzt_contracts_recevables_notes  -->  users : id_creator:id_user",
  "bzt_contracts_recevables_notes_invoices  -->  bzt_contracts_recevables_notes : id_recevable_note",
  "bzt_contracts_recevables_notes_invoices  -->  contracts_invoices : id_invoice",
  "clients_access_managements  -->  clients : id_client",
  "clients_access_managements_assets_links  -->  clients_access_managements : id_access_management",
  "clients_access_managements_assets_links  -->  documentation_documents : id_asset:id_document",
  "clients_assets_identify  -->  documentation_documents : id_asset:id_document",
  "clients_backup_plans  -->  clients : id_client",
  "clients_backup_plans_assets_links  -->  clients_backup_plans : id_backup_plan",
  "clients_backup_plans_assets_links  -->  documentation_documents : id_asset:id_document",
  "clients_complaints  -->  clients : id_client",
  "clients_complaints_clients_links  -->  clients : id_client",
  "clients_complaints_clients_links  -->  clients_complaints : id_complaint",
  "clients_contacts  -->  clients : id_client",
  "clients_contacts_assets_links  -->  clients_contacts : id_contact",
  "clients_contacts_assets_links  -->  documentation_documents : id_asset:id_document",
  "clients_contacts_assets_responsible_types_links  -->  clients_contacts_assets_links : id_contact_asset_link",
  "clients_contacts_assets_responsible_types_links  -->  clients_contacts_responsible_types : id_responsible_type",
  "clients_cyber_uploads  -->  clients : id_client",
  "clients_cyber_uploads  -->  users : id_user",
  "clients_cyber_uploads_complaints_links  -->  clients_complaints : id_complaint",
  "clients_cyber_uploads_complaints_links  -->  clients_cyber_uploads : id_cyber_upload",
  "clients_cyber_uploads_incidents_links  -->  clients_cyber_uploads : id_cyber_upload",
  "clients_cyber_uploads_incidents_links  -->  clients_incidents : id_incident",
  "clients_cyber_uploads_types_links  -->  clients_cyber_uploads : id_cyber_upload",
  "clients_cyber_uploads_types_links  -->  clients_cyber_uploads_types : id_cyber_upload_type",
  "clients_graph_api_microsoft_secret  -->  clients : id_client",
  "clients_incident_assets_links  -->  clients_incidents : id_incident",
  "clients_incident_assets_links  -->  documentation_documents : id_asset:id_document",
  "clients_incident_clients_links  -->  clients : id_client",
  "clients_incident_clients_links  -->  clients_incidents : id_incident",
  "clients_incident_consequences_links  -->  clients_incident_consequences : id_incident_consequence",
  "clients_incident_consequences_links  -->  clients_incidents : id_incident",
  "clients_incident_impacts_links  -->  clients_incident_impacts : id_incident_impact",
  "clients_incident_impacts_links  -->  clients_incidents : id_incident",
  "clients_incident_law25_malicious_uses_links  -->  clients_incident_law25_malicious_uses : id_incident_malicious_use",
  "clients_incident_law25_malicious_uses_links  -->  clients_incidents : id_incident",
  "clients_incident_sensitive_data_links  -->  clients_incidents : id_incident",
  "clients_incident_sensitive_data_links  -->  clients_sensitive_data : id_sensitive_data",
  "clients_incident_types_links  -->  clients_incidents : id_incident",
  "clients_incidents  -->  clients : id_client",
  "clients_ingram_invoices  -->  accounting_products : id_product",
  "clients_ingram_invoices  -->  clients : id_client",
  "clients_ingram_invoices  -->  contracts_invoices : id_invoice",
  "clients_ingram_sync  -->  accounting_products : id_product",
  "clients_ingram_sync  -->  clients : id_client",
  "clients_links  -->  clients : id_parent_client:id_client",
  "clients_links  -->  clients : id_child_client:id_client",
  "clients_loi25  -->  clients : id_client",
  "clients_nist_categories_scores  -->  clients : id_client",
  "clients_nist_categories_scores  -->  nist_categories : id_nist_category",
  "clients_nist_functions_scores  -->  clients : id_client",
  "clients_nist_functions_scores  -->  nist_functions : id_nist_function",
  "clients_nist_subcategories_scores  -->  clients : id_client",
  "clients_nist_subcategories_scores  -->  nist_subcategories : id_nist_subcategory",
  "clients_password_policies  -->  clients : id_client",
  "clients_password_policies_assets_links  -->  clients_password_policies : id_password_policy",
  "clients_password_policies_assets_links  -->  documentation_documents : id_asset:id_document",
  "clients_quiz_answers  -->  clients : id_client",
  "clients_quiz_answers  -->  clients_quiz_items : selected_item_answer:id_quiz_item",
  "clients_quiz_answers  -->  clients_quiz_questions : id_quiz_question",
  "clients_quiz_answers_items_links  -->  clients_quiz_answers : id_quiz_answer",
  "clients_quiz_answers_items_links  -->  clients_quiz_items : id_quiz_item",
  "clients_quiz_items  -->  clients_quiz_questions : id_quiz_question",
  "clients_quiz_questions  -->  clients_quiz_categories : id_quiz_category",
  "clients_quiz_questions  -->  clients_quiz_question_types : id_quiz_question_type",
  "clients_raci_categories  -->  clients_raci_matrices : id_raci_matrix",
  "clients_raci_matrices  -->  clients : id_client",
  "clients_raci_responsibilities  -->  clients_raci_categories : id_raci_category",
  "clients_raci_responsibilities  -->  clients_raci_matrices : id_raci_matrix",
  "clients_raci_roles  -->  clients_contacts : id_contact",
  "clients_raci_roles  -->  clients_raci_matrices : id_raci_matrix",
  "clients_raci_squares  -->  clients_raci_responsibilities : id_raci_responsibility",
  "clients_raci_squares  -->  clients_raci_roles : id_raci_role",
  "clients_raci_squares  -->  clients_raci_values : id_raci_value",
  "clients_raci_types_links  -->  clients_raci_matrices : id_raci_matrix",
  "clients_raci_types_links  -->  clients_raci_types : id_raci_type",
  "clients_remote_accesses  -->  clients : id_client",
  "clients_remote_accesses_assets_links  -->  clients_remote_accesses : id_remote_access",
  "clients_remote_accesses_assets_links  -->  documentation_documents : id_asset:id_document",
  "clients_risks  -->  clients : id_client",
  "clients_risks  -->  documentation_documents : id_asset:id_document",
  "clients_risks_vulnerabilities_links  -->  clients_risks : id_risk",
  "clients_risks_vulnerabilities_links  -->  clients_risks_vulnerabilities : id_risk_vulnerability",
  "clients_sensitive_data  -->  clients : id_client",
  "clients_sensitive_data  -->  clients_sensitive_data_types : id_type:id_sensitive_data_type",
  "clients_sensitive_data_asset_links  -->  clients_sensitive_data : id_sensitive_data",
  "clients_sensitive_data_asset_links  -->  documentation_documents : id_asset:id_document",
  "clients_sensitive_data_category_links  -->  clients_sensitive_data : id_sensitive_data",
  "clients_sensitive_data_category_links  -->  clients_sensitive_data_categories : id_sensitive_data_category",
  "clients_sherweb_invoices  -->  accounting_products : id_product",
  "clients_sherweb_invoices  -->  clients : id_client",
  "clients_sherweb_invoices  -->  contracts_invoices : id_invoice",
  "clients_sherweb_sync  -->  accounting_products : id_product",
  "clients_sherweb_sync  -->  clients : id_client",
  "contracts  -->  clients : id_client",
  "contracts  -->  clients_succursale : id_succursale",
  "contracts_alerts  -->  contracts : id_contract",
  "contracts_contacts  -->  contracts : id_contract",
  "contracts_invoices  -->  clients : id_client",
  "contracts_invoices  -->  clients_succursale : id_succursale",
  "contracts_invoices  -->  contracts : id_contract",
  "contracts_invoices_items  -->  accounting_products : id_product",
  "contracts_invoices_items  -->  contracts_invoices : id_invoice",
  "contracts_invoices_items  -->  contracts_invoices_items : id_bundle:id_invoice_item",
  "contracts_invoices_paiements  -->  clients : id_client",
  "contracts_invoices_paiements_excess  -->  clients : id_client",
  "contracts_invoices_paiements_invoices  -->  contracts_invoices : id_invoice",
  "contracts_invoices_paiements_invoices  -->  contracts_invoices_paiements : id_paiement",
  "contracts_items  -->  accounting_products : id_product",
  "contracts_items  -->  contracts : id_contract",
  "contracts_items  -->  contracts_items : id_bundle:id_contract_item",
  "contracts_logs  -->  contracts : id_contract",
  "contracts_logs  -->  users : id_user",
  "contracts_microsoft_graph_invoices  -->  accounting_products : id_product",
  "contracts_microsoft_graph_invoices  -->  clients : id_client",
  "contracts_microsoft_graph_invoices  -->  contracts_invoices : id_invoice",
  "contracts_microsoft_graph_invoices_users  -->  accounting_products : id_product",
  "contracts_microsoft_graph_invoices_users  -->  contracts_invoices : id_invoice",
  "contracts_recevables_notes  -->  clients : id_client",
  "contracts_recevables_notes  -->  users : id_creator:id_user",
  "contracts_recevables_notes_invoices  -->  contracts_invoices : id_invoice",
  "contracts_recevables_notes_invoices  -->  contracts_recevables_notes : id_recevable_note",
  "department_director  -->  users : id_user",
  "documentation_catalog_dependencies  -->  documentation_documents : id_parent_asset:id_document",
  "documentation_catalog_dependencies  -->  documentation_documents : id_child_asset:id_document",
  "documentation_catalog_groups  -->  clients : id_client",
  "documentation_clients_rights  -->  clients : id_client",
  "documentation_clients_rights  -->  documentation_groups : id_documentation_group",
  "documentation_clients_rights  -->  users : id_user",
  "documentation_documents  -->  documentation_catalog_groups : id_group",
  "documentation_documents_rights  -->  documentation_documents : id_document",
  "documentation_documents_rights  -->  documentation_groups : id_documentation_group",
  "documentation_documents_rights  -->  users : id_user",
  "documentation_groups_users  -->  documentation_groups : id_documentation_group",
  "documentation_groups_users  -->  users : id_user",
  "documentation_guides  -->  clients : id_client",
  "documentation_ip_addresses  -->  documentation_documents : id_document",
  "documentation_logs  -->  clients : id_client",
  "documentation_logs  -->  users : id_user",
  "documentation_passwords  -->  documentation_passwords : id_parent:id_password",
  "documentation_passwords_rights  -->  documentation_groups : id_documentation_group",
  "documentation_passwords_rights  -->  documentation_passwords : id_password",
  "documentation_passwords_rights  -->  users : id_user",
  "duo_client_assignements  -->  clients : client_id:id_client",
  "expense_account_credit_card_users  -->  expense_account_credit_card : id_expense_account_credit_card",
  "expense_account_credit_card_users  -->  users : id_user",
  "expense_accounts_banking_information  -->  users : id_user",
  "expense_accounts_departments  -->  expense_accounts : id_expense_account",
  "expense_accounts_items  -->  clients : id_client",
  "expense_accounts_items  -->  expense_account_credit_card : id_expense_account_credit_card",
  "expense_accounts_items  -->  expense_accounts : id_expense_account",
  "expense_accounts_items_accounts  -->  expense_accounts_items : id_expense_account_item",
  "expense_accounts_items_files  -->  expense_accounts_items : id_expense_account_item",
  "expense_accounts_items_files  -->  files : id_file",
  "ingram_product_group_assignments  -->  ingram_product_groups : group_id:id",
  "nist_categories  -->  nist_functions : id_nist_function",
  "nist_subcategories  -->  nist_categories : id_nist_category",
  "pro_projects_files  -->  pro_projects : id_project",
  "profitability  -->  clients : id_client",
  "profitability  -->  tic_types : tic_type_value:value",
  "receivable_process_files  -->  files : id_file",
  "receivable_process_files  -->  receivable_process : id_receivable_process",
  "sec_catalog_loi25_people  -->  clients_catalog : id_catalog",
  "sec_catalog_service_incidents  -->  clients_catalog : id_catalog",
  "sec_catalog_service_incidents  -->  sec_incident_types : id_incident_type",
  "sec_catalog_service_sensitive_data  -->  clients_catalog_services : id_service",
  "sec_catalog_service_sensitive_data  -->  sec_sensitive_types : id_type",
  "sec_clients_catalog_group_services_scores  -->  clients_catalog_services : id_service",
  "sec_clients_catalog_group_services_scores  -->  sec_nist_function_category_outcomes : id_outcome",
  "sec_clients_catalog_services_nist_identify  -->  clients_catalog_services : id_service",
  "sec_clients_catalog_services_nist_identify  -->  sec_identify_authenticities : id_authenticity",
  "sec_clients_catalog_services_nist_identify  -->  sec_identify_availabilities : id_availability",
  "sec_clients_catalog_services_nist_identify  -->  sec_identify_commercial_values : id_commercial_value",
  "sec_clients_catalog_services_nist_identify  -->  sec_identify_confidentialities : id_confidentiality",
  "sec_clients_catalog_services_nist_identify  -->  sec_identify_integrities : id_integrity",
  "sec_clients_catalog_services_nist_identify  -->  sec_identify_non_repudations : id_non_repudiation:id_non_repudation",
  "sec_clients_catalog_services_nist_identify  -->  sec_identify_rpos : id_rpo",
  "sec_clients_catalog_services_nist_identify  -->  sec_identify_rtos : id_rto",
  "sec_clients_catalog_services_nist_identify  -->  sec_identify_tracabilities : id_tracability",
  "sec_clients_catalog_services_nist_identify  -->  sec_risk_levels : id_risk_level_tolerance:id_risk_level",
  "sec_clients_catalog_services_risks  -->  clients_catalog_services : id_service",
  "sec_clients_catalog_services_risks  -->  sec_risk_levels : probability_complexity:id_risk_level",
  "sec_clients_catalog_services_risks  -->  sec_risk_levels : threat_level:id_risk_level",
  "sec_clients_catalog_services_risks  -->  sec_risk_levels : vulnerability_level:id_risk_level",
  "sec_clients_catalog_services_risks  -->  sec_risk_levels : probability_occurrence:id_risk_level",
  "sec_clients_catalog_services_risks  -->  sec_risk_types : id_risk_type",
  "sec_contacts_roles_and_responsibilities  -->  clients_catalog_services : id_service",
  "sec_contacts_roles_and_responsibilities  -->  sec_contacts : id_contact",
  "sec_nist_function_category  -->  sec_nist_function : id_function",
  "sec_nist_function_category_outcomes  -->  sec_nist_function_category : id_category",
  "sec_outcomes_reduction_measures  -->  sec_nist_function_category_outcomes : id_outcome",
  "sec_outcomes_reduction_measures  -->  sec_reduction_measures : id_reduction_measure",
  "sec_raci  -->  sec_contacts : id_contact",
  "sec_raci_categories  -->  sec_nist_function_category : id_category",
  "sec_raci_categories  -->  sec_raci : id_raci",
  "sec_reduction_measures  -->  clients_catalog : id_catalog",
  "sec_reduction_measures  -->  sec_importance_levels : id_importance_level",
  "sec_risks_reduction_measures  -->  sec_clients_catalog_services_risks : id_risk:id_security_risk",
  "sec_risks_reduction_measures  -->  sec_reduction_measures : id_reduction_measure",
  "sec_services_passwords  -->  clients_catalog_services : id_service",
  "sec_services_passwords  -->  sec_password_lengths : id_length",
  "sec_services_reduction_measures  -->  clients_catalog_services : id_service",
  "sec_services_reduction_measures  -->  sec_reduction_measures : id_reduction_measure",
  "tasks_admin  -->  users : creator_user_id:id_user",
  "tasks_admin_assigned_users  -->  tasks_admin : id_task_admin",
  "tasks_admin_assigned_users  -->  users : id_user",
  "tasks_admin_notes  -->  tasks_admin : id_task_admin",
  "veeam_client_links  -->  clients : id_client",
  "veeam_data  -->  clients : id_client",
  "ven_pd_details  -->  users : id_createur:id_user",
  "ven_pd_details  -->  ven_pd_sections : id_section",
  "ven_pd_details  -->  ven_plan_directeur : id_plan",
  "ven_pd_ms_groupes  -->  ven_plan_directeur : id_plan",
  "ven_pd_ms_produits  -->  ven_pd_ms_groupes : id_groupe",
  "ven_pd_notes  -->  ven_plan_directeur : id_plan",
  "ven_pd_section_params  -->  ven_plan_directeur : id_plan",
  "ven_plan_directeur  -->  users : id_createur:id_user",
  "vente_actions  -->  clients : id_client",
  "vente_actions  -->  users : id_user",
  "vente_actions  -->  vente_categories : id_category",
  "vente_actions  -->  vente_config : id_config",
  "vente_categories  -->  vente_config : id_config",
  "vente_config_users  -->  users : id_user",
  "vente_config_users  -->  vente_config : id_config",
  "vente_notes  -->  clients : id_client",
  "vente_notes  -->  users : id_user",
  "vente_notes  -->  vente_config : id_config",
  "vente_removed_client_users  -->  clients : id_client",
  "vente_removed_client_users  -->  users : id_user",
  "vente_removed_client_users  -->  vente_config : id_config"
];
